<template>
  <div class="exam-grading-page">
    <!-- 顶部标题栏 -->
    <div class="grading-header">
      <!-- <span class="title">数据审核</span> -->
      
      <!-- 当前店铺信息 -->
      <div class="current-dealer-info" v-if="currentExaminee && (currentExaminee.dealerCode || currentExaminee.dealerName)" style="margin-left: 0;">
         <span class="dealer-code">{{ currentExaminee.dealerCode }}</span>
         <span class="dealer-name">{{ currentExaminee.dealerName }}</span>
      </div>

      <!-- 审核级别切换（仅考试问卷显示） -->
      <div class="review-level-tabs" v-if="phase === 0 || examForm === 1">
        <el-radio-group v-model="reviewLevel" size="small" @change="handleReviewLevelChange">
          <el-radio-button :label="1" :disabled="!canAccessLevel(1)">一审</el-radio-button>
          <el-radio-button :label="2" :disabled="!canAccessLevel(2)">二审</el-radio-button>
          <el-radio-button :label="3" :disabled="!canAccessLevel(3)">三审</el-radio-button>
        </el-radio-group>
        <!-- 状态提示区域 - 固定宽度，避免布局跳动 -->
        <span class="level-hint-wrapper">
          <span class="level-hint readonly-hint" v-if="currentExaminee && isReadOnlyMode">
            <i class="el-icon-view"></i> 只读模式
          </span>
          <span class="level-hint warning" v-else-if="currentExaminee && !canEditCurrentLevel && needPreviousLevel">
            <i class="el-icon-warning"></i> 需先完成{{ previousLevelText }}审
          </span>
          <span class="level-hint warning" v-else-if="currentExaminee && !canEditCurrentLevel">
            <i class="el-icon-warning"></i> 当前级别已完成
          </span>
        </span>
      </div>
      <div class="review-level-tabs" v-else>
        <span style="font-size: 14px; font-weight: 500; color: #303133;">资料审核</span>
        <span class="level-hint-wrapper">
          <span class="level-hint readonly-hint" v-if="currentExaminee && isReadOnlyMode">
            <i class="el-icon-check" style="color: #67C23A"></i> 已审核
          </span>
        </span>
      </div>

      <!-- ===== 42 号方案 §10.6.1 顶部数据治理工具条：修改审核 / 作废审核 / 审核历史 ===== -->
      <!-- 仅研究员 / 管理员可见；按答卷当前审核状态动态展示可操作级别 -->
      <div class="review-governance-bar" v-if="canGovernReview && currentExaminee">
        <el-dropdown v-if="modifiableLevels.length > 0" size="small" trigger="click"
                     @command="openModifyDialog">
          <el-button size="small" type="primary" plain>
            修改审核 <i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item v-for="lvl in modifiableLevels" :key="'mod-'+lvl" :command="lvl">
              修改{{ ['', '一', '二', '三'][lvl] }}审结果
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>

        <el-dropdown v-if="voidableLevels.length > 0" size="small" trigger="click"
                     @command="openVoidDialog" style="margin-left: 8px;">
          <el-button size="small" type="warning" plain>
            作废审核 <i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item v-for="lvl in voidableLevels" :key="'void-'+lvl" :command="lvl">
              作废{{ ['', '一', '二', '三'][lvl] }}审结果
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>

        <el-button size="small" style="margin-left: 8px;" @click="openHistoryDrawer">
          <i class="el-icon-time"></i>
          审核历史<span v-if="reviewHistoryCount > 0">({{ reviewHistoryCount }})</span>
        </el-button>
      </div>
    </div>

    <!-- ===== 作废某级审核 弹窗 ===== -->
    <el-dialog title="作废审核结果" :visible.sync="voidDialogVisible" width="520px" :close-on-click-modal="false">
      <div v-if="voidDialogLevel > 0">
        <p>门店：<strong>{{ currentExaminee && currentExaminee.dealerCode }} {{ currentExaminee && currentExaminee.dealerName }}</strong></p>
        <p>当前审核进度：<strong>{{ auditStatusText(currentExaminee && currentExaminee.auditStatus) }}</strong></p>
        <el-alert type="warning" :closable="false" show-icon style="margin: 8px 0;">
          <div slot="title">
            作废 {{ ['', '一', '二', '三'][voidDialogLevel] }}审后，答卷将回退到
            "<strong>{{ ['', '待一审', '一审完成', '二审完成'][voidDialogLevel] }}</strong>"状态。
            <span v-if="voidDialogLevel < 3">当前 {{ voidDialogLevel }} 审及以上的数据将写入历史，可恢复。</span>
          </div>
        </el-alert>
        <el-input type="textarea" v-model="voidDialogReason" :rows="3" maxlength="500" show-word-limit
                  placeholder="请填写作废原因（至少 10 字）"></el-input>
      </div>
      <div slot="footer">
        <el-button @click="voidDialogVisible = false">取消</el-button>
        <el-button type="danger" :loading="voidSubmitting" @click="submitVoidLevel">确认作废</el-button>
      </div>
    </el-dialog>

    <!-- ===== 修改审核 弹窗（仅作为"准备"入口，真正修改在下一步进入可编辑模式） ===== -->
    <el-dialog title="修改审核" :visible.sync="modifyDialogVisible" width="520px" :close-on-click-modal="false">
      <div v-if="modifyDialogLevel > 0">
        <p>门店：<strong>{{ currentExaminee && currentExaminee.dealerCode }} {{ currentExaminee && currentExaminee.dealerName }}</strong></p>
        <p>修改级别：<strong>{{ ['', '一', '二', '三'][modifyDialogLevel] }}审</strong></p>
        <el-alert type="info" :closable="false" show-icon style="margin: 8px 0;">
          <div slot="title">
            填写原因后将进入编辑模式，提交时系统会自动把当前结果快照到历史，再保存新结果。
            <br/>原审核人 / 原审核时间会保留在历史版本中。
          </div>
        </el-alert>
        <el-input type="textarea" v-model="modifyDialogReason" :rows="3" maxlength="500" show-word-limit
                  placeholder="请填写修改原因（至少 10 字）"></el-input>
      </div>
      <div slot="footer">
        <el-button @click="modifyDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="enterModifyMode">进入编辑</el-button>
      </div>
    </el-dialog>

    <!-- ===== 审核历史 抽屉 ===== -->
    <el-drawer title="审核历史" :visible.sync="historyDrawerVisible" direction="rtl" size="520px">
      <div class="history-timeline" v-loading="historyLoading">
        <el-empty v-if="!historyLoading && reviewHistoryList.length === 0" description="暂无审核历史"></el-empty>
        <div v-else class="timeline-wrap">
          <div v-for="h in reviewHistoryList" :key="h.id" class="timeline-item"
               :class="{ 'timeline-current': h.isCurrent }">
            <div class="timeline-dot" :class="'dot-type-' + h.actionType"></div>
            <div class="timeline-body">
              <div class="timeline-line1">
                <span class="tl-level">{{ ['', '一', '二', '三'][h.reviewLevel] }}审 · V{{ h.reviewVersion }}</span>
                <span class="tl-action-type" :class="'action-type-' + h.actionType">{{ h.actionTypeText }}</span>
                <span v-if="h.isCurrent" class="tl-current-tag">当前生效</span>
              </div>
              <div class="timeline-line2">
                <span v-if="h.actionAt">{{ h.actionAt }}</span>
                <span v-if="h.actionByName"> · 操作人：{{ h.actionByName }}</span>
                <span v-if="h.restoredFromVersion"> · 恢复自 V{{ h.restoredFromVersion }}</span>
              </div>
              <div class="timeline-line3" v-if="h.actionReason">
                原因：{{ h.actionReason }}
              </div>
              <div class="timeline-line4" v-if="h.actionType !== 3 && !h.isCurrent && canGovernReview">
                <el-button size="mini" plain @click="openRestoreDialog(h)">恢复此版本</el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-drawer>

    <!-- ===== 恢复到历史版本 弹窗 ===== -->
    <el-dialog title="恢复到历史版本" :visible.sync="restoreDialogVisible" width="520px" :close-on-click-modal="false">
      <div v-if="restoreDialogTarget">
        <p>恢复目标：<strong>{{ ['', '一', '二', '三'][restoreDialogTarget.reviewLevel] }}审 · V{{ restoreDialogTarget.reviewVersion }}</strong></p>
        <p v-if="restoreDialogTarget.actionAt">原始操作时间：{{ restoreDialogTarget.actionAt }}</p>
        <el-alert type="warning" :closable="false" show-icon style="margin: 8px 0;">
          <div slot="title">
            本次恢复将执行：<br/>
            1. 当前生效数据先作为"作废前快照"写入历史<br/>
            2. 将 V{{ restoreDialogTarget.reviewVersion }} 的审核数据恢复为当前生效
          </div>
        </el-alert>
        <el-input type="textarea" v-model="restoreDialogReason" :rows="3" maxlength="500" show-word-limit
                  placeholder="请填写恢复原因（至少 10 字）"></el-input>
      </div>
      <div slot="footer">
        <el-button @click="restoreDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="restoreSubmitting" @click="submitRestore">确认恢复</el-button>
      </div>
    </el-dialog>

    <!-- 三栏布局 -->
    <div class="grading-content">
      <!-- 左侧：考生列表 -->
      <div class="left-panel">
        <div class="panel-header">
          <span class="panel-title">审核名单</span>
          <span class="progress">{{ gradedCount }}/{{ totalCount }}</span>
        </div>
        
        <div class="examinee-list">
          <div
            v-for="(item, index) in filteredExamineeList"
            :key="item.answerKey"
            class="examinee-item"
            :class="{ active: currentExaminee && currentExaminee.answerKey === item.answerKey }"
            @click="selectExaminee(item)"
          >
            <span class="examinee-name">{{ item.dealerCode || ('答卷' + (index + 1)) }}</span>
            <span class="draft-badge" v-if="hasDraft(item)">草稿</span>
            <span class="status-badge" :class="getStatusClass(item)">
              {{ getStatusText(item) }}
            </span>
          </div>
        </div>
        
        <div class="left-panel-footer">
          <el-select v-model="statusFilter" size="mini" placeholder="筛选状态" clearable style="width: 100%;">
            <el-option label="全部" value="" />
            <el-option label="待审核" :value="0" />
            <template v-if="isSimplifiedAudit">
              <el-option label="已通过" :value="1" />
            </template>
            <template v-else>
              <el-option label="一审完成" :value="1" />
              <el-option label="二审完成" :value="2" />
              <el-option label="三审完成" :value="3" />
            </template>
          </el-select>
          <div class="footer-info" v-if="currentExaminee">
            <p>答题时长：{{ formatDuration(currentExaminee.durationSeconds) }}</p>
            <p>提交时间：{{ currentExaminee.submitTime }}</p>
            <p>当前状态：{{ getStatusText(currentExaminee) }}</p>
          </div>
        </div>
      </div>

      <!-- 中间：答题详情 + 底部按钮 -->
      <section class="center-section">
      <div class="center-panel" v-loading="detailLoading" element-loading-text="加载答题详情...">
        <div v-if="currentExaminee" class="answer-list">

          <!-- ===== 一票否决 Banner ===== -->
          <div
            v-if="currentExaminee.vetoTriggered && !currentExaminee.vetoOverridden"
            class="veto-banner-danger">
            <div class="veto-title-row">
              <i class="el-icon-warning"></i>
              <span>神客选中了一票否决选项，本次考试判为不通过</span>
            </div>
            <ul v-if="currentExaminee.vetoHits && currentExaminee.vetoHits.length" class="veto-list">
              <li v-for="h in currentExaminee.vetoHits" :key="(h.itemKey || '') + '|' + (h.optionValue || '')">
                <strong>{{ stripHtml(h.label) }}</strong>
                <span v-if="h.message && stripHtml(h.message) !== stripHtml(h.label)"> — {{ stripHtml(h.message) }}</span>
              </li>
            </ul>
            <div class="veto-actions" v-if="!isReadOnlyMode">
              <el-button type="danger" size="small" plain @click="openVetoOverrideDialog">
                <i class="el-icon-circle-close"></i> 推翻一票否决（客户申诉）
              </el-button>
              <span class="veto-tip">推翻后按审核重算分数判通过，操作将记入审计日志</span>
            </div>
          </div>
          <div
            v-else-if="currentExaminee.vetoTriggered && currentExaminee.vetoOverridden"
            class="veto-banner-warning">
            <div class="veto-title-row">
              <i class="el-icon-circle-check"></i>
              <span>一票否决已推翻 — {{ currentExaminee.vetoOverriddenAt || '' }}</span>
            </div>
            <div class="veto-override-reason">
              推翻理由：{{ currentExaminee.vetoOverrideReason }}
            </div>
          </div>

          <!-- ===== 跨阶段参考资料区域（仅考试模式显示，简化审核不需要） ===== -->
          <div v-if="!isSimplifiedAudit && crossPhaseGroups.length > 0" class="cross-phase-reference">
            <div class="ref-section-title">
              <i class="el-icon-folder-opened"></i>
              <span>参考资料（其他阶段）</span>
            </div>
            
            <div v-for="group in crossPhaseGroups" :key="group.phaseNo"
                 class="ref-phase-group"
                 :class="{ 'ref-phase-untrusted': isCrossPhaseUntrusted(group) }">
              <div class="ref-phase-header">
                <span class="ref-phase-badge">{{ group.phaseNo }}</span>
                <span class="ref-phase-name">{{ group.phaseName }}</span>
                <!-- 42 号方案 §9.3：参考资料可信度徽章 -->
                <span class="ref-phase-audit-tag" :class="crossPhaseAuditClass(group)">
                  <i v-if="isCrossPhaseUntrusted(group)" class="el-icon-warning-outline"></i>
                  {{ crossPhaseAuditText(group) }}
                </span>
                <span class="ref-phase-time" v-if="group.submitTime">提交于 {{ group.submitTime }}</span>
              </div>
              
              <div v-for="item in group.items" :key="item.itemKey" class="ref-question-card"
                   :ref="'ref-item-' + group.phaseNo + '-' + item.itemKey">
                <div class="ref-question-title">
                  <span v-if="item.serialNo" class="ref-serial">{{ item.serialNo }}.</span>
                  {{ stripHtml(item.label) }}
                </div>
                
                <div class="ref-answer-area">
                  <!-- 选择题：显示选中项 -->
                  <template v-if="isCrossPhaseChoiceQuestion(item.itemType)">
                    <div v-for="opt in item.options" :key="opt.value" class="ref-option"
                         :class="{ selected: isCrossPhaseOptionSelected(item, opt) }">
                      <i v-if="isCrossPhaseOptionSelected(item, opt)" class="el-icon-check" style="color: #2563eb; font-weight: bold;"></i>
                      <span v-else class="ref-option-circle"></span>
                      <span>{{ stripHtml(opt.label) }}</span>
                    </div>
                  </template>
                  
                  <!-- 文件上传题：图片/视频/录音 -->
                  <template v-else-if="isFileUploadQuestion(item.itemType) || isAudioRecordQuestion(item.itemType)">
                    <template v-if="getCrossPhaseAnswerValue(item)">
                      <div class="ref-file-gallery">
                        <template v-for="(file, fIdx) in parseRefFileList(getCrossPhaseAnswerValue(item))">
                          <div v-if="isImage(file.url)" :key="'ri-'+fIdx" class="ref-file-thumb"
                               @click="openFloatingMedia('image', file.url, item.serialNo + '. ' + stripHtml(item.label))">
                            <img :src="file.url" />
                            <span class="ref-thumb-overlay"><i class="el-icon-zoom-in"></i></span>
                          </div>
                          <div v-else-if="isAudio(file.url)" :key="'ra-'+fIdx" class="ref-file-thumb ref-audio-thumb"
                               @click="openFloatingMedia('audio', file.url, file.name || '录音')">
                            <i class="el-icon-headset"></i>
                            <span>{{ file.name || '录音' }}</span>
                          </div>
                          <div v-else-if="isVideo(file.url)" :key="'rv-'+fIdx" class="ref-file-thumb ref-video-thumb"
                               @click="openFloatingMedia('video', file.url, file.name || '视频')">
                            <i class="el-icon-video-play"></i>
                            <span>{{ file.name || '视频' }}</span>
                          </div>
                          <!-- PDF：FloatingMediaPlayer 不支持 pdf，用 <a target="_blank"> 新 tab 浏览器原生预览 -->
                          <a v-else-if="isPdf(file.url)" :key="'rp-'+fIdx" :href="file.url" target="_blank" rel="noopener"
                             class="ref-file-thumb ref-pdf-thumb" style="text-decoration:none;color:inherit;">
                            <i class="el-icon-document"></i>
                            <span>{{ file.name || 'PDF材料' }}</span>
                          </a>
                          <a v-else :key="'rf-'+fIdx" :href="file.url" target="_blank" class="ref-file-link">
                            <i class="el-icon-document"></i> {{ file.name || '文件' }}
                          </a>
                        </template>
                      </div>
                    </template>
                    <!-- 纯 URL 录音（非JSON格式） -->
                    <template v-else-if="isAudioRecordQuestion(item.itemType) && getCrossPhaseAnswerValue(item)">
                      <div class="ref-file-thumb ref-audio-thumb"
                           @click="openFloatingMedia('audio', getCrossPhaseAnswerValue(item), '录音')">
                        <i class="el-icon-headset"></i>
                        <span>播放录音</span>
                      </div>
                    </template>
                    <span v-else class="ref-no-answer">未作答</span>
                  </template>
                  
                  <!-- 评分题 -->
                  <template v-else-if="isRatingQuestion(item.itemType)">
                    <el-rate v-if="getCrossPhaseAnswerValue(item)"
                             :value="Number(getCrossPhaseAnswerValue(item)) || 0" disabled show-score text-color="#ff9900" />
                    <span v-else class="ref-no-answer">未作答</span>
                  </template>
                  
                  <!-- 签名题 -->
                  <template v-else-if="isSignatureQuestion(item.itemType)">
                    <div v-if="getCrossPhaseAnswerValue(item)" class="ref-signature-container"
                         @click="openFloatingMedia('image', getCrossPhaseAnswerValue(item), '签名')">
                      <img :src="getCrossPhaseAnswerValue(item)" />
                    </div>
                    <span v-else class="ref-no-answer">未签名</span>
                  </template>
                  
                  <!-- 廉政协议题 -->
                  <template v-else-if="item.itemType === 'INTEGRITY_AGREEMENT'">
                    <div v-if="getCrossPhaseAnswerValue(item)">
                      <!-- 廉政协议为图片时直接展示 -->
                      <div v-if="isAgreementImage(parseAgreementAnswer(getCrossPhaseAnswerValue(item)).agreementUrl)" class="ref-agreement-image"
                           @click="openFloatingMedia('image', parseAgreementAnswer(getCrossPhaseAnswerValue(item)).agreementUrl, '廉政协议')">
                        <img :src="parseAgreementAnswer(getCrossPhaseAnswerValue(item)).agreementUrl" style="max-width:100%;border-radius:4px;cursor:pointer;" />
                      </div>
                      <!-- 廉政协议为 PDF 时渲染 -->
                      <div v-else class="ref-agreement-pdf" :ref="'refPdf_' + group.phaseNo + '_' + item.itemKey">
                        <div v-if="refPdfStates[group.phaseNo + '_' + item.itemKey] === 'loading'" style="color:#94a3b8;font-size:13px;padding:8px 0;"><i class="el-icon-loading"></i> PDF 加载中...</div>
                        <div v-if="refPdfStates[group.phaseNo + '_' + item.itemKey] === 'error'" style="color:#f56c6c;font-size:13px;padding:8px 0;"><i class="el-icon-warning-outline"></i> PDF 加载失败</div>
                      </div>
                      <div class="ref-agreement-status" style="margin-top:8px;">
                        <template v-if="parseAgreementAnswer(getCrossPhaseAnswerValue(item)).checked">
                          <i class="el-icon-circle-check" style="color:#67c23a;margin-right:4px;"></i>
                          <span style="color:#67c23a;font-weight:600;">已确认同意廉政协议</span>
                        </template>
                        <template v-else>
                          <i class="el-icon-circle-close" style="color:#f56c6c;margin-right:4px;"></i>
                          <span style="color:#f56c6c;">未确认</span>
                        </template>
                      </div>
                    </div>
                    <span v-else class="ref-no-answer">未作答</span>
                  </template>
                  
                  <!-- 手机号验证题 -->
                  <template v-else-if="isPhoneVerifyQuestion(item.itemType)">
                    <div v-if="getCrossPhaseAnswerValue(item)" class="ref-phone-answer">
                      <span>手机号：{{ parsePhoneAnswer(getCrossPhaseAnswerValue(item)).phone || '-' }}</span>
                      <span v-if="parsePhoneAnswer(getCrossPhaseAnswerValue(item)).code" style="margin-left:12px;">验证码：{{ parsePhoneAnswer(getCrossPhaseAnswerValue(item)).code }}</span>
                    </div>
                    <span v-else class="ref-no-answer">未作答</span>
                  </template>
                  
                  <!-- 其他题型（文本等） -->
                  <template v-else>
                    <span v-if="getCrossPhaseAnswerValue(item)" class="ref-text-answer">{{ formatAnswerDisplay(getCrossPhaseAnswerValue(item)) }}</span>
                    <span v-else class="ref-no-answer">未作答</span>
                  </template>
                </div>
              </div>
            </div>
          </div>
          
          <div v-if="crossPhaseLoading" class="cross-phase-loading">
            <i class="el-icon-loading"></i> 加载其他阶段资料...
          </div>
          
          <!-- ===== 当前审核阶段标记 ===== -->
          <div v-if="crossPhaseGroups.length > 0" class="current-audit-marker">
            <span class="audit-marker-badge">当前审核</span>
            <span class="audit-marker-text">{{ phase > 0 ? '阶段' + phase : '' }}</span>
          </div>
          
          <!-- ===== 原有的审核题目列表 ===== -->
          <div
            v-for="(question, qIndex) in filteredQuestionList"
            :key="question.itemKey"
            :ref="'question-' + qIndex"
            class="question-card"
          >
            <div class="question-header">
              <span class="question-title">{{ question.serialNo ? question.serialNo + '.' + stripHtml(question.label || '') + ' 【' + question.typeName + '】' : '第' + (qIndex + 1) + '题 【' + question.typeName + '】' }}</span>
              <span class="question-score-info" v-if="question.enableScoring && question.maxScore">
                ({{ question.maxScore }}分)
              </span>
              <span class="full-record-badge" v-if="isAudioRecordQuestion(question.itemType)">
                <i class="dot"></i> 全程录音
              </span>
              <el-button v-if="isAudioRecordQuestion(question.itemType) && getAnswerValue(question.itemKey)"
                type="text" size="mini" icon="el-icon-position" 
                @click="openFloatingAudio(question)" 
                class="float-play-btn">悬浮播放</el-button>
            </div>
            
            <div class="question-content">
              <div class="question-label" v-if="!question.serialNo">{{ stripHtml(question.label) }}</div>
              
              <!-- 选择题选项（排除所有明检题，明检题在下方专用分支渲染） -->
              <div v-if="isChoiceQuestion(question.itemType) && !isMingjianQuestion(question.itemType)" class="options-list">
                <div v-if="!canEditReviewAnswer(question)" class="loopback-hint">
                  未配置回圈，当前题目不可修改答案
                </div>
                <template v-for="opt in question.options">
                  <div
                    :key="opt.value"
                    class="option-item"
                    :class="{
                      'clickable': canEditReviewAnswer(question),
                      'is-review-selected': isReviewSelected(question, opt)
                    }"
                    @click="canEditReviewAnswer(question) && toggleReviewOption(question, opt)"
                  >
                    <span class="option-marker">
                      <i v-if="isReviewSelected(question, opt)" class="el-icon-check review-check"></i>
                      <span v-else-if="isMultipleChoice(question.itemType)" class="option-checkbox"></span>
                      <span v-else class="option-circle"></span>
                    </span>
                    <span class="option-text">{{ stripHtml(opt.label) }}</span>
                    <span v-if="canShowOriginalForQuestion(question) && isOriginalSelected(question, opt)" class="original-tag">原答案</span>
                    <span v-if="isReview1Selected(question, opt)" class="review1-tag">一审</span>
                    <span v-if="isReview2Selected(question, opt)" class="review2-tag">二审</span>
                  </div>
                  <!-- "其他"选项的文本输入框（只读显示） -->
                  <div v-if="isOtherOption(opt)" :key="'other-' + opt.value" class="other-input-wrapper">
                    <el-input
                      v-model="otherTextMap[question.itemKey]"
                      class="other-input"
                      size="small"
                      placeholder="请输入其他内容"
                      disabled
                    />
                  </div>
                </template>
              </div>
              
              <!-- 评分类题目：显示星星 -->
              <div v-else-if="isRatingQuestion(question.itemType)" class="subjective-answer">
                <div v-if="question.description || question.tipContent" class="question-description" style="color: #e6a23c; font-size: 13px; margin-bottom: 10px; line-height: 1.6;">{{ stripHtml(question.description || question.tipContent) }}</div>
                <div class="answer-content rating-display">
                  <el-rate 
                    v-if="getAnswerValue(question.itemKey)"
                    :value="Number(getAnswerValue(question.itemKey)) || 0" 
                    disabled 
                    :max="getRatingMax(question)"
                    show-score
                    text-color="#ff9900"
                  />
                  <span v-else>未作答</span>
                </div>
              </div>
              
              <!-- 录音题答案展示 -->
              <div v-else-if="isAudioRecordQuestion(question.itemType)" class="audio-answer">
                <div class="answer-content" v-if="getAnswerValue(question.itemKey)">
                  <AudioPlayer 
                    ref="audioPlayer"
                    :src="getAudioUrl(question.itemKey)" 
                    :filename="getAudioFilename(question, currentExaminee)"
                  />
                </div>
                <div class="answer-content" v-else>未作答</div>
              </div>
              
              <!-- 签名题：显示签名图片（统一 evidence-tile 缩略图样式） -->
              <div v-else-if="isSignatureQuestion(question.itemType)" class="subjective-answer">
                <div class="answer-label">考生签名：</div>
                <div class="answer-content" v-if="getAnswerValue(question.itemKey)">
                  <div class="evidence-grid">
                    <div class="evidence-tile view-only"
                         @click="openFloatingMedia('image', getAnswerValue(question.itemKey), '签名')">
                      <img :src="getAnswerValue(question.itemKey)" alt="签名" class="evidence-thumb fit-contain" />
                      <i class="evidence-zoom el-icon-zoom-in" title="放大预览"
                         @click.stop="openFloatingMedia('image', getAnswerValue(question.itemKey), '签名')"></i>
                      <div class="evidence-name">签名</div>
                    </div>
                  </div>
                </div>
                <div class="answer-content" v-else>未签名</div>
              </div>
              
              <!-- 廉政协议题 -->
              <div v-else-if="question.itemType === 'INTEGRITY_AGREEMENT'" class="agreement-answer-section">
                <!-- 廉政协议为图片时用统一的 evidence-tile 缩略图样式 -->
                <div v-if="isAgreementImage(parseAgreementValue(question.itemKey).agreementUrl)"
                     class="evidence-grid">
                  <div class="evidence-tile view-only"
                       @click="openFloatingMedia('image', parseAgreementValue(question.itemKey).agreementUrl, '廉政协议')">
                    <img :src="parseAgreementValue(question.itemKey).agreementUrl" alt="廉政协议" class="evidence-thumb fit-contain" />
                    <i class="evidence-zoom el-icon-zoom-in" title="放大预览"
                       @click.stop="openFloatingMedia('image', parseAgreementValue(question.itemKey).agreementUrl, '廉政协议')"></i>
                    <div class="evidence-name">廉政协议</div>
                  </div>
                </div>
                <!-- 廉政协议为 PDF 时渲染 -->
                <div v-else-if="parseAgreementValue(question.itemKey).agreementUrl" class="agreement-pdf-pages" :ref="'gradingPdf_' + question.itemKey">
                  <div v-if="gradingPdfStates[question.itemKey] === 'loading'" class="pdf-loading"><i class="el-icon-loading"></i> PDF 加载中...</div>
                  <div v-if="gradingPdfStates[question.itemKey] === 'error'" class="pdf-error"><i class="el-icon-warning-outline"></i> PDF 加载失败</div>
                </div>
                <div class="agreement-check-status">
                  <i :class="parseAgreementValue(question.itemKey).checked ? 'el-icon-success' : 'el-icon-warning'" 
                     :style="{ color: parseAgreementValue(question.itemKey).checked ? '#67c23a' : '#e6a23c' }"></i>
                  <span>{{ parseAgreementValue(question.itemKey).checked ? '已确认同意廉政协议' : '未确认' }}</span>
                </div>
              </div>
              
              <!-- 手机号验证题 -->
              <div v-else-if="isPhoneVerifyQuestion(question.itemType)" class="subjective-answer">
                <div class="answer-content" v-if="getAnswerValue(question.itemKey)">
                  <span>手机号：{{ parsePhoneAnswer(getAnswerValue(question.itemKey)).phone || '-' }}</span>
                  <span v-if="parsePhoneAnswer(getAnswerValue(question.itemKey)).code" style="margin-left:12px;">验证码：{{ parsePhoneAnswer(getAnswerValue(question.itemKey)).code }}</span>
                </div>
                <div class="answer-content" v-else>未作答</div>
              </div>
              
              <!-- 明检分类题型：显示取证媒介 + 分类结果 -->
              <div v-else-if="isMingjianQuestion(question.itemType)" class="mingjian-answer-section">
                <!-- 拍照图片区（明检-拍照题）
                     采用与明检-文件题一致的 evidence-grid + evidence-tile 卡片样式，
                     统一缩略图视觉（见 42 号方案后续优化） -->
                <div class="mingjian-photos" v-if="isMingjianPhotoQuestion(question.itemType) && getMingjianPhotos(question.itemKey).length > 0">
                  <div class="mingjian-section-label"><i class="el-icon-camera"></i> 拍照取证</div>
                  <div class="evidence-grid">
                    <div v-for="(photo, pIdx) in getMingjianPhotos(question.itemKey)" :key="'mj-'+pIdx"
                         class="evidence-tile view-only"
                         @click="openFloatingMedia('image', photo.url, photo.name || '取证图片')">
                      <img :src="photo.url" :alt="photo.name || '取证图片'" class="evidence-thumb" />
                      <i class="evidence-zoom el-icon-zoom-in" title="放大预览"
                         @click.stop="openFloatingMedia('image', photo.url, photo.name || '取证图片')"></i>
                      <div class="evidence-name" :title="photo.name || ('取证图片 ' + (pIdx + 1))">
                        {{ photo.name || ('取证图片 ' + (pIdx + 1)) }}
                      </div>
                    </div>
                  </div>
                </div>
                <!-- 文件附件区（神客附件 + 审核员追加证据 合并在同一网格） -->
                <div class="mingjian-files" v-if="isMingjianFileQuestion(question.itemType)">
                  <div class="mingjian-section-label">
                    <i class="el-icon-paperclip"></i> 附件
                    <span class="mingjian-files-hint">点击勾选作为证据</span>
                  </div>
                  <div class="evidence-grid">
                    <!-- 神客原附件（_sourceLevel=0），任何级别审核员都可软删 -->
                    <div v-for="(f, fIdx) in getVisibleMingjianFiles(question.itemKey)" :key="'mjf-'+fIdx+'-'+f.url"
                         :class="['evidence-tile', { 'selected': isEvidenceSelected(question.itemKey, f.url) }]"
                         @click="canEditEvidence(question) && toggleEvidence(question.itemKey, f.url)">
                      <i v-if="canEditEvidence(question)"
                         :class="['evidence-check', isEvidenceSelected(question.itemKey, f.url) ? 'el-icon-success checked' : 'el-icon-circle-check']"></i>
                      <template v-if="isMingjianImageFile(f)">
                        <img :src="f.url" :alt="f.name" class="evidence-thumb" />
                        <i class="evidence-zoom el-icon-zoom-in" title="放大预览"
                           @click.stop="openFloatingMedia('image', f.url, f.name || '附件')"></i>
                      </template>
                      <template v-else>
                        <i :class="['evidence-file-icon', fileIconClass(f)]"></i>
                        <a :href="f.url" target="_blank" rel="noopener" class="evidence-zoom"
                           title="下载/打开" @click.stop>
                          <i class="el-icon-download"></i>
                        </a>
                      </template>
                      <i v-if="canDeleteEvidence(question, f)" class="evidence-remove el-icon-close"
                         title="移除该附件（软删除，保留留痕）"
                         @click.stop="removeEvidence(question.itemKey, f)"></i>
                      <div class="evidence-name" :title="f.name">{{ f.name }}</div>
                    </div>

                    <!-- 审核员追加的证据（含历史各级别，橙色边框区分来源） -->
                    <div v-for="(f, fIdx) in getAllVisibleAppendedFiles(question.itemKey)"
                         :key="'app-'+f._sourceLevel+'-'+f._originalIdx"
                         class="evidence-tile appended">
                      <span class="evidence-badge">{{ reviewLevelName(f._sourceLevel) }}追加</span>
                      <template v-if="isMingjianImageFile(f)">
                        <img :src="f.url" :alt="f.name" class="evidence-thumb" />
                        <i class="evidence-zoom el-icon-zoom-in" title="放大预览"
                           @click.stop="openFloatingMedia('image', f.url, f.name || '追加证据')"></i>
                      </template>
                      <template v-else>
                        <i :class="['evidence-file-icon', fileIconClass(f)]"></i>
                        <a :href="f.url" target="_blank" rel="noopener" class="evidence-zoom" title="下载/打开" @click.stop>
                          <i class="el-icon-download"></i>
                        </a>
                      </template>
                      <i v-if="canDeleteEvidence(question, f)" class="evidence-remove el-icon-close"
                         title="移除追加证据（软删除，保留留痕）"
                         @click.stop="removeEvidence(question.itemKey, f)"></i>
                      <div class="evidence-name" :title="f.name">{{ f.name }}</div>
                    </div>

                    <!-- 追加上传入口（始终放最后） -->
                    <el-upload
                      v-if="canEditEvidence(question)"
                      :action="''"
                      :http-request="(opts) => handleAppendEvidence(question.itemKey, opts)"
                      :show-file-list="false"
                      :before-upload="(f) => beforeAppendUpload(question, f)"
                      accept=""
                      class="evidence-tile add-tile"
                    >
                      <i class="el-icon-plus"></i>
                      <div class="evidence-name">追加证据</div>
                    </el-upload>

                    <!-- 完全没可见附件时，追加框旁边加个占位说明 -->
                    <div v-if="getVisibleMingjianFiles(question.itemKey).length === 0 && getAllVisibleAppendedFiles(question.itemKey).length === 0"
                         class="mingjian-files-inline-empty">
                      暂无可用附件
                    </div>
                  </div>
                </div>
                <!-- 分类结果 -->
                <div class="mingjian-result">
                  <div class="mingjian-section-label"><i class="el-icon-s-grid"></i> 分类结果</div>
                  <div v-if="question.itemType === 'MINGJIAN_PHOTO_TEXT'" class="mingjian-text-review">
                    <div class="answer-content" style="margin-bottom: 8px;">
                      <span style="color: #909399; font-size: 12px;">原答案：</span>{{ getMingjianTextValue(question.itemKey) || '未填写' }}
                    </div>
                    <div v-if="canEditReviewAnswer(question)" style="margin-top: 4px;">
                      <el-input
                        type="textarea"
                        :rows="2"
                        placeholder="请输入审核答案"
                        :value="reviewAnswerMap[question.itemKey] || ''"
                        @input="val => $set(reviewAnswerMap, question.itemKey, val)"
                        :disabled="isReadOnlyMode"
                        size="small"
                      />
                    </div>
                  </div>
                  <div v-else class="options-list">
                    <div v-for="opt in question.options" :key="opt.value" class="option-item"
                         :class="{
                           'clickable': canEditReviewAnswer(question),
                           'is-review-selected': isReviewSelected(question, opt)
                         }"
                         @click="canEditReviewAnswer(question) && toggleReviewOption(question, opt)">
                      <span class="option-marker">
                        <i v-if="isReviewSelected(question, opt)" class="el-icon-check review-check"></i>
                        <span v-else-if="isMultipleChoice(question.itemType)" class="option-checkbox"></span>
                        <span v-else class="option-circle"></span>
                      </span>
                      <span class="option-text">{{ stripHtml(opt.label) }}</span>
                      <span v-if="canShowOriginalForQuestion(question) && isMingjianOriginalSelected(question, opt)" class="original-tag">原答案</span>
                      <span v-if="isReview1Selected(question, opt)" class="review1-tag">一审</span>
                      <span v-if="isReview2Selected(question, opt)" class="review2-tag">二审</span>
                    </div>
                  </div>
                  <!-- "其他"补充文本展示 -->
                  <div v-if="getMingjianOtherText(question.itemKey)" class="mingjian-other-text" style="margin-top: 6px; padding: 6px 10px; background: #fdf6ec; border-radius: 4px; font-size: 13px; color: #e6a23c;">
                    <i class="el-icon-edit"></i> 其他补充：{{ getMingjianOtherText(question.itemKey) }}
                  </div>
                </div>
              </div>
              
              <!-- 其他主观题答案 -->
              <div v-else-if="!isFileUploadQuestion(question.itemType)" class="subjective-answer">
                <div class="answer-content">{{ formatAnswerDisplay(getAnswerValue(question.itemKey)) || '未作答' }}</div>
              </div>
              
              <!-- 文件上传题的内容展示
                   图片 / PDF 统一走 evidence-tile 缩略图；
                   视频 / 音频保留 inline 播放（审核场景需要快速预听/预览）；
                   其他文件保留下载链接 -->
              <div v-else class="file-upload-answer">
                <div class="answer-label">上传文件：</div>
                <div v-if="getFileList(question.itemKey).length > 0">
                  <!-- 图片部分：evidence-tile 卡片网格 -->
                  <div v-if="getFileList(question.itemKey).some(f => isImage(f.url))" class="evidence-grid" style="margin-bottom: 8px;">
                    <template v-for="(file, fIdx) in getFileList(question.itemKey)">
                      <div v-if="isImage(file.url)" :key="'img-' + fIdx" class="evidence-tile view-only"
                           @click="openFloatingMedia('image', file.url, file.name || '图片')">
                        <img :src="file.url" :alt="file.name || '图片'" class="evidence-thumb" />
                        <i class="evidence-zoom el-icon-zoom-in" title="放大预览"
                           @click.stop="openFloatingMedia('image', file.url, file.name || '图片')"></i>
                        <div class="evidence-name" :title="file.name">{{ file.name || '图片' }}</div>
                      </div>
                    </template>
                  </div>
                  <!-- PDF 部分：evidence-tile 图标卡片
                       FloatingMediaPlayer 组件不支持 pdf 类型（仅 image/video/audio），
                       统一改为新 tab 打开，浏览器原生渲染 PDF -->
                  <div v-if="getFileList(question.itemKey).some(f => isPdf(f.url))" class="evidence-grid" style="margin-bottom: 8px;">
                    <template v-for="(file, fIdx) in getFileList(question.itemKey)">
                      <a v-if="isPdf(file.url)" :key="'pdf-' + fIdx" :href="file.url" target="_blank" rel="noopener"
                         class="evidence-tile view-only evidence-tile-link">
                        <i class="evidence-file-icon el-icon-document"></i>
                        <i class="evidence-zoom el-icon-view" title="新窗口预览 PDF"></i>
                        <div class="evidence-name" :title="file.name">{{ file.name || 'PDF文件' }}</div>
                      </a>
                    </template>
                  </div>
                  <!-- 视频 / 音频 / 其他文件：保留原有 inline 形态；
                       只有确实含非图片非 PDF 文件时才渲染容器，避免空 div -->
                  <div v-if="getFileList(question.itemKey).some(f => !isImage(f.url) && !isPdf(f.url))"
                       class="file-gallery">
                    <template v-for="(file, fIdx) in getFileList(question.itemKey)">
                      <div v-if="isVideo(file.url)" :key="'vid-' + fIdx" class="file-thumb video-thumb"
                           style="cursor: pointer;" @click="openFloatingMedia('video', file.url, file.name || '视频')">
                        <video :src="file.url" controls preload="metadata" style="max-width: 300px; max-height: 200px;"></video>
                      </div>
                      <div v-else-if="isAudio(file.url)" :key="'aud-' + fIdx" class="file-thumb audio-thumb"
                           style="cursor: pointer;" @click="openFloatingMedia('audio', file.url, file.name || '音频文件')">
                        <audio :src="file.url" controls preload="metadata" style="width: 250px;"></audio>
                        <span class="audio-name">{{ file.name || '音频文件' }}</span>
                      </div>
                      <a v-else-if="!isImage(file.url) && !isPdf(file.url)" :key="'file-' + fIdx"
                         :href="file.url" target="_blank" class="file-download-link">
                        <i class="el-icon-document"></i>
                        <span>{{ file.name || '下载文件' }}</span>
                      </a>
                    </template>
                  </div>
                </div>
                <div v-else class="no-file">未上传文件</div>
              </div>
            </div>
            
            <!-- 审核评分区域（评分题 或 简化审核模式，但排除RATING主观评分题） -->
            <div class="review-section" v-if="(question.enableScoring || isSimplifiedAudit) && !isRatingQuestion(question.itemType)">
              <!-- 原答案特殊情况说明（仅考试审核模式） -->
              <div v-if="!isSimplifiedAudit && specialRemarkMap[question.itemKey]" class="guest-special-remark" style="margin-bottom: 8px; padding: 10px; background: #FFF0F0; border: 1px dashed #FF4D4F; border-radius: 4px; color: #FF4D4F;">
                <strong>原答案 特殊情况说明：</strong>
                <span>{{ specialRemarkMap[question.itemKey] }}</span>
              </div>
              
              <!-- 历史审核说明（仅考试审核模式） -->
              <div v-if="!isSimplifiedAudit && reviewLevel > 1 && specialRemark1Map[question.itemKey]" class="history-special-remark" style="margin-bottom: 8px; padding: 10px; background: #E6F7FF; border: 1px dashed #1890FF; border-radius: 4px; color: #1890FF;">
                <strong>一审 特殊情况说明：</strong>
                <span>{{ specialRemark1Map[question.itemKey] }}</span>
              </div>
              <div v-if="!isSimplifiedAudit && reviewLevel > 2 && specialRemark2Map[question.itemKey]" class="history-special-remark" style="margin-bottom: 8px; padding: 10px; background: #FFF7E6; border: 1px dashed #FA8C16; border-radius: 4px; color: #FA8C16;">
                <strong>二审 特殊情况说明：</strong>
                <span>{{ specialRemark2Map[question.itemKey] }}</span>
              </div>
              
              <div class="review-options">
                <span class="review-label">{{ isSimplifiedAudit ? '审核结果：' : '评审结果：' }}</span>
                <el-radio-group 
                  v-model="reviewMap[question.itemKey]" 
                  size="small" 
                  @change="handleReviewChange(question, $event)"
                  :disabled="isReadOnlyMode || isReviewLocked(question)"
                >
                  <template v-if="isSimplifiedAudit">
                    <!-- 简化审核：通过/不通过 -->
                    <el-radio label="SCORE">
                      <i class="el-icon-check" style="color: #67C23A"></i> 通过
                    </el-radio>
                    <el-radio label="NO_SCORE">
                      <i class="el-icon-close" style="color: #F56C6C"></i> 不通过
                    </el-radio>
                  </template>
                  <template v-else>
                    <!-- 评分审核：得分/不得分/不涉及 -->
                    <el-radio label="SCORE" :disabled="!canSelectScore(question)">
                      <i class="el-icon-check" style="color: #67C23A"></i> 得分
                    </el-radio>
                    <el-radio label="NO_SCORE">
                      <i class="el-icon-close" style="color: #F56C6C"></i> 不得分
                    </el-radio>
                    <el-radio label="NOT_APPLICABLE" :disabled="!canSelectNotApplicable(question)">
                      <i class="el-icon-minus" style="color: #909399"></i> 不涉及
                    </el-radio>
                  </template>
                </el-radio-group>
              </div>
              <!-- 备注区域 -->
              <div class="review-remark" style="margin-top: 8px;">
                <!-- 历史失分备注（仅考试审核模式） -->
                <div v-if="!isSimplifiedAudit && reviewLevel > 1 && remark1Map[question.itemKey]" style="margin-bottom: 8px; padding: 10px; background: #F5F5F5; border: 1px dashed #909399; border-radius: 4px; color: #606266;">
                  <strong>一审 失分备注：</strong>
                  <span>{{ remark1Map[question.itemKey] }}</span>
                </div>
                <div v-if="!isSimplifiedAudit && reviewLevel > 2 && remark2Map[question.itemKey]" style="margin-bottom: 8px; padding: 10px; background: #F5F5F5; border: 1px dashed #909399; border-radius: 4px; color: #606266;">
                  <strong>二审 失分备注：</strong>
                  <span>{{ remark2Map[question.itemKey] }}</span>
                </div>
                
                <!-- 当前备注输入 -->
                <div class="current-input">
                  <div style="margin-bottom: 5px; display: flex; align-items: center;">
                    <span style="font-size: 13px; font-weight: 500; color: #606266;">{{ isSimplifiedAudit ? '审核备注' : '失分备注' }}</span>
                    <span v-if="reviewMap[question.itemKey] === 'NO_SCORE'" style="color: #F56C6C; margin-left: 5px; font-size: 12px;">* (该题不得分，请填写失分原因)</span>
                    <span v-else style="color: #909399; margin-left: 5px; font-size: 12px;">（可选）</span>
                  </div>
                  <el-input
                    type="textarea"
                    v-model="remarkMap[question.itemKey]"
                    :placeholder="isSimplifiedAudit ? '请填写不通过原因' : '请填写失分备注说明'"
                    :rows="2"
                    maxlength="500"
                    show-word-limit
                    size="small"
                    :disabled="isReadOnlyMode || (!isSimplifiedAudit && reviewMap[question.itemKey] === 'SCORE')"
                  />
                </div>
              </div>

              <!-- 特殊情况说明（仅考试审核模式显示） -->
              <div class="review-special-remark" v-if="!isSimplifiedAudit && formEnableSpecialRemark && question.enableScoring" style="margin-top: 8px; margin-bottom: 8px;">
                <!-- 当前输入框 -->
                <div class="current-input">
                  <div style="margin-bottom: 5px; display: flex; align-items: center;">
                    <span style="font-size: 13px; font-weight: 500; color: #606266;">特殊情况说明</span>
                    <span style="color: #909399; margin-left: 5px; font-size: 12px;">（可选）</span>
                  </div>
                  <el-input
                    type="textarea"
                    v-model="reviewSpecialRemarkMap[question.itemKey]"
                    :placeholder="reviewMap[question.itemKey] === 'NO_SCORE' ? '此题为扣分项，请填写特殊情况说明' : '如有特殊情况请在此说明（可选）'"
                    :rows="2"
                    maxlength="500"
                    show-word-limit
                    size="small"
                    :disabled="isReadOnlyMode"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="empty-tip">
          <p>请从左侧选择一份试卷进行阅卷</p>
        </div>
      </div>

      <!-- 底部固定按钮栏（仅中间区域） -->
      <div class="grading-footer">
        <el-button 
          v-if="!isReadOnlyMode"
          type="success" 
          size="medium" 
          @click="handleSubmitReview" 
          :disabled="!canEditCurrentLevel || submitLoading"
          :loading="submitLoading"
        >
          <i v-if="!submitLoading" class="el-icon-check"></i> {{ submitLoading ? '提交中...' : '提交审核' }}
        </el-button>
        <el-button 
          v-if="!isReadOnlyMode && currentExaminee"
          type="warning" 
          size="medium" 
          @click="saveDraft" 
          plain
        >
          <i class="el-icon-document-copy"></i> 保存草稿
        </el-button>
        <span v-if="isReadOnlyMode" class="readonly-text">
          <i class="el-icon-view"></i> 当前为只读模式，仅供查看
        </span>
        <el-button size="medium" @click="handleSaveAndBack">
          <i class="el-icon-back"></i> 返回
        </el-button>
      </div>
      </section>

      <!-- 右侧：评分/导航区域 -->
      <div class="right-panel">
        <!-- 得分区域（仅评分审核模式显示） -->
        <template v-if="!isSimplifiedAudit">
          <div class="score-summary">
            <span class="label">原始得分</span>
            <span class="score original">{{ originalScore }}</span>
            <span v-if="scoringRuleLabel" class="rule-chip" :title="scoringRuleFullLabel">{{ scoringRuleLabel }}</span>
          </div>

          <div class="score-summary review-score">
            <span class="label">审核得分</span>
            <span class="score" :class="{ changed: reviewScoreChanged }">{{ reviewScore }}</span>
          </div>
          
          <div class="level-display" v-if="scoreLevel">
            <span class="level-label">成绩</span>
            <span class="level-value" :style="{ color: scoreLevel.color }">{{ scoreLevel.name }}</span>
          </div>
        </template>
        
        <!-- 简化审核模式标题 -->
        <div v-else class="simplified-title">
          <span class="label">题目导航</span>
        </div>
        
        <!-- 搜索框 -->
        <div class="nav-search">
          <el-input
            v-model="navSearchKeyword"
            placeholder="搜索题目..."
            size="mini"
            prefix-icon="el-icon-search"
            clearable
          />
        </div>

        <!-- 题目导航列表 -->
        <div class="question-nav" :class="{ 'nav-list-mode': true }">
          <!-- 参考资料导航（仅考试模式显示） -->
          <template v-if="!isSimplifiedAudit && crossPhaseGroups.length > 0">
            <template v-for="group in crossPhaseGroups">
              <div :key="'nav-phase-' + group.phaseNo" class="nav-phase-title"
                   v-if="filteredRefItems(group).length > 0"
                   @click="$set(navCollapseMap, group.phaseNo, !navCollapseMap[group.phaseNo])">
                <i class="el-icon-arrow-right nav-collapse-arrow" :class="{ expanded: !navCollapseMap[group.phaseNo] }"></i>
                <span class="nav-phase-badge">{{ group.phaseNo }}</span>
                <span>{{ group.phaseName }}</span>
                <span class="nav-phase-count">{{ filteredRefItems(group).length }}题</span>
              </div>
              <template v-if="!navCollapseMap[group.phaseNo]">
              <div
                v-for="item in filteredRefItems(group)"
                :key="'nav-ref-' + group.phaseNo + '-' + item.itemKey"
                class="nav-item nav-list-item nav-ref-item"
                @click="scrollToRefItem(group.phaseNo, item.itemKey)"
                :title="stripHtml(item.label)"
              >
                <span class="nav-index">{{ item.serialNo }}.</span>
                <span class="nav-title">{{ truncateTitle(stripHtml(item.label), 10) }}</span>
                <span class="nav-ref-tag">参考</span>
              </div>
              </template>
            </template>
            <!-- 当前阶段分隔线 -->
            <div class="nav-section-divider" v-if="filteredAuditQuestions.length > 0">
              <span>当前审核</span>
            </div>
          </template>

          <!-- 当前阶段审核题导航 -->
          <div
            v-for="(q, i) in filteredAuditQuestions"
            :key="q.itemKey"
            class="nav-item nav-list-item"
            :class="getNavItemClass(q)"
            @click="scrollToQuestion(q._originalIndex)"
            :title="stripHtml(q.label)"
          >
            <span class="nav-index">{{ q.serialNo || (q._originalIndex + 1) }}.</span>
            <span class="nav-title">{{ truncateTitle(stripHtml(q.label), 10) }}</span>
            <span class="nav-status" v-if="reviewMap[q.itemKey]">
              <template v-if="isSimplifiedAudit">
                <span v-if="reviewMap[q.itemKey] === 'SCORE'" class="nav-pass">通过</span>
                <span v-else-if="reviewMap[q.itemKey] === 'NO_SCORE'" class="nav-fail">不通过</span>
              </template>
              <template v-else>
                <span v-if="reviewMap[q.itemKey] === 'SCORE'" class="nav-pass">得分</span>
                <span v-else-if="reviewMap[q.itemKey] === 'NO_SCORE'" class="nav-fail">失分</span>
                <span v-else-if="reviewMap[q.itemKey] === 'NOT_APPLICABLE'" style="color:#909399;font-size:11px;">不涉及</span>
              </template>
            </span>
          </div>
        </div>
        
        <!-- 切换按钮 -->
        <div class="nav-buttons">
          <el-button size="small" :disabled="currentIndex <= 0" @click="prevExaminee">上一份</el-button>
          <el-button size="small" :disabled="currentIndex >= filteredExamineeList.length - 1" @click="nextExaminee">下一份</el-button>
        </div>
      </div>
    </div>
    

    
    <!-- 悬浮媒体播放器 -->
    <FloatingMediaPlayer
      :visible.sync="floatingMedia.visible"
      :type="floatingMedia.type"
      :src="floatingMedia.src"
      :title="floatingMedia.title"
      :start-time="floatingMedia.startTime || 0"
      @close="closeFloatingMedia"
    />
  </div>
</template>

<script>
import AudioPlayer from './components/AudioPlayer.vue'
import FloatingMediaPlayer from './components/FloatingMediaPlayer.vue'
import { parseAgreementAnswer as _parseAgreement, renderPdfToContainer } from '@/utils/agreementPdf'
import { evaluateLogic } from '../../../survey/views/fill/LogicEngine'

export default {
  name: 'ExamGrading',
  components: {
    AudioPlayer,
    FloatingMediaPlayer
  },
  props: {
    formKey: {
      type: String,
      required: true
    },
    projectType: {
      type: Number,
      default: 0
    },
    // 从数据页面选中进入时，指定默认选中的考生
    initialAnswerKey: {
      type: String,
      default: null
    },
    // #21 阶段号（1/2/3/4），0表示非分阶段审核
    phase: {
      type: Number,
      default: 0
    },
    // 是否考试问卷（需多级审核）
    examForm: {
      type: Number,
      default: 0
    },
    waveId: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      examineeList: [],
      currentExaminee: null,
      currentIndex: -1,
      questionList: [],
      // 一票否决推翻：本次审核是否推翻及理由（仅内存，提交后清空）
      pendingVetoOverride: { enabled: false, reason: '' },
      answerMap: {},        // itemKey -> 用户原始答案
      reviewAnswerMap: {},  // itemKey -> 审核人修改后的答案（当前审核级别）
      review1AnswerMap: {}, // itemKey -> 一审修改后的答案（二审/三审时使用）
      review2AnswerMap: {}, // itemKey -> 二审修改后的答案（三审时使用）
      reviewMap: {},        // itemKey -> SCORE/NO_SCORE/NOT_APPLICABLE
      preloadedReviewKeys: new Set(),  // 上一轮继承的审核结果的 itemKey（用于驳回重审时锁定）
      remarkMap: {},        // itemKey -> remark text (当前输入)
      remark1Map: {},       // itemKey -> 一审失分备注
      remark2Map: {},       // itemKey -> 二审失分备注
      reviewResult1Map: {}, // itemKey -> 一审审核结果（SCORE/NO_SCORE/NOT_APPLICABLE）
      reviewResult2Map: {}, // itemKey -> 二审审核结果（SCORE/NO_SCORE/NOT_APPLICABLE）
      touchedQuestions: {}, // itemKey -> true，用户手动操作过的题目，不再显示蓝色原答案标记
      reviewLevel: 1,       // 当前审核级别 1/2/3
      statusFilter: '',     // 状态筛选
      loading: false,
      submitLoading: false, // 提交审核中
      detailLoading: false, // 答题详情加载中
      updateCounter: 0,     // 用于强制计算属性重新计算
      answerCache: {},      // answerKey -> { answers: [], reviewDetail: {} }
      preloadQueue: [],     // 预加载队列
      otherTextMap: {},     // itemKey -> 其他选项的文本输入
      specialRemarkMap: {}, // itemKey -> 神客特殊情况说明
      reviewSpecialRemarkMap: {}, // itemKey -> 当前审核级别的特殊情况说明
      specialRemark1Map: {}, // itemKey -> 一审特殊情况说明
      specialRemark2Map: {}, // itemKey -> 二审特殊情况说明
      specialRemark3Map: {},  // itemKey -> 三审特殊情况说明
      // 明检-文件题：审核员证据数据，按级别独立存储
      // 结构：evidenceData[level][itemKey] = { selectedEvidenceUrls: [...], appendedFiles: [{url,name,size,type}] }
      evidenceData: { 1: {}, 2: {}, 3: {} },
      formEnableSpecialRemark: true, // 表单级别的特殊说明开关（默认开启，审核端始终显示）
      // === 跨阶段合并展示 ===
      crossPhaseGroups: [], // 其他阶段的参考资料 [{phaseNo, phaseName, items: [...]}]
      crossPhaseLoading: false,
      refPdfStates: {}, // 参考资料 PDF 渲染状态 { 'phaseNo_itemKey': 'loading'|'done'|'error' }
      // === 悬浮媒体播放器 ===
      floatingMedia: { visible: false, type: 'image', src: '', title: '' },
      // === 导航搜索 ===
      navSearchKeyword: '',
      navCollapseMap: {}, // phaseNo -> boolean（折叠状态）
      gradingPdfStates: {}, // PDF渲染状态
      logicRules: [],
      logicHiddenSet: new Set(),

      // ===== 42 号方案 D.1：作废 / 修改审核 / 恢复 / 审核历史 =====
      // 作废弹窗
      voidDialogVisible: false,
      voidDialogLevel: 0,
      voidDialogReason: '',
      voidSubmitting: false,
      // 修改审核弹窗（进入编辑模式前的原因确认）
      modifyDialogVisible: false,
      modifyDialogLevel: 0,
      modifyDialogReason: '',
      // 修改模式下的上下文（进入后，页面切到该级别的可编辑状态，保存时会走 modify 接口）
      modifyMode: { active: false, level: 0, reason: '' },
      // 审核历史抽屉
      historyDrawerVisible: false,
      historyLoading: false,
      reviewHistoryList: [],
      // 恢复弹窗
      restoreDialogVisible: false,
      restoreDialogTarget: null,
      restoreDialogReason: '',
      restoreSubmitting: false
    }
  },
  computed: {
    totalCount() {
      return this.examineeList.length
    },
    gradedCount() {
      return this.examineeList.filter(e => e.auditStatus >= this.reviewLevel).length
    },
    filteredExamineeList() {
      if (this.statusFilter === '' || this.statusFilter === null) {
        return this.examineeList
      }
      return this.examineeList.filter(e => e.auditStatus === this.statusFilter)
    },
    // 审核页题目列表：对齐答题端，只按 itemKey 过滤隐藏题目
    filteredQuestionList() {
      const hidden = this.logicHiddenSet instanceof Set ? this.logicHiddenSet : new Set()
      return (this.questionList || []).filter(q => !hidden.has(q.itemKey))
    },
    // 搜索过滤后的审核题导航列表（带原始索引）
    filteredAuditQuestions() {
      const keyword = (this.navSearchKeyword || '').trim().toLowerCase()
      const list = this.filteredQuestionList
      const result = []
      // 状态关键词映射
      const statusKeywords = {
        '得分': 'SCORE', '通过': 'SCORE',
        '失分': 'NO_SCORE', '不得分': 'NO_SCORE', '不通过': 'NO_SCORE',
        '不涉及': 'NOT_APPLICABLE'
      }
      // 检查搜索词是否是状态关键词
      const matchedStatus = keyword ? statusKeywords[keyword] : null
      for (let i = 0; i < list.length; i++) {
        const q = list[i]
        if (keyword) {
          if (matchedStatus) {
            // 按审核状态筛选
            if (this.reviewMap[q.itemKey] !== matchedStatus) continue
          } else {
            // 按题目标题/序号搜索
            if (!this.stripHtml(q.label).toLowerCase().includes(keyword)
                && !(q.serialNo && q.serialNo.toLowerCase().includes(keyword))) {
              continue
            }
          }
        }
        result.push({ ...q, _originalIndex: i })
      }
      return result
    },
    // 原始得分（考生答题后系统自动计算的）
    // 按规则展示：bonusNormalized / percentage 优先用后端算好的 finalScore，其它规则用 totalScore
    originalScore() {
      if (!this.currentExaminee) return '0.0'
      const rule = this.currentScoringRule
      if ((rule === 'bonusNormalized' || rule === 'percentage') && this.currentExaminee.finalScore != null) {
        return parseFloat(this.currentExaminee.finalScore).toFixed(1)
      }
      if (this.currentExaminee.totalScore !== undefined && this.currentExaminee.totalScore !== null) {
        return parseFloat(this.currentExaminee.totalScore).toFixed(1)
      }
      return '0.0'
    },
    // 审核得分聚合（根据当前评审结果实时计算）
    // 返回 { regularScore, bonusScore, regularFullScore, bonusFullScore, regularNaExcluded, bonusNaExcluded, totalScore }
    reviewAggregate() {
      // 依赖 updateCounter 确保响应式更新
      const _trigger = this.updateCounter
      let regularScore = 0
      let bonusScore = 0
      let regularFullScore = 0
      let bonusFullScore = 0
      let regularNaExcluded = 0
      let bonusNaExcluded = 0

      for (const q of this.filteredQuestionList) {
        if (!q.enableScoring) continue
        const result = this.reviewMap[q.itemKey]
        const max = q.maxScore || 0
        const isBonus = q.isBonus === true

        // 累计题目满分到对应桶（含 NA 题，NA 单独再在 naExcluded 里扣）
        if (isBonus) bonusFullScore += max
        else regularFullScore += max

        // 按审核结果累计得分
        if (result === 'SCORE') {
          let earned = max
          // 保留 perOption 模式的既有处理（SCORE 视为满分）
          if (q.scoringMethod === 'perOption' && this.hasAllOptionsWithScore(q)) {
            earned = max
          }
          if (isBonus) bonusScore += earned
          else regularScore += earned
        } else if (result === 'NO_SCORE') {
          // perOption 模式下 NO_SCORE 按选项加减累计
          if (q.scoringMethod === 'perOption' && this.hasAllOptionsWithScore(q)) {
            const reviewAnswer = this.reviewAnswerMap[q.itemKey]
            if (reviewAnswer) {
              let selectedValues = []
              try {
                const parsed = JSON.parse(reviewAnswer)
                selectedValues = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)]
              } catch {
                selectedValues = [String(reviewAnswer)]
              }
              let positiveSum = 0
              let negativeSum = 0
              selectedValues.forEach(val => {
                let opt = q.options.find(o => String(o.value) === val)
                if (!opt) {
                  opt = q.options.find(o => this.stripHtml(o.value) === val || this.stripHtml(o.label) === val)
                }
                if (opt && opt.score) {
                  if (opt.score > 0) positiveSum += opt.score
                  else negativeSum += opt.score
                }
              })
              let baseScore = 0
              if (positiveSum === 0 && negativeSum < 0) baseScore = max
              let calculated = baseScore + positiveSum
              if (calculated > max) calculated = max
              let earned = calculated + negativeSum
              if (earned < 0) earned = 0
              if (isBonus) bonusScore += earned
              else regularScore += earned
            }
          }
          // 非 perOption：NO_SCORE 不加分
        } else if (result === 'NOT_APPLICABLE') {
          if (isBonus) bonusNaExcluded += max
          else regularNaExcluded += max
        }
        // 未审核的题目按"按满分池保留、不加得分"处理（和老逻辑一致）
      }

      return {
        regularScore, bonusScore,
        regularFullScore, bonusFullScore,
        regularNaExcluded, bonusNaExcluded,
        // 合计值（供 simple / percentage 规则用）
        totalScore: regularScore + bonusScore,
        totalFullScore: regularFullScore + bonusFullScore,
        totalNaExcluded: regularNaExcluded + bonusNaExcluded
      }
    },
    // 当前答卷使用的算分规则（取提交时的快照，审核期间不受项目规则调整影响）
    currentScoringRule() {
      const r = this.currentExaminee && this.currentExaminee.scoringRule
      return r || 'simple'
    },
    scoringRuleLabel() {
      const map = { simple: '原始分', percentage: '百分制', bonusNormalized: '加分+百分制', custom: '自定义' }
      return map[this.currentScoringRule] || ''
    },
    scoringRuleFullLabel() {
      const map = {
        simple: '原始总分 (simple)：final = 原始得分',
        percentage: '百分比归一 (percentage)：final = 得分 / (满分 − 不涉及) × 100',
        bonusNormalized: '加分题 + 百分制 (bonusNormalized)：final = 加分得分 + (常规得分 / (常规满分 − 常规不涉及)) × 100',
        custom: '自定义 (custom)：暂未实现，按 simple 兜底'
      }
      return map[this.currentScoringRule] || ''
    },
    // 审核得分（按规则展示；保持字符串返回以兼容既有模板）
    reviewScore() {
      const agg = this.reviewAggregate
      const rule = this.currentScoringRule
      if (rule === 'bonusNormalized') {
        const denom = (agg.regularFullScore - agg.regularNaExcluded)
        const base = denom > 0 ? (agg.regularScore / denom * 100) : 0
        return (agg.bonusScore + base).toFixed(1)
      }
      if (rule === 'percentage') {
        const denom = (agg.totalFullScore - agg.totalNaExcluded)
        const pct = denom > 0 ? (agg.totalScore / denom * 100) : 0
        return pct.toFixed(1)
      }
      // simple / custom（custom 暂兜底走 simple）
      return agg.totalScore.toFixed(1)
    },
    // 审核得分是否与原始得分不同
    reviewScoreChanged() {
      return this.reviewScore !== this.originalScore && this.reviewScore !== '0.0'
    },
    scoreLevel() {
      // 使用审核得分来计算等级（如果有审核结果），否则使用原始得分
      const score = this.reviewScoreChanged ? parseFloat(this.reviewScore) : parseFloat(this.originalScore)
      if (score >= 90) return { name: '优秀', color: '#67C23A' }
      if (score >= 75) return { name: '良好', color: '#409EFF' }
      if (score >= 60) return { name: '及格', color: '#E6A23C' }
      return { name: '不及格', color: '#F56C6C' }
    },
    // 当前级别文本
    reviewLevelText() {
      const texts = { 1: '一', 2: '二', 3: '三' }
      return texts[this.reviewLevel] || ''
    },

    // ===== 42 号方案 D.1：数据治理工具条计算属性 =====
    // 已完成的审核级别（audit_status 取值：0/1/2/3）
    completedLevels() {
      const s = this.currentExaminee && this.currentExaminee.auditStatus
      const max = Math.max(0, Math.min(3, Number(s) || 0))
      const list = []
      for (let i = 1; i <= max; i++) list.push(i)
      return list
    },
    // 可作废的级别：所有已完成级别（作废 N 级会级联清除 ≥ N，所以这里的级别是"作废起点"）
    voidableLevels() {
      return this.completedLevels
    },
    // 可修改的级别：所有已完成级别
    modifiableLevels() {
      return this.completedLevels
    },
    reviewHistoryCount() {
      return (this.reviewHistoryList || []).length
    },
    // 当前级别是否可编辑
    canEditCurrentLevel() {
      if (!this.currentExaminee) return false
      const status = this.currentExaminee.auditStatus || 0
      // 管理员始终可编辑（允许重新审核已完成的答卷）
      if (this.isAdminUser) return true
      // 有审核级别切换（examForm===1）时，使用reviewLevel逻辑
      if (this.examForm === 1) {
        return this.reviewLevel === status + 1 || (status === 0 && this.reviewLevel === 1)
      }
      // 纯分阶段模式（无审核级别）：未审核(0)或驳回重审(-1)都可编辑
      if (this.phase > 0) return status === 0 || status === -1
      // 考试模式
      return this.reviewLevel === status + 1 || (status === 0 && this.reviewLevel === 1)
    },
    // 是否是只读模式
    isReadOnlyMode() {
      if (!this.currentExaminee) return false
      // 管理员永不只读（允许重新审核已完成的答卷）
      if (this.isAdminUser) return false
      const status = this.currentExaminee.auditStatus || 0
      // 有审核级别切换（examForm===1）时，按reviewLevel判断
      if (this.examForm === 1) {
        const result = this.reviewLevel <= status
        return result
      }
      // 纯分阶段模式（无审核级别）：已完成(>=1)才只读；驳回重审(-1)不只读
      if (this.phase > 0) return status >= 1
      // 考试模式
      const result = this.reviewLevel <= status
      return result
    },
    // 是否需要先完成上级审核（选择的级别比当前状态高出超过1级）
    needPreviousLevel() {
      if (!this.currentExaminee) return false
      const status = this.currentExaminee.auditStatus || 0
      // 如果当前选择的级别比已完成的状态高出超过1级，需要先完成上级
      return this.reviewLevel > status + 1
    },
    // 需要完成哪一级审核的文本
    previousLevelText() {
      const texts = { 1: '一', 2: '二', 3: '三' }
      const status = this.currentExaminee?.auditStatus || 0
      return texts[status + 1] || '一'
    },
    // #21 是否为简化审核模式（阶段1/2/4只需通过/不通过）
    isSimplifiedAudit() {
      return this.phase > 0 && this.phase !== 3
    },
    // 当前用户的审核权限列表（来自 jkd_roles_tb.check_permission）
    allowedReviewLevels() {
      const info = this.$store.getters.userInfo || {}
      const perm = info.check_permission
      // check_permission 可能是数组（已在 api/index.js 中解析）或逗号分隔字符串
      if (Array.isArray(perm) && perm.length > 0) {
        return perm.map(Number).filter(n => n >= 1 && n <= 3)
      }
      if (typeof perm === 'string' && perm.trim()) {
        return perm.split(',').map(Number).filter(n => n >= 1 && n <= 3)
      }
      // 没有配置 check_permission → 默认全部开放（管理员等角色）
      return [1, 2, 3]
    },
    // 是否为管理员/超级管理员（不受审核权限限制）
    isAdminUser() {
      const info = this.$store.getters.userInfo || {}
      const roleName = (info.roles && info.roles.name) || info.role_name || info.roleName || ''
      return ['管理员', '超级管理员', '技术管理员', 'admin', 'superadmin'].includes(roleName)
    },
    // 42 号方案 §10.4 / §11.4：数据治理权限 = 研究员（RD）或管理员
    // 兼顾系统级和项目级两种角色来源；首期按用户信息里的 role_code/roles.name 初判，后端接口仍会二次守卫
    isResearcherUser() {
      const info = this.$store.getters.userInfo || {}
      const roleName = (info.roles && info.roles.name) || info.role_name || info.roleName || ''
      const roleCode = info.role_code || (info.roles && info.roles.code) || ''
      return ['研究员', '研究', 'RD'].includes(roleName) || ['RD', 'rd'].includes(roleCode)
    },
    // 综合：研究员 + 管理员 → 拥有数据治理权限（作废 / 修改 / 恢复）
    canGovernReview() {
      return this.isAdminUser || this.isResearcherUser
    },
    // 是否显示原答案标记（蓝色勾+"原答案"标签）
    // 新规则：一审/二审/三审都显示神客原答案，审核员做参考
    shouldShowOriginalMarks() {
      return true
    }
  },
  created() {
    // 先加载题目，再加载答卷并选中考生，避免逻辑计算时 questionList 尚未就绪
    this.initPageData()
    // 根据用户审核权限自动选择可用的最低级别
    if (!this.isAdminUser && this.allowedReviewLevels.length > 0) {
      const minLevel = Math.min(...this.allowedReviewLevels)
      if (minLevel !== this.reviewLevel) {
        this.reviewLevel = minLevel
      }
    }
  },
  mounted() {
    // 调整父容器样式，让阅卷页面填满整个可视区域（减去顶部导航60px）
    const dataPage = this.$el.closest('.data-page')
    if (dataPage) {
      this._originalHeight = dataPage.style.height
      this._originalBackground = dataPage.style.background
      dataPage.style.height = 'calc(100vh - 60px)'
      dataPage.style.background = '#f5f7fa'
    }
  },
  beforeDestroy() {
    // 恢复父容器原始样式
    const dataPage = this.$el.closest('.data-page')
    if (dataPage) {
      if (this._originalHeight !== undefined) {
        dataPage.style.height = this._originalHeight
      }
      if (this._originalBackground !== undefined) {
        dataPage.style.background = this._originalBackground
      }
    }
  },
  methods: {
    async initPageData() {
      await this.loadQuestions()
      await this.loadExamineeList()
    },

    async loadExamineeList() {
      try {
        this.loading = true
        const { getAnswerPage } = await import('index/api/project/form')
        const params = { formKey: this.formKey, page: 1, size: 500 }
        if (this.waveId) params.waveId = this.waveId
        const res = await getAnswerPage(params)
        const data = res.data?.data || res.data || {}
        const records = data.records || data.list || []
        
        this.examineeList = records.map(r => ({
          answerKey: r.answerKey,
          submitTime: r.submitTime,
          durationSeconds: r.durationSeconds,
          totalScore: r.totalScore,
          fullScore: r.fullScore,
          reviewedScore: r.reviewedScore,
          reviewedFullScore: r.reviewedFullScore,
          // ===== 算分规则快照（供本地实时计算用）=====
          scoringRule: r.scoringRule || 'simple',
          bonusScore: r.bonusScore,
          bonusFullScore: r.bonusFullScore,
          regularFullScore: r.regularFullScore,
          finalScore: r.finalScore,
          reviewedBonusScore: r.reviewedBonusScore,
          reviewedFinalScore: r.reviewedFinalScore,
          auditStatus: r.auditStatus || 0,
          dealerCode: r.dealerCode || r.DealerCode || null,
          uniqueCode: r.uniqueCode || null,
          dealerName: r.dealerName || r.DealerName || ''
          // 注意：不再在列表加载时存储 answers，改为选中时加载
        }))
        
        // 如果后端返回了 answers，缓存起来
        records.forEach(r => {
          if (r.answers && r.answers.length > 0) {
            this.answerCache[r.answerKey] = { answers: r.answers }
          }
        })
        
        this.loading = false
        
        if (this.examineeList.length > 0) {
          if (this.initialAnswerKey) {
            const targetExaminee = this.examineeList.find(e => e.answerKey === this.initialAnswerKey)
            if (targetExaminee) {
              await this.selectExaminee(targetExaminee)
            } else {
              await this.selectExaminee(this.examineeList[0])
            }
          } else {
            await this.selectExaminee(this.examineeList[0])
          }
        }
      } catch (e) {
        this.loading = false
        console.error('[ExamGrading] Load examinee list failed:', e)
      }
    },
    
    async loadQuestions() {
      try {
        const { getFormItems } = await import('index/api/project/form')
        const res = await getFormItems(this.formKey)
        const items = res.data?.data || res.data || []
        
        
        this.questionList = items.filter(item => {
          // 兼容 itemType 和 item_type 两种字段名
          const type = item.itemType || item.item_type
          return !this.isPaginationItem(type)
        }).map(item => {
          // 兼容 itemType 和 item_type 两种字段名
          let itemType = item.itemType || item.item_type

          // 解析 config_json 获取配置
          let config = {}
          try {
            const configStr = item.configJson || item.config_json || '{}'
            config = JSON.parse(configStr)
          } catch (e) {

          }

          // 防御：若后端 itemType 与 config.tag 不一致（历史数据/保存时序问题），以 config.tag 为准
          // 特别是明检题，tagIcon 会撞到普通题，itemType 可能被错误保存
          const cfgTag = config.tag || config.__config__?.tag
          const tagToItemType = {
            'mingjian-photo-radio': 'MINGJIAN_PHOTO_RADIO',
            'mingjian-photo-checkbox': 'MINGJIAN_PHOTO_CHECKBOX',
            'mingjian-photo-text': 'MINGJIAN_PHOTO_TEXT',
            'mingjian-file-radio': 'MINGJIAN_FILE_RADIO',
            'mingjian-file-checkbox': 'MINGJIAN_FILE_CHECKBOX'
          }
          if (cfgTag && tagToItemType[cfgTag] && itemType !== tagToItemType[cfgTag]) {
            console.warn('[ExamGrading] itemType 修正:', { itemKey: item.itemKey || item.item_key, original: itemType, corrected: tagToItemType[cfgTag], tag: cfgTag })
            itemType = tagToItemType[cfgTag]
          }
          
          // 获取选项：优先使用 options，兼容不同字段名
          // 合并 config_json.__slot__.options 中的 notApplicable、score 等属性
          const rawOptions = item.options || item.optionsRaw || []
          const slotOptions = config.__slot__?.options || []
          const options = rawOptions.map((opt, idx) => {
            const slotOpt = slotOptions.find(so => String(so.value) === String(opt.value)) || slotOptions[idx]
            if (slotOpt) {
              return { ...opt, ...(slotOpt.score !== undefined ? { score: slotOpt.score } : {}), ...(slotOpt.notApplicable !== undefined ? { notApplicable: slotOpt.notApplicable } : {}) }
            }
            return opt
          })
          
          // 尝试从多个来源获取分数（兼容不同配置格式）
          let scoreValue = item.score || item.maxScore || config.score || config.__config__?.score || 0
          
          // 如果没有直接的分数字段，尝试从选项中获取最大分数
          if (!scoreValue && options.length > 0) {
            const optionScores = options.map(opt => opt.score || 0)
            scoreValue = Math.max(...optionScores)
          }
          
          // 直接使用题目设置的分数，不设置默认值
          
          // 获取正确答案：优先从 config_json 中获取
          const correctAnswer = item.correctAnswer || config.correctAnswer || null
          const cfg = config || {}
          const cfgConfig = cfg.__config__ || {}
          const cfgSlot = cfg.__slot__ || {}
          const pick = (...vals) => {
            for (const v of vals) {
              if (v === null || v === undefined) continue
              const s = String(v).trim()
              if (s && s !== 'null' && s !== 'undefined') return s
            }
            return ''
          }

          const mappedQuestion = {
            itemKey: item.itemKey || item.item_key,
            itemType: itemType,
            typeName: this.getTypeName(itemType),
            label: item.label || item.title,
            serialNo: pick(item.serialNo, item.serial_no, cfgConfig.serialNo, cfgConfig.serial_no, cfg.serialNo, cfg.serial_no, cfgSlot.serialNo, cfgSlot.serial_no),
            options: options,
            correctAnswer: correctAnswer,
            maxScore: scoreValue,
            configJson: item.configJson || item.config_json || '{}',
            description: item.description || cfgConfig.tipContent || cfg.tipContent || cfgConfig.description || '',
            tipContent: item.tipContent || item.tip_content || cfgConfig.tipContent || cfg.tipContent || '',
            scoringMethod: cfg.scoringMethod || cfgConfig.scoringMethod || 'single',
            enableScoring: cfg.enableScoring !== undefined ? cfg.enableScoring : cfgConfig.enableScoring,
            isBonus: (cfgConfig.isBonus === true) || (cfg.isBonus === true),
            enableSpecialRemark: cfg.enableSpecialRemark,
            dataSourceType: pick(item.dataSourceType, item.data_source_type, item.datasourceType, item.datasource_type, cfg.dataSourceType, cfg.data_source_type, cfg.datasourceType, cfgConfig.dataSourceType, cfgConfig.data_source_type, cfgSlot.dataSourceType, cfgSlot.data_source_type),
            sourceFormKey: pick(item.sourceFormKey, item.source_form_key, item.sourceformkey, cfg.sourceFormKey, cfg.source_form_key, cfg.sourceformkey, cfgConfig.sourceFormKey, cfgConfig.source_form_key, cfgSlot.sourceFormKey, cfgSlot.source_form_key),
            sourceItemKey: pick(item.sourceItemKey, item.source_item_key, item.sourceitemkey, cfg.sourceItemKey, cfg.source_item_key, cfg.sourceitemkey, cfgConfig.sourceItemKey, cfgConfig.source_item_key, cfgSlot.sourceItemKey, cfgSlot.source_item_key),
            // 关系ID（后端关系表返回）
            relationIds: Array.isArray(item.relationIds) ? item.relationIds : (Array.isArray(item.relation_ids) ? item.relation_ids : (item.relationId ? [item.relationId] : (item.relation_id ? [item.relation_id] : []))),
            isReferenced: item.isReferenced === true || item.is_referenced === true,
            isLoopbackReference: item.isLoopbackReference === true || item.is_loopback_reference === true,
            isLoopbackSource: item.isLoopbackSource === true || item.is_loopback_source === true,
            // 题目属性键（回圈关联有时引用 __vModel__）
            vModel: pick(item.__vModel__, item.vModel, item.v_model, cfg.__vModel__, cfg.vModel, cfg.v_model, cfgConfig.__vModel__, cfgConfig.vModel, cfgSlot.__vModel__, cfgSlot.vModel),
            // 回圈选项映射（可选）：key=源题选项value，value=目标题选项value
            // 兼容多种历史字段名（含 __slot__）
            optionMapping: item.optionMapping || item.sourceOptionMapping || item.loopbackOptionMapping || config.optionMapping || config.sourceOptionMapping || config.loopbackOptionMapping || config.__config__?.optionMapping || config.__config__?.sourceOptionMapping || config.__config__?.loopbackOptionMapping || config.__slot__?.optionMapping || config.__slot__?.sourceOptionMapping || config.__slot__?.loopbackOptionMapping || null,
            exclusive: config.exclusive || config.__config__?.exclusive || false,
            exclusiveOptions: config.exclusiveOptions || config.__config__?.exclusiveOptions || [],
            exclusiveGroups: config.exclusiveGroups || config.__config__?.exclusiveGroups || []
          }


          return mappedQuestion
        })
      } catch (e) {
        console.error('[ExamGrading] Load questions failed:', e)
      }
    },
    
    async selectExaminee(examinee) {
      this.currentExaminee = examinee
      this.currentIndex = this.filteredExamineeList.findIndex(e => e.answerKey === examinee.answerKey)

      // 先加载逻辑规则，确保审核端题目可见性与答题端一致
      await this.ensureLogicRulesLoaded()
      
      // 重置答案映射
      this.answerMap = {}
      this.reviewAnswerMap = {}
      this.reviewMap = {}
      this.preloadedReviewKeys = new Set()
      this.logicHiddenSet = new Set()
      this.remarkMap = {}
      this.review1AnswerMap = {}
      this.review2AnswerMap = {}
      this.reviewResult1Map = {}
      this.reviewResult2Map = {}
      this.touchedQuestions = {}
      this.otherTextMap = {}
      this.specialRemarkMap = {}
      this.reviewSpecialRemarkMap = {}
      this.specialRemark1Map = {}
      this.specialRemark2Map = {}
      this.specialRemark3Map = {}
      
      const answerKey = examinee.answerKey
      
      // 从缓存加载答题详情，或发起请求
      let answers = []
      let reviewDetail = null
      
      if (this.answerCache[answerKey]) {
        // 命中缓存
        answers = this.answerCache[answerKey].answers || []
        reviewDetail = this.answerCache[answerKey].reviewDetail
      } else {
        // 需要加载
        this.detailLoading = true
        try {
          const { getAnswerDetail } = await import('index/api/project/form')
          const res = await getAnswerDetail(answerKey)
          const data = res.data?.data || res.data || {}
          answers = data.answers || []
          // 缓存答题详情
          this.answerCache[answerKey] = { answers }
        } catch (e) {
          console.warn('[ExamGrading] Load answer detail failed:', e)
        }
        this.detailLoading = false
      }
      
      // 构建答案映射（只记录原答案，不自动填充审核答案）
      answers.forEach(ans => {
        const key = ans.itemKey || ans.item_key
        const value = ans.value || ans.answer
        this.$set(this.answerMap, key, value)
        // 保存"其他"选项的文本输入
        if (ans.other_text || ans.otherText) {
          this.$set(this.otherTextMap, key, ans.other_text || ans.otherText)
        }
      })

      // 按答题端逻辑引擎计算隐藏题（审核端与答题端保持一致）
      this.applyLogicVisibility()
      
      // 加载审核详情（带缓存）
      if (reviewDetail) {
        this.applyReviewDetail(reviewDetail)
      } else {
        try {
          const { getReviewDetail } = await import('index/api/project/form')
          const res = await getReviewDetail(answerKey)
          const data = res.data?.data || res.data || {}
          // 缓存审核详情
          if (this.answerCache[answerKey]) {
            this.answerCache[answerKey].reviewDetail = data
          }
          this.applyReviewDetail(data)
        } catch (e) {
          console.error('[ExamGrading] Load review detail failed:', e)
        }
      }

      // 结合审核答案再次计算显示逻辑（保证审核端按实际触发条件显示）
      this.applyLogicVisibility()

      // === 调试：明检-文件题加载诊断（验证 itemType / tag / 答案数据） ===
      try {
        const fileQs = (this.questionList || []).filter(q => {
          let cfgTag = ''
          try { cfgTag = (JSON.parse(q.configJson || '{}').tag) || '' } catch (e) {}
          return /^MINGJIAN_FILE_/.test(q.itemType || '') || /^mingjian-file-/.test(cfgTag)
        })
        if (fileQs.length > 0) {
          console.group('[ExamGrading 诊断] 明检-文件题 共 ' + fileQs.length + ' 道')
          fileQs.forEach((q, i) => {
            let tag = ''
            try { tag = JSON.parse(q.configJson || '{}').tag || '' } catch (e) {}
            const raw = this.answerMap[q.itemKey]
            let parsed = null
            try { parsed = typeof raw === 'string' ? JSON.parse(raw) : raw } catch (e) {}
            const filesArr = this.getMingjianFiles(q.itemKey)
            const imgs = this.mingjianImageFiles(q.itemKey)
            console.log('#' + (i + 1), {
              itemKey: q.itemKey,
              label: (q.label || '').slice(0, 30),
              itemType: q.itemType,
              configTag: tag,
              isMingjianFile: this.isMingjianFileQuestion(q.itemType),
              answerMapHas: raw != null,
              answerRaw: raw ? String(raw).slice(0, 120) + '…' : null,
              parsedFile: parsed?.file || null,
              parsedFiles: parsed?.files || null,
              filesCount: filesArr.length,
              imageCount: imgs.length
            })
          })
          console.groupEnd()
        }
      } catch (diagErr) {
        console.warn('[ExamGrading 诊断] 失败', diagErr)
      }

      // === 加载跨阶段参考资料（严格同店：优先 uniqueCode） ===
      const crossPhaseCode = examinee.uniqueCode || examinee.dealerCode
      console.log('[ExamGrading] 跨阶段参考: uniqueCode=', examinee.uniqueCode, 'dealerCode=', examinee.dealerCode, '→ crossPhaseCode=', crossPhaseCode)
      if (crossPhaseCode) {
        await this.loadCrossPhaseData(crossPhaseCode)
        console.log('[ExamGrading] 跨阶段参考加载完成, groups=', this.crossPhaseGroups.length)
      } else {
        console.log('[ExamGrading] 跨阶段参考: 无 dealerCode，跳过加载')
        this.crossPhaseGroups = []
      }

      // 将“同店跨阶段答案”按回圈关系预填到当前审核答案
      if (this.phase !== 3) {
        this.applyCrossPhaseLoopbackDefaults()
      }
      // 反向回圈：其他阶段的题目引用了当前阶段的题目时，拉取其他阶段的答案
      this.applyReverseCrossPhaseLoopback()
      

      // 初始化未设置的题目
      this.questionList.forEach(q => {
        if (!(q.itemKey in this.reviewMap) || !this.reviewMap[q.itemKey]) {
          // 所有审核模式均不自动评分，等待审核员手动选择
          // - 简化审核模式（阶段1/2/4）：通过/不通过
          // - 多级审核模式（phase=0/phase=3/examForm=1）：得分/不得分/不涉及
        }
        if (!(q.itemKey in this.remarkMap)) {
          this.$set(this.remarkMap, q.itemKey, '')
        }
      })
      
      // 恢复草稿（如果有）
      this.restoreDraft()
      
      // 预加载相邻的项（后台静默加载）
      this.preloadAdjacent()
      
      // 渲染廉政协议 PDF
      this.$nextTick(() => this.renderGradingPdfs())
    },
    
    async ensureLogicRulesLoaded() {
      if (Array.isArray(this.logicRules) && this.logicRules.length > 0) return

      const isValidLogicRule = (r) => {
        if (!r || typeof r !== 'object') return false
        const logicType = r.logicType || r.logic_type
        const sourceItemKey = r.sourceItemKey || r.source_item_key
        const targetItemKey = r.targetItemKey || r.target_item_key
        return !!logicType && (!!sourceItemKey || !!targetItemKey)
      }

      let loadedRules = []

      // 1) 优先走逻辑接口
      try {
        const { getFormLogicRequest } = await import('index/api/project/form')
        const res = await getFormLogicRequest(this.formKey)
        const data = res.data?.data || res.data || []
        const candidate = Array.isArray(data) ? data : []
        loadedRules = candidate.filter(isValidLogicRule)

        if (candidate.length > 0 && loadedRules.length === 0) {
          console.warn('[ExamGrading] Logic API returned non-rule payload, fallback to form detail logic.')
        }
      } catch (e) {
        console.warn('[ExamGrading] Load logic rules from logic API failed:', e)
      }

      // 2) 兜底：与答题端一致，从表单详情里读取 logic
      if (!loadedRules || loadedRules.length === 0) {
        try {
          const { getProjectRequest } = await import('index/api/project/form')
          const res = await getProjectRequest(this.formKey)
          const formData = res.data?.data || res.data || {}
          const detailLogic = formData.logic
          const candidate = Array.isArray(detailLogic) ? detailLogic : []
          loadedRules = candidate.filter(isValidLogicRule)
        } catch (e) {
          console.warn('[ExamGrading] Load logic rules from form detail failed:', e)
        }
      }

      this.logicRules = Array.isArray(loadedRules) ? loadedRules : []
    },

    getEffectiveAnswerMap() {
      const normalize = (v) => {
        if (v === null || v === undefined) return ''
        const s = String(v).trim()
        return (!s || s === 'undefined' || s === 'null') ? '' : s
      }

      const parseLogicValue = (value) => {
        if (typeof value !== 'string') return value
        const s = value.trim()
        if (!s) return value
        if ((s.startsWith('"') && s.endsWith('"')) ||
            (s.startsWith('[') && s.endsWith(']')) ||
            (s.startsWith('{') && s.endsWith('}'))) {
          try {
            return JSON.parse(s)
          } catch (e) {
            return value
          }
        }
        return value
      }

      // 1) 先构建别名 -> itemKey 映射（UUID）
      const aliasToItemKey = new Map()
      ;(this.questionList || []).forEach(q => {
        const itemKey = normalize(q.itemKey)
        if (!itemKey) return

        const aliases = [itemKey, normalize(q.serialNo), normalize(q.vModel)]
        try {
          const cfg = JSON.parse(q.configJson || '{}')
          aliases.push(
            normalize(cfg.__vModel__ || cfg.vModel || cfg.v_model),
            normalize(cfg.__config__?.__vModel__ || cfg.__config__?.vModel || cfg.__config__?.v_model),
            normalize(cfg.__slot__?.__vModel__ || cfg.__slot__?.vModel || cfg.__slot__?.v_model)
          )
        } catch (e) {}

        ;[...new Set(aliases.filter(Boolean))].forEach(a => {
          if (!aliasToItemKey.has(a)) aliasToItemKey.set(a, itemKey)
        })
      })

      const merged = {}

      // 2) 原答案：无论 key 是 UUID/serialNo/vModel，统一回填到 UUID
      Object.keys(this.answerMap || {}).forEach(rawKey => {
        const canonicalKey = aliasToItemKey.get(normalize(rawKey)) || normalize(rawKey)
        const value = parseLogicValue(this.answerMap[rawKey])
        if (canonicalKey) merged[canonicalKey] = value
      })

      // 3) 审核端显隐只按神秘客原始答案计算：不使用 reviewAnswerMap 覆盖

      // 4) 选择题：若答案是选项文案，映射成 option.value（与规则 conditionValue 对齐）
      ;(this.questionList || []).forEach(q => {
        const itemKey = normalize(q.itemKey)
        if (!itemKey) return
        let value = merged[itemKey]
        if (value === null || value === undefined || String(value).trim() === '') return

        if (this.isChoiceQuestion(q.itemType) && Array.isArray(q.options) && q.options.length > 0) {
          const labelToValue = new Map()
          q.options.forEach(opt => {
            const plainLabel = normalize(this.stripHtml(opt.label || ''))
            if (plainLabel) labelToValue.set(plainLabel, opt.value)
          })

          const mapOne = (v) => {
            const nv = normalize(v)
            return (nv && labelToValue.has(nv)) ? labelToValue.get(nv) : v
          }

          value = Array.isArray(value) ? value.map(mapOne) : mapOne(value)
          merged[itemKey] = value
        }

        // 5) 再把同一个值铺到别名键（兼容规则 sourceItemKey 写法）
        const aliases = [normalize(q.serialNo), normalize(q.vModel)]
        try {
          const cfg = JSON.parse(q.configJson || '{}')
          aliases.push(
            normalize(cfg.__vModel__ || cfg.vModel || cfg.v_model),
            normalize(cfg.__config__?.__vModel__ || cfg.__config__?.vModel || cfg.__config__?.v_model),
            normalize(cfg.__slot__?.__vModel__ || cfg.__slot__?.vModel || cfg.__slot__?.v_model)
          )
        } catch (e) {}

        ;[...new Set(aliases.filter(Boolean))].forEach(alias => {
          merged[alias] = value
        })
      })

      return merged
    },

    applyLogicVisibility() {
      try {
        const effectiveAnswers = this.getEffectiveAnswerMap()

        // 规范化规则：补齐 formKey，避免 sourceFormKey 判定为 external 导致条件失效
        const normalizedRules = (this.logicRules || []).map(rule => {
          const r = { ...rule }
          if (!r.formKey && !r.form_key) {
            r.formKey = this.formKey
          }

          const actionConfig = r.actionConfig || r.action_config
          if (actionConfig) {
            try {
              const cfg = typeof actionConfig === 'string' ? JSON.parse(actionConfig) : { ...actionConfig }
              if (cfg?.conditionGroup?.conditions && Array.isArray(cfg.conditionGroup.conditions)) {
                cfg.conditionGroup.conditions = cfg.conditionGroup.conditions.map(c => {
                  const cc = { ...c }
                  if (!cc.sourceFormKey) cc.sourceFormKey = this.formKey
                  return cc
                })
                r.actionConfig = JSON.stringify(cfg)
              }
            } catch (e) {}
          }
          return r
        })

        const result = evaluateLogic(normalizedRules, effectiveAnswers)
        const rawHidden = result?.hidden instanceof Set ? result.hidden : new Set()

        // 审核页最终过滤按 itemKey；将 serialNo/vModel 等别名映射回 itemKey
        const hiddenByItemKey = new Set()
        const normalize = (v) => {
          if (v === null || v === undefined) return ''
          const s = String(v).trim()
          return (!s || s === 'undefined' || s === 'null') ? '' : s
        }

        const aliasToItemKey = new Map()
        ;(this.questionList || []).forEach(q => {
          const itemKey = normalize(q.itemKey)
          if (!itemKey) return

          const aliases = [itemKey, normalize(q.serialNo), normalize(q.vModel)]
          try {
            const cfg = JSON.parse(q.configJson || '{}')
            aliases.push(
              normalize(cfg.__vModel__ || cfg.vModel || cfg.v_model),
              normalize(cfg.__config__?.__vModel__ || cfg.__config__?.vModel || cfg.__config__?.v_model),
              normalize(cfg.__slot__?.__vModel__ || cfg.__slot__?.vModel || cfg.__slot__?.v_model)
            )
          } catch (e) {}

          ;[...new Set(aliases.filter(Boolean))].forEach(a => {
            if (!aliasToItemKey.has(a)) aliasToItemKey.set(a, itemKey)
          })
        })

        rawHidden.forEach(h => {
          const key = normalize(h)
          if (!key) return
          const itemKey = aliasToItemKey.get(key)
          if (itemKey) hiddenByItemKey.add(itemKey)
        })

        this.logicHiddenSet = hiddenByItemKey
      } catch (e) {
        console.warn('[ExamGrading] Evaluate logic failed:', e)
        this.logicHiddenSet = new Set()
      }
    },

    // 应用审核详情数据
    applyReviewDetail(data) {
      if (!data) return

      // 将 veto 字段同步到 currentExaminee（Banner / 推翻按钮展示用）
      if (this.currentExaminee) {
        this.$set(this.currentExaminee, 'vetoTriggered', !!data.vetoTriggered)
        this.$set(this.currentExaminee, 'vetoHits', Array.isArray(data.vetoHits) ? data.vetoHits : [])
        this.$set(this.currentExaminee, 'vetoReason', data.vetoReason || '')
        this.$set(this.currentExaminee, 'vetoOverridden', !!data.vetoOverridden)
        this.$set(this.currentExaminee, 'vetoOverrideReason', data.vetoOverrideReason || '')
        this.$set(this.currentExaminee, 'vetoOverriddenBy', data.vetoOverriddenBy || null)
        this.$set(this.currentExaminee, 'vetoOverriddenAt', data.vetoOverriddenAt || '')
        this.$set(this.currentExaminee, 'rawTotalScore', data.rawTotalScore != null ? data.rawTotalScore : null)
        this.$set(this.currentExaminee, 'reviewedScore', data.reviewedScore != null ? data.reviewedScore : 0)
      }

      if (!data.items) return

      data.items.forEach(item => {
        // 兜底：如果 answerMap 里没有这道题的原答案（getAnswerDetail 失败或字段名变更），
        // 用 review 接口返回的 answerValue 补齐。两接口返回的都是同一份 d.answer_value（已刷新 OSS 签名）。
        if (item.answerValue && !this.answerMap[item.itemKey]) {
          this.$set(this.answerMap, item.itemKey, item.answerValue)
        }
        // 装载明检-文件题的证据数据（按级别独立）
        ;[1, 2, 3].forEach(lvl => {
          const raw = item['evidenceData' + lvl]
          if (raw) {
            try {
              const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
              if (parsed && typeof parsed === 'object') {
                if (!this.evidenceData[lvl]) this.$set(this.evidenceData, lvl, {})
                this.$set(this.evidenceData[lvl], item.itemKey, {
                  selectedEvidenceUrls: Array.isArray(parsed.selectedEvidenceUrls) ? parsed.selectedEvidenceUrls : [],
                  appendedFiles: Array.isArray(parsed.appendedFiles) ? parsed.appendedFiles : []
                })
              }
            } catch (e) {}
          }
        })
        // 加载一审答案和结果
        if (item.reviewAnswer1) {
          this.$set(this.review1AnswerMap, item.itemKey, item.reviewAnswer1)
        }
        if (item.reviewResult1) {
          this.$set(this.reviewResult1Map, item.itemKey, item.reviewResult1)
        }
        // 加载二审答案和结果
        if (item.reviewAnswer2) {
          this.$set(this.review2AnswerMap, item.itemKey, item.reviewAnswer2)
        }
        if (item.reviewResult2) {
          this.$set(this.reviewResult2Map, item.itemKey, item.reviewResult2)
        }
        
        // 加载神客特殊说明
        if (item.specialRemark) {
          this.$set(this.specialRemarkMap, item.itemKey, item.specialRemark)
        }
        // 加载历史审核特殊说明
        if (item.specialRemark1) this.$set(this.specialRemark1Map, item.itemKey, item.specialRemark1)
        if (item.specialRemark2) this.$set(this.specialRemark2Map, item.itemKey, item.specialRemark2)
        if (item.specialRemark3) this.$set(this.specialRemark3Map, item.itemKey, item.specialRemark3)
        
        // 加载历史失分备注
        if (item.reviewRemark1) this.$set(this.remark1Map, item.itemKey, item.reviewRemark1)
        if (item.reviewRemark2) this.$set(this.remark2Map, item.itemKey, item.reviewRemark2)
        
        // 根据审核级别获取对应级别的结果、答案和备注
        // 简化审核模式（阶段1/2/4）统一按一审字段处理
        // 【修复】只预填"得分"(SCORE)的结果，"不得分"(NO_SCORE)不继承，让审核员重新判断
        let result, answer, remark, specialRemark
        if (this.isSimplifiedAudit) {
          result = item.reviewResult1
          answer = item.reviewAnswer1
          remark = item.reviewRemark1
          specialRemark = item.specialRemark1
        } else if (this.reviewLevel === 3) {
          // 优先取本级结果，否则只继承上一级"得分"的结果
          result = item.reviewResult3
          if (!result && item.reviewResult2 === 'SCORE') result = 'SCORE'
          else if (!result && !item.reviewResult2 && item.reviewResult1 === 'SCORE') result = 'SCORE'
          answer = item.reviewAnswer3 || (item.reviewResult2 === 'SCORE' ? item.reviewAnswer2 : null) || (item.reviewResult1 === 'SCORE' ? item.reviewAnswer1 : null)
          remark = item.reviewRemark3
          specialRemark = item.specialRemark3
        } else if (this.reviewLevel === 2) {
          result = item.reviewResult2
          if (!result && item.reviewResult1 === 'SCORE') result = 'SCORE'
          answer = item.reviewAnswer2 || (item.reviewResult1 === 'SCORE' ? item.reviewAnswer1 : null)
          remark = item.reviewRemark2
          specialRemark = item.specialRemark2
        } else {
          result = item.reviewResult1
          answer = item.reviewAnswer1
          remark = item.reviewRemark1
          specialRemark = item.specialRemark1
        }
        
        if (result) {
          // 多级审核模式（阶段3的一审/二审/三审）：只有该级别确实已审核（auditStatus >= reviewLevel）时才加载结果
          // 避免后端返回的自动计算结果导致题目被默认选中
          // 阶段1/2/4（简化审核）：始终预填，它们没有多级审核
          const auditStatus = this.currentExaminee?.auditStatus || 0
          if (!this.isSimplifiedAudit && auditStatus < this.reviewLevel) {
            // 阶段3多级审核：尚未审核到当前级别，不预填
          } else {
            this.$set(this.reviewMap, item.itemKey, result)
            // 记录从上一轮继承的审核结果（用于驳回重审时锁定）
            this.preloadedReviewKeys.add(item.itemKey)
          }
        }
        // 答案预填充逻辑
        if (answer && this.isReadOnlyMode) {
          // 只读模式：加载当前级别已有审核答案
          this.$set(this.reviewAnswerMap, item.itemKey, answer)
        } else if (this.isSimplifiedAudit && !this.isReadOnlyMode) {
          // 阶段1/2/4 简化审核编辑模式：已有审核结果时恢复答案
          // 确保提交后刷新不会"丢失"已选答案
          if (item.reviewResult1 && item.reviewAnswer1) {
            this.$set(this.reviewAnswerMap, item.itemKey, item.reviewAnswer1)
          }
        } else if (!this.isSimplifiedAudit && !this.isReadOnlyMode) {
          // 多级审核编辑模式：一审不预填，二审只选中一审得分的，三审只选中二审得分的
          let prevAnswer = null
          if (this.reviewLevel === 2 && item.reviewResult1 === 'SCORE') prevAnswer = item.reviewAnswer1
          else if (this.reviewLevel === 3 && item.reviewResult2 === 'SCORE') prevAnswer = item.reviewAnswer2
          // reviewLevel === 1（一审）：prevAnswer 保持 null，不预填任何答案
          if (prevAnswer) {
            this.$set(this.reviewAnswerMap, item.itemKey, prevAnswer)
          }
        }
        if (remark) {
          this.$set(this.remarkMap, item.itemKey, remark)
        }
        if (specialRemark) {
          this.$set(this.reviewSpecialRemarkMap, item.itemKey, specialRemark)
        }
      })
    },
    
    // 预加载相邻的考生详情（静默后台加载）
    async preloadAdjacent() {
      const list = this.filteredExamineeList
      const idx = this.currentIndex
      const preloadRange = 2 // 预加载前后各2个
      
      for (let offset = 1; offset <= preloadRange; offset++) {
        // 预加载后面的项
        if (idx + offset < list.length) {
          this.preloadExamineeDetail(list[idx + offset])
        }
        // 预加载前面的项
        if (idx - offset >= 0) {
          this.preloadExamineeDetail(list[idx - offset])
        }
      }
    },
    
    // 预加载单个考生的详情（静默，不显示 loading）
    async preloadExamineeDetail(examinee) {
      const answerKey = examinee.answerKey
      if (this.answerCache[answerKey]) return // 已缓存
      
      try {
        const { getAnswerDetail } = await import('index/api/project/form')
        const res = await getAnswerDetail(answerKey)
        const data = res.data?.data || res.data || {}
        this.answerCache[answerKey] = { answers: data.answers || [] }
        
        // 同时预加载审核详情
        const { getReviewDetail } = await import('index/api/project/form')
        const reviewRes = await getReviewDetail(answerKey)
        const reviewData = reviewRes.data?.data || reviewRes.data || {}
        this.answerCache[answerKey].reviewDetail = reviewData
      } catch (e) {
        // 预加载失败不影响正常使用
      }
    },
    
    // === 跨阶段数据加载 ===
    // 42 号方案 §9.3：跨阶段参考资料的状态徽章
    // - 文本：已驳回 / 未审核 / 一审通过 / 二审通过 / 已三审
    // - 颜色：rejected 红 / pending 灰 / level1 橙 / level2 橙 / done 绿
    // - 未审核 / 已驳回 视为"不可信"，上层 ref-phase-group 加警示样式
    crossPhaseAuditText(group) {
      const s = Number(group && group.auditStatus)
      if (s === -1) return '已驳回'
      if (s >= 3) return '已三审'
      if (s === 2) return '二审通过'
      if (s === 1) return '一审通过'
      return '未审核'
    },
    crossPhaseAuditClass(group) {
      const s = Number(group && group.auditStatus)
      if (s === -1) return 'audit-tag-rejected'
      if (s >= 3) return 'audit-tag-done'
      if (s >= 1) return 'audit-tag-level'
      return 'audit-tag-pending'
    },
    isCrossPhaseUntrusted(group) {
      const s = Number(group && group.auditStatus)
      // 审核未开始（含已提交待审） 或 已驳回 → 标记为不可信参考
      return s <= 0
    },

    async loadCrossPhaseData(dealerCode) {
      this.crossPhaseLoading = true
      try {
        const strictDealerCode = (dealerCode || '').trim()
        if (!strictDealerCode) {
          this.crossPhaseGroups = []
          return
        }

        const { getCrossPhaseData } = await import('index/api/project/form')
        const res = await getCrossPhaseData(strictDealerCode, this.formKey)
        const data = res.data?.data || res.data || {}
        this.crossPhaseGroups = (data.phases || []).map(phase => ({
          phaseNo: phase.phaseNo,
          phaseName: phase.phaseName,
          submitTime: phase.submitTime,
          // 42 号方案 §9.3：保留审核状态用于渲染状态徽章，提示参考资料的可信度
          auditStatus: typeof phase.auditStatus === 'number' ? phase.auditStatus : 0,
          answerStatus: typeof phase.answerStatus === 'number' ? phase.answerStatus : 0,
          items: (phase.items || []).filter(item => {
            // 过滤分页项
            return !this.isPaginationItem(item.itemType)
          }).map(item => ({
            itemKey: item.itemKey,
            itemType: item.itemType,
            label: item.label,
            serialNo: item.serialNo,
            options: item.options || [],
            answer: item.answer // { value, otherText } 或 null
          }))
        }))
      } catch (e) {
        console.warn('[ExamGrading] Load cross-phase data failed:', e)
        this.crossPhaseGroups = []
      }
      this.crossPhaseLoading = false
      // 渲染参考资料中的廉政协议 PDF
      this.$nextTick(() => this.renderCrossPhasePdfs())
    },
    
    // 获取跨阶段题目的答案值（优先取审核答案，其次取原始答案）
    getCrossPhaseAnswerValue(item) {
      if (!item.answer) return null
      return item.answer.reviewAnswer1 || item.answer.value || null
    },



    // 将“同店跨阶段答案”按回圈关系预填到当前审核答案
    applyCrossPhaseLoopbackDefaults() {
      const normalize = (v) => {
        if (v === null || v === undefined) return ''
        const s = String(v).trim()
        return (!s || s === 'undefined' || s === 'null') ? '' : s
      }

      const parseToValues = (raw) => {
        if (raw === null || raw === undefined) return []
        if (Array.isArray(raw)) return raw.map(v => String(v))
        if (typeof raw === 'string') {
          const s = raw.trim()
          if (!s) return []
          try {
            const parsed = JSON.parse(s)
            if (Array.isArray(parsed)) return parsed.map(v => String(v))
            if (parsed !== null && parsed !== undefined) return [String(parsed)]
          } catch (e) {}
          return [s]
        }
        return [String(raw)]
      }

      const crossPhaseAnswerMap = new Map()
      ;(this.crossPhaseGroups || []).forEach(group => {
        ;(group.items || []).forEach(item => {
          const rawValue = this.getCrossPhaseAnswerValue(item)
          const v = normalize(rawValue)
          if (!v) return

          const keys = [normalize(item.itemKey), normalize(item.serialNo)]
          ;[...new Set(keys.filter(Boolean))].forEach(k => {
            if (!crossPhaseAnswerMap.has(k)) crossPhaseAnswerMap.set(k, rawValue)
          })
        })
      })

      if (crossPhaseAnswerMap.size === 0) return

      ;(this.questionList || []).forEach(targetQ => {
        if (!this.isChoiceQuestion(targetQ?.itemType)) return

        const current = this.reviewAnswerMap[targetQ.itemKey]
        if (normalize(current)) return // 用户已选，不覆盖

        const sourceKey = normalize(targetQ.sourceItemKey)
        if (!sourceKey) return

        // 【防御】sourceItemKey 不能等于自身（编辑器误配数据防护）
        if (sourceKey === normalize(targetQ.itemKey)) return

        // 【防御】必须有 sourceFormKey 且指向其他问卷才是有效回圈配置
        const sourceFormKey = normalize(targetQ.sourceFormKey)
        if (!sourceFormKey) return

        // 【BUG修复】多级审核二审/三审：上一级对该题给出 NO_SCORE 时，不从跨阶段原答案回圈
        // 保证"得分才带到下一级"的规则不被跨阶段回圈覆盖
        if (!this.isSimplifiedAudit && this.reviewLevel > 1) {
          const prevResult = this.reviewLevel === 2
            ? this.reviewResult1Map[targetQ.itemKey]
            : this.reviewResult2Map[targetQ.itemKey]
          if (prevResult === 'NO_SCORE' || prevResult === 'NOT_APPLICABLE') return
        }

        const sourceRaw = crossPhaseAnswerMap.get(sourceKey)
        if (sourceRaw === null || sourceRaw === undefined) return

        let values = parseToValues(sourceRaw)
        if (values.length === 0) return

        // 应用 optionMapping
        const mapping = this.normalizeOptionMapping(targetQ.optionMapping)
        if (mapping) {
          values = values.map(v => {
            const key = String(v)
            return Object.prototype.hasOwnProperty.call(mapping, key) ? String(mapping[key]) : key
          })
        }

        // 仅保留目标题存在的选项值
        const targetOptionValues = new Set(((targetQ.options || []).map(o => String(o.value))))
        if (targetOptionValues.size > 0) {
          values = values.filter(v => targetOptionValues.has(String(v)))
        }

        values = [...new Set(values)]
        if (values.length === 0) return

        const finalAnswer = this.isMultipleChoice(targetQ.itemType)
          ? JSON.stringify(values)
          : JSON.stringify([values[0]])

        this.$set(this.reviewAnswerMap, targetQ.itemKey, finalAnswer)
        this.updateReviewResultFromAnswer(targetQ, finalAnswer)
      })
    },

    // 反向回圈：其他阶段的题目配置了 sourceItemKey 引用当前阶段的题目
    // 时，从其他阶段拉取答案预填到当前题目
    // 场景：Phase 3 的 S1 配置了 sourceItemKey → Phase 4 的 S1
    //       Phase 3 先做了，Phase 4 后做时应自动拿 Phase 3 的答案
    applyReverseCrossPhaseLoopback() {
      const normalize = (v) => {
        if (v === null || v === undefined) return ''
        const s = String(v).trim()
        return (!s || s === 'undefined' || s === 'null') ? '' : s
      }

      // 当前问卷的所有题目 itemKey 集合
      const currentItemKeys = new Set((this.questionList || []).map(q => normalize(q.itemKey)).filter(Boolean))
      if (currentItemKeys.size === 0) return

      // 遍历跨阶段题目，查找哪些题目的 sourceItemKey 指向当前问卷
      const reverseMatches = [] // { sourceItem, targetItemKey }
      ;(this.crossPhaseGroups || []).forEach(group => {
        ;(group.items || []).forEach(crossItem => {
          try {
            const cfg = JSON.parse(crossItem.configJson || '{}')
            const innerCfg = cfg.__config__ || cfg
            const srcItemKey = normalize(innerCfg.sourceItemKey)
            if (!srcItemKey) return
            // sourceItemKey 指向当前问卷的某个题目 → 反向引用
            if (currentItemKeys.has(srcItemKey)) {
              reverseMatches.push({
                crossItem,       // 其他阶段的题目（有答案的一方）
                targetItemKey: srcItemKey  // 当前问卷被引用的题目
              })
            }
          } catch (e) {}
        })
      })

      if (reverseMatches.length === 0) return

      reverseMatches.forEach(({ crossItem, targetItemKey }) => {
        // 当前题目已有审核答案，不覆盖
        const current = normalize(this.reviewAnswerMap[targetItemKey])
        if (current) return

        // 取跨阶段题目的答案（优先审核答案 → 原始答案）
        const rawAnswer = this.getCrossPhaseAnswerValue(crossItem)
        if (!normalize(rawAnswer)) return

        // 找到当前问卷的目标题目
        const targetQ = this.questionList.find(q => normalize(q.itemKey) === targetItemKey)
        if (!targetQ || !this.isChoiceQuestion(targetQ.itemType)) return

        // 解析答案值
        let values = []
        try {
          const parsed = JSON.parse(rawAnswer)
          values = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)]
        } catch {
          values = [String(rawAnswer)]
        }

        // 仅保留目标题存在的选项值
        const targetOptionValues = new Set((targetQ.options || []).map(o => String(o.value)))
        if (targetOptionValues.size > 0) {
          values = values.filter(v => targetOptionValues.has(String(v)))
        }
        values = [...new Set(values)]
        if (values.length === 0) return

        const finalAnswer = this.isMultipleChoice(targetQ.itemType)
          ? JSON.stringify(values)
          : JSON.stringify([values[0]])

        // 预填审核答案
        this.$set(this.reviewAnswerMap, targetItemKey, finalAnswer)

        // 自动设置审核结果
        const crossAnswer = crossItem.answer || {}
        const crossReviewResult = normalize(crossAnswer.reviewResult1)

        if (targetQ.enableScoring) {
          // 开启评分：根据跨阶段审核结果自动设置 SCORE/NO_SCORE
          if (crossReviewResult === 'SCORE' || crossReviewResult === 'NO_SCORE') {
            this.$set(this.reviewMap, targetItemKey, crossReviewResult)
          } else {
            // 跨阶段未审核，根据答案自动判断得分
            this.updateReviewResultFromAnswer(targetQ, finalAnswer)
          }
        } else {
          // 未开启评分：只回圈选项，自动设为通过
          this.$set(this.reviewMap, targetItemKey, 'SCORE')
        }
      })
    },
    // 解析廉政协议答案 JSON（跨阶段参考资料用）
    parseAgreementAnswer(val) {
      return _parseAgreement(val)
    },
    // 渲染参考资料中的廉政协议 PDF
    async renderCrossPhasePdfs() {
      for (const group of this.crossPhaseGroups) {
        for (const item of group.items) {
          if (item.itemType !== 'INTEGRITY_AGREEMENT') continue
          const val = this.getCrossPhaseAnswerValue(item)
          const { agreementUrl } = _parseAgreement(val)
          if (!agreementUrl) continue
          
          const stateKey = group.phaseNo + '_' + item.itemKey
          const refKey = 'refPdf_' + stateKey
          const container = this.$refs[refKey]?.[0] || this.$refs[refKey]
          if (!container) continue
          
          await renderPdfToContainer(agreementUrl, container, state => {
            this.$set(this.refPdfStates, stateKey, state)
          })
        }
      }
    },
    
    // 打开悬浮媒体播放器
    openFloatingMedia(type, src, title) {
      this.floatingMedia = { visible: true, type, src, title: title || '媒体预览', startTime: 0 }
    },
    // 打开悬浮音频播放器（联动主播放器）
    openFloatingAudio(question) {
      let startTime = 0
      // 直接从 DOM 查找主播放器的 audio 元素
      const audioEl = this.$el.querySelector('.audio-answer audio')
      if (audioEl) {
        startTime = audioEl.currentTime || 0
        audioEl.pause()
      }
      this.floatingMedia = {
        visible: true,
        type: 'audio',
        src: this.getAudioUrl(question.itemKey),
        title: this.stripHtml(question.label),
        startTime
      }
    },
    
    // 关闭悬浮媒体播放器
    closeFloatingMedia() {
      this.floatingMedia.visible = false
    },
    
    // 判断答案值是否包含文件URL
    parseRefFileList(value) {
      if (!value) return []
      const trimmed = value.trim()
      // JSON 数组格式
      if (trimmed.startsWith('[')) {
        try {
          const files = JSON.parse(trimmed)
          if (!Array.isArray(files)) return []
          return files.map(f => {
            if (typeof f === 'string') return { url: f, name: f.split('/').pop() }
            return {
              url: f.url || f.Url || f.fileUrl || f.FileUrl || '',
              name: f.name || f.Name || ''
            }
          }).filter(f => !!f.url)
        } catch { return [] }
      }
      // 纯 URL
      if (trimmed.startsWith('http')) {
        return [{ url: trimmed, name: trimmed.split('/').pop() }]
      }
      return []
    },
    
    // 判断跨阶段题目是选择题
    isCrossPhaseChoiceQuestion(itemType) {
      return ['RADIO', 'CHECKBOX', 'SELECT', 'MULTI_SELECT'].includes(itemType)
    },
    
    // 判断跨阶段题目某选项是否被选中
    isCrossPhaseOptionSelected(item, opt) {
      const val = this.getCrossPhaseAnswerValue(item)
      if (!val) return false
      const str = String(val).trim()
      // JSON 数组格式
      if (str.startsWith('[')) {
        try {
          const arr = JSON.parse(str)
          return Array.isArray(arr) && arr.map(String).includes(String(opt.value))
        } catch { return false }
      }
      return String(val) === String(opt.value)
    },
    
    // 解析廉政协议答案（主审核区用）
    parseAgreementValue(itemKey) {
      return _parseAgreement(this.answerMap[itemKey])
    },
    
    // 渲染审核页廉政协议 PDF
    async renderGradingPdfs() {
      for (const q of this.filteredQuestionList) {
        if (q.itemType !== 'INTEGRITY_AGREEMENT') continue
        const { agreementUrl } = this.parseAgreementValue(q.itemKey)
        if (!agreementUrl) continue
        
        const refKey = 'gradingPdf_' + q.itemKey
        const container = this.$refs[refKey]?.[0] || this.$refs[refKey]
        if (!container) continue
        
        await renderPdfToContainer(agreementUrl, container, state => {
          this.$set(this.gradingPdfStates, q.itemKey, state)
        })
      }
    },
    
    // 检查当前用户是否有权限访问指定审核级别
    canAccessLevel(level) {
      if (this.isAdminUser) return true
      return this.allowedReviewLevels.includes(level)
    },
    handleReviewLevelChange() {
      // 切换审核级别时重新加载当前试卷
      if (this.currentExaminee) {
        this.selectExaminee(this.currentExaminee)
      }
    },
    
    prevExaminee() {
      if (this.currentIndex > 0) {
        this.selectExaminee(this.filteredExamineeList[this.currentIndex - 1])
      }
    },
    
    nextExaminee() {
      if (this.currentIndex < this.filteredExamineeList.length - 1) {
        this.selectExaminee(this.filteredExamineeList[this.currentIndex + 1])
      }
    },
    
    async handleSaveAndBack() {
      this.$emit('back')
    },
    
    // 评审结果变更处理
    // 判断该题审核结果是否锁定（驳回重提交时，上一轮已通过的题目不可修改，仅审核重新提交的题目）
    isReviewLocked(question) {
      if (!this.currentExaminee) return false
      // 考试模式（第三阶段）使用三级审核，不存在驳回机制，不锁定
      if (this.examForm === 1 || this.phase === 3 || this.phase === 0) return false
      // 简化审核模式（阶段1/2/4）：只在驳回重提交场景锁定（auditStatus=0 且有继承的审核结果）
      const auditStatus = this.currentExaminee.auditStatus
      if (auditStatus !== 0) return false
      // 该题有从上一轮继承的审核结果 → 锁定（说明该题之前已通过，无需重新审核）
      return this.preloadedReviewKeys.has(question.itemKey)
    },

    handleReviewChange(question, value) {
      const itemKey = question.itemKey
      const currentResult = this.reviewMap[itemKey]

      // 简化审核模式：直接设置，不做得分校验
      if (this.isSimplifiedAudit) {
        this.$set(this.reviewMap, itemKey, value)
        this._onReviewResultSettled(itemKey, value)
        return
      }

      // 考试审核模式：得分校验逻辑
      if (currentResult === 'SCORE' && value === 'NOT_APPLICABLE') {
        this.$message.warning('得分选项不能修改为不涉及')
        this.$nextTick(() => {
          this.$set(this.reviewMap, itemKey, 'SCORE')
        })
        return
      }

      if (value === 'SCORE' && !this.canSelectScore(question)) {
        this.$message.warning('只有选中正确答案才能选择得分')
        this.$nextTick(() => {
          this.$set(this.reviewMap, itemKey, currentResult || 'NO_SCORE')
        })
        return
      }

      this.$set(this.reviewMap, itemKey, value)
      this._onReviewResultSettled(itemKey, value)
    },
    /** 判定结果落地后的联动钩子：若改判得分（SCORE），清空该题证据勾选，因为得分无需失分证据支撑 */
    _onReviewResultSettled(itemKey, value) {
      if (value !== 'SCORE') return
      const lvl = this._effectiveReviewLevel()
      const bucket = this.evidenceData[lvl] && this.evidenceData[lvl][itemKey]
      if (bucket && Array.isArray(bucket.selectedEvidenceUrls) && bucket.selectedEvidenceUrls.length > 0) {
        this.$set(bucket, 'selectedEvidenceUrls', [])
      }
    },
    
    // 判断是否可以选择"得分"
    canSelectScore(question) {
      // 检查是否所有选项都有分值设置（perOption模式）
      if (this.hasAllOptionsWithScore(question)) {
        // 需要检查当前选中选项的得分是否达到满分
        const reviewAnswer = this.reviewAnswerMap[question.itemKey]
        if (!reviewAnswer) return false
        
        const fullScore = question.maxScore || 0
        if (fullScore <= 0) return true
        
        try {
          let selectedValues = JSON.parse(reviewAnswer)
          if (!Array.isArray(selectedValues)) selectedValues = [selectedValues]
          
          let positiveSum = 0
          let negativeSum = 0
          selectedValues.forEach(val => {
            const opt = question.options.find(o => String(o.value) === String(val))
            if (opt && opt.score) {
              if (opt.score > 0) positiveSum += opt.score
              else negativeSum += opt.score
            }
          })
          
          let baseScore = 0
          if (positiveSum === 0 && negativeSum < 0) baseScore = fullScore
          let calculated = baseScore + positiveSum
          if (calculated > fullScore) calculated = fullScore
          let earnedScore = calculated + negativeSum
          if (earnedScore < 0) earnedScore = 0
          
          return earnedScore >= fullScore
        } catch {
          return false
        }
      }
      
      // 否则需要判断当前审核答案是否正确
      const reviewAnswer = this.reviewAnswerMap[question.itemKey]
      if (!reviewAnswer) return false
      
      if (!question.correctAnswer) {
        return true  // 没有正确答案的题目，默认可以得分
      }
      
      return this.checkAnswerCorrect(reviewAnswer, question.correctAnswer, question.scoringMethod)
    },

    // 判断是否可以选择"不涉及"
    canSelectNotApplicable(question) {
      const currentResult = this.reviewMap[question.itemKey]
      // 如果当前是得分状态，不允许改为不涉及
      if (currentResult === 'SCORE') {
        return false
      }
      // 不得分状态可以改为不涉及
      return true
    },
    
    // 判断是否使用"每个选项可自定义分值"模式
    hasAllOptionsWithScore(question) {
      // 首先检查 scoringMethod 配置
      if (question.scoringMethod === 'perOption') {
        return true
      }
      
      if (!question.options || question.options.length === 0) {
        return false
      }
      
      const hasAnyScore = question.options.some(opt => opt.score !== undefined && opt.score !== null && opt.score > 0)
      const allScoresDefined = question.options.every(opt => opt.score !== undefined && opt.score !== null)
      
      const result = hasAnyScore && allScoresDefined
      return result
    },
    
    // 判断某一级别是否可编辑
    canEditLevel(level) {
      if (!this.currentExaminee) return true  // 没选择试卷时都可点击
      const status = this.currentExaminee.auditStatus || 0
      // 允许编辑当前状态及之后的级别（例如：一审完成时，可以修改一审；只有进入二审后才不能修改一审）
      return level >= status
    },

    // 打开推翻一票否决弹框（弹窗输入理由）
    openVetoOverrideDialog() {
      if (!this.currentExaminee) return
      this.$prompt('请填写推翻一票否决的理由（将记入审计日志）', '推翻一票否决（客户申诉）', {
        confirmButtonText: '确认推翻',
        cancelButtonText: '取消',
        inputType: 'textarea',
        inputPlaceholder: '例如：客户申诉该项描述不清，经复核录音无违规话术',
        inputValidator: (val) => {
          const v = String(val || '').trim()
          if (v.length < 5) return '理由至少 5 个字符'
          return true
        }
      }).then(({ value }) => {
        this.pendingVetoOverride = { enabled: true, reason: String(value || '').trim() }
        this.$message.success('已暂存推翻动作，提交审核后生效')
      }).catch(() => { /* 取消 */ })
    },

    // HTML 标签剥离（veto Banner 展示用）
    stripHtml(html) {
      if (!html) return ''
      const tmp = document.createElement('div')
      tmp.innerHTML = String(html)
      return (tmp.textContent || tmp.innerText || '').trim()
    },

    // 提交当前级别的审核
    async handleSubmitReview() {
      if (!this.currentExaminee) {
        this.$message.warning('请先选择试卷')
        return
      }
      
      if (!this.canEditCurrentLevel) {
        this.$message.warning('当前级别已完成审核，不可修改')
        return
      }
      
      // 检查是否所有题目都已评审（排除未开启评分的题目，简化审核模式下全部检查）
      // 签字题、分割线、图片展示等非内容题型不需要审核
      // 驳回重审场景：锁定的题目已有上一轮的结果，不需要重新审核
      const NON_REVIEW_TYPES = ['SIGNATURE', 'DIVIDER', 'IMG_DISPLAY', 'IMAGE_DISPLAY', 'CAROUSEL', 'IMAGE_CAROUSEL']
      const shouldReview = q => !NON_REVIEW_TYPES.includes(q.itemType) && !this.isRatingQuestion(q.itemType) && (q.enableScoring || this.isSimplifiedAudit) && !this.isReviewLocked(q)
      const qTitle = q => q.serialNo ? q.serialNo + '.' + this.stripHtml(q.label || '') : '第' + (this.filteredQuestionList.indexOf(q) + 1) + '题'
      const unreviewed = this.filteredQuestionList.filter(q => shouldReview(q) && !this.reviewMap[q.itemKey])
      if (unreviewed.length > 0) {
        this.$message.warning(`还有 ${unreviewed.length} 道题目未评审`)
        return
      }
      
      // 检查不得分题目的失分备注必填
      if (!this.isSimplifiedAudit) {
        for (const q of this.filteredQuestionList) {
          if (!shouldReview(q)) continue
          if (this.reviewMap[q.itemKey] === 'NO_SCORE') {
            const remark = this.remarkMap[q.itemKey]
            if (!remark || !remark.trim()) {
              const idx = this.filteredQuestionList.indexOf(q)
              this.$message.warning(`${qTitle(q)} 为不得分，请填写失分备注`)
              this.scrollToQuestion(idx)
              return
            }
          }
        }
      }
      
      // 检查选择题是否已勾选答案（仅考试审核模式，简化审核不需要重新选择）
      if (!this.isSimplifiedAudit) {
        for (const q of this.filteredQuestionList) {
          if (!shouldReview(q)) continue
          if (!this.isChoiceQuestion(q.itemType)) continue
          const answer = this.reviewAnswerMap[q.itemKey]
          if (!answer || (Array.isArray(answer) && answer.length === 0)) {
            const idx = this.filteredQuestionList.indexOf(q)
            this.$message.warning(`${qTitle(q)} 未勾选选项，请选择答案`)
            this.scrollToQuestion(idx)
            return
          }
        }
      }
      
      // 检查明检文本题是否已填写审核答案（仅考试审核模式）
      if (!this.isSimplifiedAudit) {
        for (const q of this.filteredQuestionList) {
          if (!shouldReview(q)) continue
          if (q.itemType !== 'MINGJIAN_PHOTO_TEXT') continue
          const answer = this.reviewAnswerMap[q.itemKey]
          if (!answer || !String(answer).trim()) {
            const idx = this.filteredQuestionList.indexOf(q)
            this.$message.warning(`${qTitle(q)} 未填写审核答案`)
            this.scrollToQuestion(idx)
            return
          }
        }
      }
      
      this.submitLoading = true
      try {
        // 构建审核结果（按当前可审核题目全集构建，避免单题答案漏传）
        const NON_REVIEW_TYPES = ['SIGNATURE', 'DIVIDER', 'IMG_DISPLAY', 'IMAGE_DISPLAY', 'CAROUSEL', 'IMAGE_CAROUSEL']
        const shouldReview = q => !NON_REVIEW_TYPES.includes(q.itemType) && !this.isRatingQuestion(q.itemType) && (q.enableScoring || this.isSimplifiedAudit) && !this.isReviewLocked(q)

        const results = (this.filteredQuestionList || [])
          .filter(q => shouldReview(q))
          .map(q => {
            const itemKey = q.itemKey
            const relationIds = Array.isArray(q.relationIds) ? q.relationIds.filter(Boolean) : []

            const rawAnswer = this.reviewAnswerMap[itemKey]
            let normalizedAnswer = ''
            if (rawAnswer !== null && rawAnswer !== undefined) {
              if (typeof rawAnswer === 'string') {
                normalizedAnswer = rawAnswer
              } else if (Array.isArray(rawAnswer) || typeof rawAnswer === 'object') {
                try {
                  normalizedAnswer = JSON.stringify(rawAnswer)
                } catch {
                  normalizedAnswer = String(rawAnswer)
                }
              } else {
                normalizedAnswer = String(rawAnswer)
              }
            }


            return {
              ItemKey: itemKey,
              Result: this.reviewMap[itemKey] || '',
              Answer: normalizedAnswer,  // 发送修改后的答案（统一字符串化）
              Remark: this.remarkMap[itemKey] || '',
              SpecialRemark: this.reviewSpecialRemarkMap[itemKey] || '',
              EvidenceData: this.serializeEvidenceData(itemKey), // 明检-文件题：勾选+追加，按当前级别
              RelationIds: relationIds
            }
          })
        
        const { submitReview } = await import('index/api/project/form')
        const submitPayload = {
          AnswerKey: this.currentExaminee.answerKey,
          // 简化审核（阶段1/2/4）统一按一审提交，避免写到review_answer_2/3导致刷新不回显
          ReviewLevel: this.isSimplifiedAudit ? 1 : this.reviewLevel,
          Reviewer: 'Admin', // TODO: 从用户信息获取
          Phase: this.phase || 0,
          Results: results
        }
        // 一票否决推翻：本次审核包含推翻动作时带上 VetoOverride
        if (this.pendingVetoOverride && this.pendingVetoOverride.enabled) {
          submitPayload.VetoOverride = {
            Enabled: true,
            Reason: this.pendingVetoOverride.reason
          }
        }

        // 42 号方案 D.1：修改模式下走 modify 接口（先快照后更新）
        let reviewRes
        if (this.modifyMode && this.modifyMode.active
            && this.modifyMode.level === submitPayload.ReviewLevel) {
          const { modifyReview } = await import('index/api/project/form')
          submitPayload.ModifyReason = this.modifyMode.reason
          reviewRes = await modifyReview(submitPayload)
          // 修改模式不会改变 audit_status，后续状态更新沿用原值
          // 清除修改模式标记
          this.modifyMode = { active: false, level: 0, reason: '' }
          this.$message.success(`${['', '一', '二', '三'][submitPayload.ReviewLevel]} 审修改已保存，历史版本已记录`)
          // 修改模式也清草稿 + 清 veto pending，但不自动切级别/下一份
          this.clearDraft()
          this.pendingVetoOverride = { enabled: false, reason: '' }
          // 清除该考生的缓存并重新加载，避免本地状态与后端不一致
          if (this.answerCache[this.currentExaminee.answerKey]) {
            delete this.answerCache[this.currentExaminee.answerKey].reviewDetail
          }
          this.selectExaminee(this.currentExaminee)
          return
        }

        reviewRes = await submitReview(submitPayload)
        
        // 更新当前试卷的审核状态（使用后端返回的实际状态）
        const actualStatus = reviewRes?.data?.data?.auditStatus ?? reviewRes?.data?.auditStatus ?? this.reviewLevel
        this.currentExaminee.auditStatus = actualStatus
        // 同时更新列表中的状态（确保列表显示正确）
        const idx = this.examineeList.findIndex(e => e.answerKey === this.currentExaminee.answerKey)
        if (idx >= 0) {
          this.$set(this.examineeList[idx], 'auditStatus', actualStatus)
        }
        
        // 清除该考生的缓存，确保下次加载时获取最新的审核详情
        if (this.answerCache[this.currentExaminee.answerKey]) {
          delete this.answerCache[this.currentExaminee.answerKey].reviewDetail
        }
        
        // 提交成功后清除草稿
        this.clearDraft()
        // 推翻动作消费完毕，本地界面同步为已推翻态
        if (this.pendingVetoOverride && this.pendingVetoOverride.enabled && this.currentExaminee) {
          this.$set(this.currentExaminee, 'vetoOverridden', true)
          this.$set(this.currentExaminee, 'vetoOverrideReason', this.pendingVetoOverride.reason)
        }
        this.pendingVetoOverride = { enabled: false, reason: '' }
        this.$message.success(this.phase > 0 ? '审核提交成功' : `${this.reviewLevelText}审提交成功`)
        
        if (!this.isSimplifiedAudit && this.reviewLevel < 3) {
          // 考试模式（Phase=0 或 Phase=3）：自动切换到下一审核级别
          const nextLevel = this.reviewLevel + 1
          if (this.canAccessLevel(nextLevel)) {
            this.reviewLevel = nextLevel
            this.handleReviewLevelChange()
          } else {
            // 无下一级权限，跳到下一份
            if (this.currentIndex < this.filteredExamineeList.length - 1) {
              this.nextExaminee()
            }
          }
        } else {
          // 简化审核模式 或 已到三审：跳到下一份
          if (this.currentIndex < this.filteredExamineeList.length - 1) {
            this.nextExaminee()
          }
        }
      } catch (e) {
        console.error('[ExamGrading] Submit review failed:', e)
        const msg = e?.data?.message || e?.response?.data?.message || e?.message || '提交失败'
        this.$message.error(msg)
      } finally {
        this.submitLoading = false
      }
    },
    
    // ===== 草稿功能 =====
    getDraftKey(examinee) {
      if (!examinee) return null
      return `review_draft_${this.formKey}_${examinee.answerKey}_${this.reviewLevel}`
    },
    
    hasDraft(examinee) {
      const key = `review_draft_${this.formKey}_${examinee.answerKey}`
      // 检查任意审核级别是否有草稿
      for (let l = 1; l <= 3; l++) {
        if (localStorage.getItem(key + '_' + l)) return true
      }
      return false
    },
    
    saveDraft() {
      if (!this.currentExaminee) return
      const key = this.getDraftKey(this.currentExaminee)
      if (!key) return
      const draft = {
        reviewMap: { ...this.reviewMap },
        remarkMap: { ...this.remarkMap },
        reviewAnswerMap: { ...this.reviewAnswerMap },
        reviewSpecialRemarkMap: { ...this.reviewSpecialRemarkMap },
        savedAt: new Date().toISOString()
      }
      try {
        localStorage.setItem(key, JSON.stringify(draft))
        this.$message.success('草稿已保存')
      } catch (e) {
        console.error('[ExamGrading] Save draft failed:', e)
        this.$message.error('保存草稿失败')
      }
    },
    
    restoreDraft() {
      if (!this.currentExaminee) return
      const key = this.getDraftKey(this.currentExaminee)
      if (!key) return
      const raw = localStorage.getItem(key)
      if (!raw) return
      try {
        const draft = JSON.parse(raw)
        // 恢复审核结果
        if (draft.reviewMap) {
          Object.entries(draft.reviewMap).forEach(([k, v]) => {
            this.$set(this.reviewMap, k, v)
          })
        }
        if (draft.remarkMap) {
          Object.entries(draft.remarkMap).forEach(([k, v]) => {
            this.$set(this.remarkMap, k, v)
          })
        }
        if (draft.reviewAnswerMap) {
          Object.entries(draft.reviewAnswerMap).forEach(([k, v]) => {
            this.$set(this.reviewAnswerMap, k, v)
          })
        }
        if (draft.reviewSpecialRemarkMap) {
          Object.entries(draft.reviewSpecialRemarkMap).forEach(([k, v]) => {
            this.$set(this.reviewSpecialRemarkMap, k, v)
          })
        }
        const savedAt = draft.savedAt ? new Date(draft.savedAt).toLocaleString() : ''
        this.$message.info(`已恢复草稿${savedAt ? '（' + savedAt + '）' : ''}`)
      } catch (e) {
        console.warn('[ExamGrading] Restore draft failed:', e)
      }
    },
    
    clearDraft() {
      if (!this.currentExaminee) return
      const key = this.getDraftKey(this.currentExaminee)
      if (key) localStorage.removeItem(key)
    },
    
    getStatusClass(examinee) {
      const status = examinee.auditStatus || 0
      if (status === -1) return 'status-rejected'
      // 简化审核模式（阶1/2/4）：已通过直接绿色
      if (this.isSimplifiedAudit) {
        return status >= 1 ? 'status-complete' : 'status-pending'
      }
      if (status >= 3) return 'status-complete'
      if (status >= 2) return 'status-review2'
      if (status >= 1) return 'status-review1'
      return 'status-pending'
    },
    
    getStatusText(examinee) {
      const status = examinee.auditStatus || 0
      if (status === -1) return '已驳回'
      // 简化审核模式（阶1/2/4）：只有通过/不通过
      if (this.isSimplifiedAudit) {
        return status >= 1 ? '已通过' : '待审核'
      }
      if (status >= 3) return '三审完成'
      if (status >= 2) return '二审完成'
      if (status >= 1) return '一审完成'
      return '待审核'
    },
    
    isPaginationItem(type) {
      return ['PAGINATION', 'PAGE_BREAK', 'DIVIDER', 'DESCRIPTION'].includes(type)
    },
    
    getTypeName(type) {
      const map = {
        // 单选题
        'RADIO': '单选题',
        'SINGLE_CHOICE': '单选题',
        // 多选题
        'CHECKBOX': '多选题',
        'MULTIPLE_CHOICE': '多选题',
        // 文本输入类
        'TEXT': '文本',
        'INPUT': '单行文本',
        'TEXTAREA': '多行文本',
        'NUMBER': '数字',
        'EMAIL': '邮箱',
        'PHONE': '电话',
        'MOBILE_INPUT': '手机号',
        'ID_CARD': '身份证号',
        'FILL_BLANK': '填空题',
        // 评分类
        'RATING': '评分',
        'RATE': '评分',
        'SLIDER': '滑块',
        'NPS': 'NPS评分',
        // 日期时间类
        'DATE': '日期',
        'DATETIME': '日期时间',
        'DATE_PICKER': '日期选择',
        'DATE_RANGE': '日期范围',
        'DATERANGE': '日期范围',
        'TIME': '时间',
        'TIME_PICKER': '时间选择',
        // 下拉选择类
        'SELECT': '下拉选择',
        'MULTI_SELECT': '多选下拉',
        'CASCADER': '级联选择',
        'CASCADER_MULTI': '级联多选',
        // 图片选择类
        'IMG_RADIO': '图片单选',
        'IMG_CHECKBOX': '图片多选',
        'IMAGE_SELECT': '图片选择',
        'IMAGE_CHOICE': '图片选择',
        // 排序题
        'SORT': '排序题',
        // 文件上传类
        'FILE': '文件上传',
        'IMAGE': '图片上传',
        'FILE_UPLOAD': '文件上传',
        'IMG_UPLOAD': '图片上传',
        // 矩阵题
        'MATRIX_RADIO': '矩阵单选',
        'MATRIX_CHECKBOX': '矩阵多选',
        'MATRIX_SCALE': '矩阵量表',
        'MATRIX_DROPDOWN': '矩阵下拉',
        'MATRIX_SELECT': '矩阵选择',
        // 横向填空
        'HORIZONTAL_INPUT': '横向填空',
        // 检查项
        'CHECKLIST': '检查项',
        // 签名定位类
        'SIGNATURE': '签名',
        'ADDRESS': '地址',
        'LOCATION': '定位',
        'REGION': '省市区',
        // 录音录像类
        'AUDIO': '录音',
        'AUDIO_RECORD': '后台录音',
        'AUDIO_RECORDING': '后台录音',
        'VIDEO': '录像',
        'VIDEO_RECORD': '视频录制',
        // 预约类
        'RESERVE_DATE': '预约日期',
        'RESERVE_TIME': '预约时段',
        // 展示类（辅助题型）
        'DIVIDER': '分割线',
        'IMAGE_DISPLAY': '图片展示',
        'IMG_DISPLAY': '图片展示',
        'CAROUSEL': '图片轮播',
        'IMAGE_CAROUSEL': '图片轮播',
        'EXTERNAL_LINK': '外部链接',
        'INTEGRITY_AGREEMENT': '廉政协议',
        'PAGINATION': '分页',
        // 明检分类题型
        'MINGJIAN_PHOTO_RADIO': '明检-拍照单选',
        'MINGJIAN_PHOTO_CHECKBOX': '明检-拍照多选',
        'MINGJIAN_PHOTO_TEXT': '明检-拍照文本',
        'MINGJIAN_FILE_RADIO': '明检-文件单选',
        'MINGJIAN_FILE_CHECKBOX': '明检-文件多选'
      }
      return map[type] || type
    },
    
    isChoiceQuestion(type) {
      return ['RADIO', 'CHECKBOX', 'SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'SELECT', 'MINGJIAN_PHOTO_RADIO', 'MINGJIAN_PHOTO_CHECKBOX', 'MINGJIAN_FILE_RADIO', 'MINGJIAN_FILE_CHECKBOX'].includes(type)
    },

    // 是否是多选题
    isMultipleChoice(type) {
      return ['CHECKBOX', 'MULTIPLE_CHOICE', 'MINGJIAN_PHOTO_CHECKBOX', 'MINGJIAN_FILE_CHECKBOX'].includes(type)
    },

    // 是否是明检-拍照题
    isMingjianPhotoQuestion(type) {
      return ['MINGJIAN_PHOTO_RADIO', 'MINGJIAN_PHOTO_CHECKBOX', 'MINGJIAN_PHOTO_TEXT'].includes(type)
    },
    // 是否是明检-文件题
    isMingjianFileQuestion(type) {
      return ['MINGJIAN_FILE_RADIO', 'MINGJIAN_FILE_CHECKBOX'].includes(type)
    },
    // 统一入口
    isMingjianQuestion(type) {
      return this.isMingjianPhotoQuestion(type) || this.isMingjianFileQuestion(type)
    },
    // 获取明检分类题的拍照图片
    getMingjianPhotos(itemKey) {
      const val = this.answerMap[itemKey]
      if (!val) return []
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        return parsed.photos || []
      } catch { return [] }
    },
    // 获取明检-文件题的附件（兼容旧 file 单对象 / 新 files 数组）
    getMingjianFiles(itemKey) {
      const val = this.answerMap[itemKey]
      if (!val) return []
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        if (Array.isArray(parsed.files)) return parsed.files
        if (parsed.file) return [parsed.file]
        return []
      } catch { return [] }
    },
    // 图片类附件（用于缩略图预览）
    mingjianImageFiles(itemKey) {
      return this.getMingjianFiles(itemKey).filter(f => this.isImageFile(f))
    },
    // 非图片类附件（用于文件行展示）
    mingjianNonImageFiles(itemKey) {
      return this.getMingjianFiles(itemKey).filter(f => !this.isImageFile(f))
    },
    isImageFile(f) {
      if (!f) return false
      const name = (f.name || f.url || '').toLowerCase()
      return /\.(jpg|jpeg|png|gif|bmp|webp)(\?|$)/.test(name)
    },
    // 模板里语义化别名
    isMingjianImageFile(f) { return this.isImageFile(f) },
    // 兼容旧调用（如果其他地方仍在用 getMingjianFile）
    getMingjianFile(itemKey) {
      const arr = this.getMingjianFiles(itemKey)
      return arr.length > 0 ? arr[0] : null
    },

    // ============ 明检-文件题：审核员证据（按当前 reviewLevel 读写） ============
    /** 去掉 OSS 签名参数后的纯 URL，作为 key 比较，避免签名变化导致失配 */
    _stripSign(url) {
      if (!url) return ''
      const i = url.indexOf('?')
      return i >= 0 ? url.substring(0, i) : url
    },
    /** 统一的有效审核级别：简化模式下当作 1，与提交时 ReviewLevel 对齐 */
    _effectiveReviewLevel() {
      return this.isSimplifiedAudit ? 1 : (this.reviewLevel || 1)
    },
    _ensureEvidenceBucket(itemKey) {
      const lvl = this._effectiveReviewLevel()
      if (!this.evidenceData[lvl]) this.$set(this.evidenceData, lvl, {})
      if (!this.evidenceData[lvl][itemKey]) {
        this.$set(this.evidenceData[lvl], itemKey, {
          selectedEvidenceUrls: [],
          appendedFiles: [],
          deletedUrls: [] // 本级软删除的神客原附件 + 上级追加的证据 URL
        })
      } else {
        // 旧数据兼容
        const b = this.evidenceData[lvl][itemKey]
        if (!Array.isArray(b.selectedEvidenceUrls)) this.$set(b, 'selectedEvidenceUrls', [])
        if (!Array.isArray(b.appendedFiles)) this.$set(b, 'appendedFiles', [])
        if (!Array.isArray(b.deletedUrls)) this.$set(b, 'deletedUrls', [])
      }
      return this.evidenceData[lvl][itemKey]
    },
    /** 检查某个证据 URL 是否被任何级别软删（去签名参数后比对）*/
    isDeletedUrl(itemKey, url) {
      if (!url) return false
      const stripped = this._stripSign(url)
      for (let lvl = 1; lvl <= 3; lvl++) {
        const bucket = this.evidenceData[lvl] && this.evidenceData[lvl][itemKey]
        if (!bucket || !Array.isArray(bucket.deletedUrls)) continue
        if (bucket.deletedUrls.some(r => this._stripSign(r.url) === stripped)) return true
      }
      return false
    },
    isEvidenceSelected(itemKey, url) {
      const lvl = this._effectiveReviewLevel()
      const bucket = this.evidenceData[lvl] && this.evidenceData[lvl][itemKey]
      if (!bucket || !Array.isArray(bucket.selectedEvidenceUrls)) return false
      const stripped = this._stripSign(url)
      return bucket.selectedEvidenceUrls.some(u => this._stripSign(u) === stripped)
    },
    toggleEvidence(itemKey, url) {
      const bucket = this._ensureEvidenceBucket(itemKey)
      const stripped = this._stripSign(url)
      const idx = bucket.selectedEvidenceUrls.findIndex(u => this._stripSign(u) === stripped)
      if (idx >= 0) bucket.selectedEvidenceUrls.splice(idx, 1)
      else bucket.selectedEvidenceUrls.push(stripped)
    },
    getAppendedFiles(itemKey) {
      const lvl = this._effectiveReviewLevel()
      const bucket = this.evidenceData[lvl] && this.evidenceData[lvl][itemKey]
      return (bucket && bucket.appendedFiles) || []
    },
    /** 汇总各级别审核员追加的证据（含来源级别），用于当前审核员查看前级追加的证据。
     *  过滤：本条本级已 deleted:true 的 + 被任何级别通过 deletedUrls 软删的。
     *  附带 _sourceLevel（追加时的审核级别）+ _originalIdx（原数组下标）用于删除定位。 */
    getAllVisibleAppendedFiles(itemKey) {
      const currentLvl = this._effectiveReviewLevel()
      const result = []
      for (let lvl = 1; lvl <= currentLvl; lvl++) {
        const bucket = this.evidenceData[lvl] && this.evidenceData[lvl][itemKey]
        if (!bucket || !Array.isArray(bucket.appendedFiles)) continue
        bucket.appendedFiles.forEach((f, idx) => {
          if (!f || f.deleted) return
          if (this.isDeletedUrl(itemKey, f.url)) return // 被跨级软删
          result.push({ ...f, _sourceLevel: lvl, _originalIdx: idx })
        })
      }
      return result
    },
    /** 神客原附件（过滤掉被任何级别软删的）；给卡片带上 _sourceLevel=0 以便统一权限/删除逻辑 */
    getVisibleMingjianFiles(itemKey) {
      return this.getMingjianFiles(itemKey)
        .filter(f => f && !this.isDeletedUrl(itemKey, f.url))
        .map(f => ({ ...f, _sourceLevel: 0 }))
    },
    /** 统一删除权限：能删"来源级别 ≤ 当前级别"的证据
     *  神客原附件：_sourceLevel = 0（一审就能删）
     *  一审追加：_sourceLevel = 1（一/二/三审都能删）
     *  二审追加：_sourceLevel = 2（二/三审能删）
     *  三审追加：_sourceLevel = 3（仅三审能删）*/
    canDeleteEvidence(question, f) {
      if (!this.canEditEvidence(question)) return false
      if (!f) return false
      // 已经被软删的不能再删
      if (f.deleted) return false
      if (this.isDeletedUrl(question.itemKey, f.url)) return false
      const srcLvl = f._sourceLevel != null ? f._sourceLevel : 0
      return this._effectiveReviewLevel() >= srcLvl
    },
    /** 向后兼容旧方法名（追加证据专用） */
    canDeleteAppendedFile(question, f) {
      return this.canDeleteEvidence(question, f)
    },
    /** 级别名：1→一审，2→二审，3→三审 */
    reviewLevelName(lvl) {
      return lvl === 1 ? '一审' : lvl === 2 ? '二审' : lvl === 3 ? '三审' : '审核'
    },
    /** 获取当前登录用户名（用于软删除签名）*/
    _currentReviewerName() {
      const info = this.$store?.getters?.userInfo || {}
      return info.name || info.username || 'Admin'
    },
    _nowStr() {
      const d = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },
    canEditEvidence(question) {
      // 复用现有审核可编辑判定（被引用题且当前级别可改）
      // 文件类明检题没有 isReferenced 这种限制，因此默认让审核员一律可编辑
      return !this.isReadOnlyMode
    },
    beforeAppendUpload(question, file) {
      // 限制单文件 200MB，与答题端默认上限对齐
      const max = 500 * 1024 * 1024
      if (file.size > max) {
        this.$message.error(`文件 ${file.name} 超过 500MB`)
        return false
      }
      return true
    },
    async handleAppendEvidence(itemKey, options) {
      try {
        const { smartUpload } = await import('@/utils/ossUpload')
        const dir = `survey/mingjian-file/review-evidence/${this.currentExaminee?.answerKey || 'tmp'}/${itemKey}`
        const loading = this.$loading({ lock: true, text: `上传中...`, spinner: 'el-icon-loading', background: 'rgba(255,255,255,.6)' })
        const result = await smartUpload(options.file, dir, () => {})
        loading.close()
        const bucket = this._ensureEvidenceBucket(itemKey)
        bucket.appendedFiles.push({
          url: result.url,
          name: result.originalName || options.file.name,
          size: options.file.size,
          type: options.file.type
        })
        this.$message.success('追加证据上传成功')
      } catch (e) {
        this.$message.error('追加证据上传失败：' + (e.message || ''))
      }
    },
    /** 统一软删除入口：按证据来源决定写在哪
     *  本级追加 (_sourceLevel === currentLevel) → 修改 appendedFiles[i].deleted
     *  其他（神客 or 上级追加）→ 在本级 bucket.deletedUrls 追加一条记录
     *  保证"谁删了什么"的审计痕迹永远留在操作人当前级别的字段里，便于追溯 */
    removeEvidence(itemKey, file) {
      if (!file) return
      const currentLvl = this._effectiveReviewLevel()
      const srcLvl = file._sourceLevel != null ? file._sourceLevel : 0
      const now = this._nowStr()
      const by = this._currentReviewerName()

      if (srcLvl > 0 && srcLvl === currentLvl
          && file._originalIdx != null
          && this.evidenceData[srcLvl] && this.evidenceData[srcLvl][itemKey]) {
        // 本级自己追加的 → 原地打 deleted:true
        const target = this.evidenceData[srcLvl][itemKey].appendedFiles[file._originalIdx]
        if (target) {
          this.$set(target, 'deleted', true)
          this.$set(target, 'deletedAt', now)
          this.$set(target, 'deletedBy', by)
        }
        return
      }

      // 神客原附件 or 上级追加 → 写到本级 deletedUrls
      const bucket = this._ensureEvidenceBucket(itemKey)
      if (!Array.isArray(bucket.deletedUrls)) this.$set(bucket, 'deletedUrls', [])
      const stripped = this._stripSign(file.url)
      if (bucket.deletedUrls.some(r => this._stripSign(r.url) === stripped)) return
      bucket.deletedUrls.push({
        url: stripped,
        sourceLevel: srcLvl, // 被删的证据来自哪（0=神客, 1/2=上级追加）
        deletedAt: now,
        deletedBy: by
      })
    },
    /** 向后兼容旧名 */
    removeAppendedFile(itemKey, file) {
      return this.removeEvidence(itemKey, file)
    },
    /** 提交审核时取出当前题的 evidence_data JSON，写入 ItemReviewResult.EvidenceData
     *  注意：简化审核模式下提交统一按一审写入，所以从 evidenceData[1] 取 */
    serializeEvidenceData(itemKey) {
      const lvl = this._effectiveReviewLevel()
      const bucket = this.evidenceData[lvl] && this.evidenceData[lvl][itemKey]
      if (!bucket) return null
      const hasData = (Array.isArray(bucket.selectedEvidenceUrls) && bucket.selectedEvidenceUrls.length > 0)
                    || (Array.isArray(bucket.appendedFiles) && bucket.appendedFiles.length > 0)
      return hasData ? JSON.stringify(bucket) : null
    },
    formatFileSize(bytes) {
      if (!bytes) return ''
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB'
      return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB'
    },
    fileIconClass(f) {
      const name = (f && f.name) || ''
      const ext = name.toLowerCase().split('.').pop() || ''
      if (['jpg','jpeg','png','gif','bmp','webp'].includes(ext)) return 'el-icon-picture'
      if (['mp4','mov','avi','mkv','webm'].includes(ext)) return 'el-icon-video-camera'
      if (['mp3','wav','m4a','aac','flac'].includes(ext)) return 'el-icon-headset'
      if (['zip','rar','7z','tar','gz'].includes(ext)) return 'el-icon-folder'
      if (['pdf','doc','docx'].includes(ext)) return 'el-icon-document'
      if (['xls','xlsx','csv'].includes(ext)) return 'el-icon-tickets'
      if (['ppt','pptx'].includes(ext)) return 'el-icon-film'
      return 'el-icon-paperclip'
    },
    // 获取明检分类-文本题的文字内容
    getMingjianTextValue(itemKey) {
      const val = this.answerMap[itemKey]
      if (!val) return ''
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        return parsed.textValue || ''
      } catch { return String(val) }
    },
    // 获取明检分类答案中的"其他"补充文本
    getMingjianOtherText(itemKey) {
      const val = this.answerMap[itemKey]
      if (!val) return ''
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        return parsed.otherText || ''
      } catch { return '' }
    },
    // 判断明检分类原始答案中某选项是否被选中
    isMingjianOriginalSelected(question, opt) {
      const val = this.answerMap[question.itemKey]
      if (!val) return false
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        if (parsed.selectedValue !== undefined) {
          return String(parsed.selectedValue) === String(opt.value)
        }
        if (Array.isArray(parsed.selectedValues)) {
          return parsed.selectedValues.map(String).includes(String(opt.value))
        }
        return false
      } catch { return false }
    },
    
    // 是否是文件上传题
    isFileUploadQuestion(type) {
      return ['FILE_UPLOAD', 'IMAGE_UPLOAD', 'UPLOAD', 'FILE', 'IMAGE', 'IMG_UPLOAD'].includes(type)
    },
    
    // 是否是录音题
    isAudioRecordQuestion(type) {
      return ['AUDIO_RECORD', 'AUDIO_RECORDING', 'VOICE_RECORD', 'RECORDING'].includes(type)
    },
    
    // 获取录音URL（确保是完整的绝对路径）
    getAudioUrl(itemKey) {
      const answer = this.answerMap[itemKey]
      if (!answer) return ''
      
      let url = answer
      // 如果是JSON对象，取url属性
      if (typeof answer === 'object' && answer.url) {
        url = answer.url
      } else if (typeof answer === 'string') {
        try {
          const parsed = JSON.parse(answer)
          url = parsed.url || answer
        } catch {
          url = answer
        }
      }
      
      // 确保URL是完整的
      if (url && !url.startsWith('http')) {
        if (url.startsWith('//')) {
          url = 'https:' + url
        } else if (url.includes('.aliyuncs.com')) {
          url = 'https://' + url
        }
      }
      
      return url
    },
    
    // 获取录音文件名（用于下载）
    getAudioFilename(question, examinee) {
      const dealerCode = examinee?.dealerCode || '未知店铺'
      const questionIndex = this.filteredQuestionList.findIndex(q => q.itemKey === question.itemKey) + 1
      return `${dealerCode}_第${questionIndex}题录音`
    },
    
    // 判断是否是图片文件
    isImage(url) {
      if (!url) return false
      // 移除查询参数后检查扩展名
      const urlPath = url.split('?')[0]
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(urlPath) || url.includes('/image/')
    },
    
    // 判断是否是视频文件
    isVideo(url) {
      if (!url) return false
      const urlPath = url.split('?')[0]
      return /\.(mp4|webm|mov|avi|mkv|flv|wmv)$/i.test(urlPath) || url.includes('/video/')
    },
    
    // 判断是否是音频文件
    isAudio(url) {
      if (!url) return false
      const urlPath = url.split('?')[0]
      // .webm 录音文件（路径含 recordings）也算音频
      if (/\.webm$/i.test(urlPath) && url.includes('recordings')) return true
      return /\.(mp3|wav|ogg|aac|m4a|flac|wma|webm)$/i.test(urlPath) || url.includes('/audio/')
    },

    // 判断是否是 PDF 文件
    isPdf(url) {
      if (!url) return false
      const urlPath = url.split('?')[0].toLowerCase()
      return urlPath.endsWith('.pdf') || url.includes('/pdf/')
    },
    
    // 获取文件列表（从答案中解析）
    getFileList(itemKey) {
      const answer = this.answerMap[itemKey]
      if (!answer) return []
      
      
      // 确保URL是完整的绝对路径
      const ensureFullUrl = (url) => {
        if (!url) return ''
        const trimmedUrl = String(url).trim()
        
        // 已经是完整URL
        if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
          return trimmedUrl
        }
        // 协议相对URL
        if (trimmedUrl.startsWith('//')) {
          return 'https:' + trimmedUrl
        }
        // OSS域名但缺少协议（如 bucket.oss-cn-xxx.aliyuncs.com/...）
        if (trimmedUrl.includes('.aliyuncs.com') || trimmedUrl.includes('.oss-cn-')) {
          return 'https://' + trimmedUrl
        }
        // 相对路径需要补全
        if (trimmedUrl.startsWith('/')) {
          return window.location.origin + trimmedUrl
        }
        // 尝试作为完整URL返回（可能是其他CDN格式）
        return trimmedUrl
      }
      
      // 处理对象类型的答案（可能是 { url, name } 或数组）
      if (typeof answer === 'object' && answer !== null) {
        if (Array.isArray(answer)) {
          return answer.map(item => {
            if (typeof item === 'object' && item.url) {
              const finalUrl = ensureFullUrl(item.url)
              return { url: finalUrl, name: item.name || '' }
            }
            const finalUrl = ensureFullUrl(String(item))
            return { url: finalUrl, name: '' }
          })
        }
        if (answer.url) {
          const finalUrl = ensureFullUrl(answer.url)
          return [{ url: finalUrl, name: answer.name || '' }]
        }
        // 对象但无 url 属性，尝试遍历值
        return []
      }
      
      // 字符串类型，尝试解析 JSON
      try {
        const parsed = JSON.parse(answer)
        if (Array.isArray(parsed)) {
          return parsed.map(item => {
            if (typeof item === 'object' && item.url) {
              const finalUrl = ensureFullUrl(item.url)
              return { url: finalUrl, name: item.name || '' }
            }
            return { url: ensureFullUrl(String(item)), name: '' }
          })
        }
        if (typeof parsed === 'object' && parsed.url) {
          return [{ url: ensureFullUrl(parsed.url), name: parsed.name || '' }]
        }
        return [{ url: ensureFullUrl(String(parsed)), name: '' }]
      } catch {
        // 非 JSON 格式，可能是逗号分隔的 URL 或纯URL
        if (answer.includes(',')) {
          return answer.split(',').filter(u => u.trim()).map(url => ({ url: ensureFullUrl(url.trim()), name: '' }))
        }
        return [{ url: ensureFullUrl(answer), name: '' }]
      }
    },
    
    // 判断是否是"其他"选项
    isOtherOption(opt) {
      if (!opt) return false
      // 检查 isOther 标志
      if (opt.isOther === true) return true
      // 检查 value 是否以 'other_' 开头（后端生成的"其他"选项通常有这样的值）
      if (opt.value && String(opt.value).toLowerCase().startsWith('other_')) return true
      // 检查标签文本
      const labelText = (opt.label || '').replace(/<[^>]*>/g, '').trim()
      return labelText === '其他' || labelText.toLowerCase() === 'other'
    },
    
    // 获取"其他"选项的文本内容
    getOtherText(itemKey, opt) {
      // 优先从直接存储的 otherTextMap 中获取
      if (this.otherTextMap && this.otherTextMap[itemKey]) {
        return this.otherTextMap[itemKey]
      }
      return ''
    },
    
    isCorrectOption(question, option) {
      if (!question.correctAnswer) return false
      try {
        const correct = JSON.parse(question.correctAnswer)
        if (Array.isArray(correct)) {
          return correct.includes(option.value)
        }
        return correct === option.value
      } catch {
        return String(question.correctAnswer) === String(option.value)
      }
    },
    
    // 是否是用户原始选中的选项
    isOriginalSelected(question, option) {
      const answer = this.answerMap[question.itemKey]
      if (!answer) return false
      const optValue = String(option.value)
      const optLabel = this.stripHtml(option.label || '')
      try {
        const selected = JSON.parse(answer)
        if (Array.isArray(selected)) {
          return selected.some(v => String(v) === optValue || String(v) === optLabel)
        }
        return String(selected) === optValue || String(selected) === optLabel
      } catch {
        // 非 JSON，直接字符串比较
        const strAnswer = String(answer)
        return strAnswer === optValue || strAnswer === optLabel ||
               this.stripHtml(strAnswer) === optValue || this.stripHtml(strAnswer) === optLabel
      }
    },

    // 是否是一审审核员认定的选项（在二审/三审时作为历史标记显示）
    // 新规则：
    //  1) 只要一审审过（有 reviewResult1）或改过答案（reviewAnswer1 非空），就显示"一审"tag
    //  2) 一审审过但没改答案时，视为"一审完全认可神客答案"，tag 打在神客原答案对应选项上
    //     （这样即使一审和神客选的相同，"原答案"+"一审"两个 tag 都会同时显示在同一选项上）
    isReview1Selected(question, option) {
      if (this.reviewLevel < 2) return false
      const reviewed = !!this.reviewResult1Map[question.itemKey]
      const review1Answer = this.review1AnswerMap[question.itemKey]
      if (!reviewed && !review1Answer) return false
      const effectiveAnswer = review1Answer || this.answerMap[question.itemKey]
      if (!effectiveAnswer) return false
      return this.matchesOption(effectiveAnswer, option)
    },

    // 是否是二审审核员认定的选项（在三审时作为历史标记显示）
    // 同 isReview1Selected 规则：未改答案时回退到一审答案 > 神客原答案
    isReview2Selected(question, option) {
      if (this.reviewLevel < 3) return false
      const reviewed = !!this.reviewResult2Map[question.itemKey]
      const review2Answer = this.review2AnswerMap[question.itemKey]
      if (!reviewed && !review2Answer) return false
      // 二审有效答案：自己改过的 > 一审改过的 > 神客原答案
      const effectiveAnswer = review2Answer
        || this.review1AnswerMap[question.itemKey]
        || this.answerMap[question.itemKey]
      if (!effectiveAnswer) return false
      return this.matchesOption(effectiveAnswer, option)
    },

    // 通用答案→选项匹配，兼容：
    //  - 普通题纯字符串: "3" / "选项A"
    //  - 普通多选数组: ["1","2"] 或 [1,2]
    //  - 明检题 JSON: {selectedValue:"3"} / {selectedValues:["3","4"]}
    //  - label 兜底（历史数据存了 label 而非 value 时）
    matchesOption(answer, option) {
      if (answer == null || !option) return false
      const optValue = String(option.value)
      const optLabel = this.stripHtml(option.label || '')

      let parsed = answer
      if (typeof answer === 'string') {
        try { parsed = JSON.parse(answer) } catch (e) { parsed = answer }
      }

      // 明检题结构：{selectedValue} 或 {selectedValues}
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        if (parsed.selectedValue !== undefined && parsed.selectedValue !== null) {
          return String(parsed.selectedValue) === optValue
        }
        if (Array.isArray(parsed.selectedValues)) {
          return parsed.selectedValues.map(String).includes(optValue)
        }
        return false
      }

      // 普通多选：数组
      if (Array.isArray(parsed)) {
        return parsed.some(v => String(v) === optValue || this.stripHtml(String(v)) === optLabel)
      }

      // 普通单选：字符串/数字
      const str = String(parsed)
      return str === optValue || str === optLabel || this.stripHtml(str) === optLabel
    },

    // 兼容别名（旧代码可能还在调用）
    isAnswerMatchOption(answer, option) {
      return this.matchesOption(answer, option)
    },

    // 按题判断是否应显示原答案标记（蓝色勾+"原答案"标签）
    // 新规则：一审/二审/三审所有级别都显示神客原答案标记（作为历史参考）
    // 用户手动改答案后仍保留原答案标记，保证"神客+历次审核"的完整溯源链不丢失
    canShowOriginalForQuestion(question) {
      return this.shouldShowOriginalMarks
    },

    // 是否是审核人选中的选项
    isReviewSelected(question, option) {
      const reviewAnswer = this.reviewAnswerMap[question.itemKey]
      if (!reviewAnswer) return false
      try {
        const selected = JSON.parse(reviewAnswer)
        const optValue = String(option.value)
        if (Array.isArray(selected)) {
          return selected.some(v => String(v) === optValue)
        }
        return String(selected) === optValue
      } catch {
        return String(reviewAnswer) === String(option.value)
      }
    },
    
    // 格式化题目数据为 Question 组件期望的格式
    formatQuestionItem(question) {
      return {
        item_key: question.itemKey,
        item_type: question.itemType,
        label: question.label,
        description: question.description || '',
        tip_content: question.tipContent || '',
        config_json: question.configJson || '{}',
        options: question.options
      }
    },
    
    // 获取审核答案（优先显示审核修改后的答案）
    getReviewAnswer(itemKey) {
      return this.reviewAnswerMap[itemKey] || this.answerMap[itemKey] || null
    },
    
    // 获取选项标签文本
    getOptionLabel(opt) {
      if (!opt) return ''
      return this.stripHtml(opt.label || opt.text || String(opt.value))
    },
    
    // 处理 Question 组件的答案变更
    handleQuestionChange(payload) {
      const { itemKey, value } = payload
      this.$set(this.reviewAnswerMap, itemKey, value)
      // 找到对应的 question 对象并更新评审结果
      const question = this.questionList.find(q => q.itemKey === itemKey)
      if (question) {
        this.updateReviewResultFromAnswer(question, value)
      }
    },

    // 是否允许编辑审核答案：以后端权威关系字段 + 元数据兜底
    // 规则：仅“被引用题(target)”可编辑；来源题(source)与普通题不可编辑
    canEditReviewAnswer(question) {
      if (!this.isChoiceQuestion(question?.itemType) && question?.itemType !== 'MINGJIAN_PHOTO_TEXT') return false

      // 简化审核模式（阶段1/2/4）：所有选择题都可编辑审核答案
      if (this.isSimplifiedAudit && !this.isReadOnlyMode) return true

      // 阶段3（多级审核）：所有选择题都可编辑审核答案，不受回圈限制
      if (!this.isSimplifiedAudit && this.examForm === 1 && !this.isReadOnlyMode) return true

      const normalize = (v) => {
        if (v === null || v === undefined) return ''
        const s = String(v).trim()
        return (!s || s === 'undefined' || s === 'null') ? '' : s
      }

      // 后端显式标记
      const isSource = question?.isLoopbackSource === true
      const isTargetByFlag = question?.isLoopbackReference === true || question?.isReferenced === true

      // 元数据兜底：存在 sourceFormKey/sourceItemKey 或 relationIds 或 optionMapping
      const hasTargetBinding = !!(normalize(question?.sourceFormKey) && normalize(question?.sourceItemKey))
      const hasRelationIds = Array.isArray(question?.relationIds) && question.relationIds.some(x => normalize(x))
      const hasOptionMapping = !!question?.optionMapping

      // 历史数据兜底：数据源类型为 questionnaire 的选择题，视为回圈题
      const dsType = normalize(question?.dataSourceType).toLowerCase()
      const isQuestionnaireSource = dsType === 'questionnaire'

      // target 优先：只要命中任一回圈特征，就允许编辑，避免被 isLoopbackSource 脏标记误伤
      const isTarget = isTargetByFlag || hasTargetBinding || hasRelationIds || hasOptionMapping || isQuestionnaireSource
      if (isTarget) return true

      // 非回圈题保持不可编辑
      return !isSource && false
    },

    // 点击切换审核选项
    toggleReviewOption(question, option) {
      const isMultiple = this.isMultipleChoice(question.itemType)
      let current = this.reviewAnswerMap[question.itemKey] || ''
      let selected = []

      // 标记用户已手动操作该题目，停止显示蓝色原答案标记
      this.$set(this.touchedQuestions, question.itemKey, true)

      // 解析当前选中状态 - 确保正确解析字符串类型
      if (current) {
        try {
          const parsed = JSON.parse(current)
          if (Array.isArray(parsed)) {
            selected = parsed.map(String)
          } else {
            selected = [String(parsed)]
          }
        } catch {
          selected = current.split(',').map(s => s.trim()).filter(Boolean)
        }
      }

      const optValue = String(option.value)
      // 使用findIndex并确保类型一致比较
      const idx = selected.findIndex(v => String(v) === optValue)

      if (isMultiple) {
        // 多选：切换选中状态
        if (idx >= 0) {
          selected.splice(idx, 1)
        } else {
          selected.push(optValue)
        }

        // 应用互斥逻辑
        if (question.exclusive) {
          // 优先使用分组配置
          const exclusiveGroups = question.exclusiveGroups || []
          if (exclusiveGroups.length > 0) {
            // 找到新选中选项所属的分组
            let clickedGroupId = null
            exclusiveGroups.forEach(group => {
              if (group.options && group.options.map(String).includes(optValue)) {
                clickedGroupId = group.id
              }
            })

            if (clickedGroupId && idx < 0) {
              // 刚刚选中了属于某分组的选项 -> 移除其他分组的选项
              selected = selected.filter(val => {
                let valGroupId = null
                exclusiveGroups.forEach(group => {
                  if (group.options && group.options.map(String).includes(val)) {
                    valGroupId = group.id
                  }
                })
                // 保留：相同分组的选项 或 不属于任何分组的选项
                return valGroupId === null || valGroupId === clickedGroupId
              })
            }
          } else if (question.exclusiveOptions && question.exclusiveOptions.length > 0) {
            // 兼容旧版：使用 exclusiveOptions 配置
            const exclusiveOptStrs = question.exclusiveOptions.map(v => String(v))
            const clickedIsExclusive = exclusiveOptStrs.includes(optValue)
            const wasExclusiveSelected = selected.filter(v => v !== optValue).some(v => exclusiveOptStrs.includes(v))

            if (clickedIsExclusive && idx < 0) {
              // 刚刚选中了互斥选项 -> 只保留该互斥选项，取消其他所有
              selected = [optValue]
            } else if (!clickedIsExclusive && wasExclusiveSelected) {
              // 在互斥选项已选中的情况下选了其他 -> 取消所有互斥选项
              selected = selected.filter(v => !exclusiveOptStrs.includes(v))
            }
          }
        }
      } else {
        // 单选：如果已选中则取消，否则选中新选项
        if (idx >= 0) {
          selected = []
        } else {
          selected = [optValue]
        }
      }

      // 保存新的审核答案
      const newAnswer = selected.length > 0 ? JSON.stringify(selected) : ''

      // 使用 $set 确保响应式更新
      this.$set(this.reviewAnswerMap, question.itemKey, newAnswer)

      // 自动同步到”引用了当前题目”的题（仅单选/多选）
      this.syncLoopbackReferencedAnswers(question, newAnswer)

      // 阶段1/2/4：回圈题填写后，反向回填到它引用的目标题目（覆盖答案）
      if (this.isSimplifiedAudit) {
        this.syncLoopbackReverseToSource(question, newAnswer)
      }

      // 按”当前审核答案 + 原答案”实时重新计算题目触发显示
      this.applyLogicVisibility()

      // 递增计数器强制计算属性重新计算
      this.updateCounter++

      // 自动更新评审结果
      this.updateReviewResultFromAnswer(question, newAnswer)
    },

    // 回圈同步：当前题答案变化后，更新引用它的题目答案
    syncLoopbackReferencedAnswers(sourceQuestion, sourceAnswer) {
      if (!this.isChoiceQuestion(sourceQuestion?.itemType)) return
      const sourceKey = sourceQuestion?.itemKey
      if (!sourceKey) return

      const normalize = (v) => {
        if (v === null || v === undefined) return ''
        const s = String(v).trim()
        return (s === 'undefined' || s === 'null') ? '' : s
      }

      const sourceKeys = this.getLoopbackCandidateKeys(sourceQuestion).map(normalize)
      const targets = (this.questionList || []).filter(q => {
        if (!q || q.itemKey === sourceKey) return false
        const qSourceItemKey = normalize(q.sourceItemKey)
        return qSourceItemKey && sourceKeys.includes(qSourceItemKey)
      })

      targets.forEach(target => {
        if (!this.isChoiceQuestion(target.itemType)) return

        const normalizedAnswer = this.normalizeLoopbackAnswerForTarget(sourceAnswer, target)
        this.$set(this.reviewAnswerMap, target.itemKey, normalizedAnswer)
        this.updateReviewResultFromAnswer(target, normalizedAnswer)
      })
    },

    // 阶段1/2/4：回圈题编辑后，反向回填到它引用的源题目（覆盖答案）
    syncLoopbackReverseToSource(editedQuestion, newAnswer) {
      if (!this.isChoiceQuestion(editedQuestion?.itemType)) return

      const normalize = (v) => {
        if (v === null || v === undefined) return ''
        const s = String(v).trim()
        return (s === 'undefined' || s === 'null') ? '' : s
      }

      // 获取回圈题引用的源题 itemKey
      const sourceItemKey = normalize(editedQuestion?.sourceItemKey)
      if (!sourceItemKey) return

      // 在题目列表中找到源题
      const sourceQuestion = (this.questionList || []).find(q => {
        if (!q) return false
        const candidateKeys = this.getLoopbackCandidateKeys(q).map(normalize)
        return candidateKeys.includes(sourceItemKey)
      })

      if (!sourceQuestion || !this.isChoiceQuestion(sourceQuestion.itemType)) return

      // 反向映射：将编辑题的答案转换为源题的格式
      const reverseAnswer = this.normalizeLoopbackAnswerReverse(newAnswer, editedQuestion, sourceQuestion)
      this.$set(this.reviewAnswerMap, sourceQuestion.itemKey, reverseAnswer)
      this.updateReviewResultFromAnswer(sourceQuestion, reverseAnswer)
    },

    // 反向映射：将回圈题答案转换为源题可用格式（反转 optionMapping）
    normalizeLoopbackAnswerReverse(answer, editedQuestion, sourceQuestion) {
      if (!answer) return ''

      let values = []
      try {
        const parsed = JSON.parse(answer)
        values = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)]
      } catch {
        values = [String(answer)]
      }

      if (values.length === 0) return ''

      // 反转 optionMapping：{ sourceVal: targetVal } → { targetVal: sourceVal }
      const mapping = this.normalizeOptionMapping(editedQuestion && editedQuestion.optionMapping)
      if (mapping) {
        const reverseMap = {}
        Object.keys(mapping).forEach(k => {
          reverseMap[String(mapping[k])] = String(k)
        })
        values = values.map(v => {
          const key = String(v)
          return Object.prototype.hasOwnProperty.call(reverseMap, key) ? reverseMap[key] : key
        })
      }

      // 只保留在源题 options 中存在的值
      const sourceOptionValues = new Set(((sourceQuestion && sourceQuestion.options) || []).map(o => String(o.value)))
      if (sourceOptionValues.size > 0) {
        values = values.filter(v => sourceOptionValues.has(String(v)))
      }

      values = [...new Set(values)]
      if (values.length === 0) return ''

      // 根据源题题型输出格式
      if (this.isMultipleChoice(sourceQuestion.itemType)) {
        return JSON.stringify(values)
      }
      return JSON.stringify([values[0]])
    },

    // 将源题答案规范为目标题可用格式（支持 optionMapping 覆盖）
    normalizeLoopbackAnswerForTarget(answer, targetQuestion) {
      if (!answer) return ''

      let values = []
      try {
        const parsed = JSON.parse(answer)
        values = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)]
      } catch {
        values = [String(answer)]
      }

      if (values.length === 0) return ''

      // 1) 先按目标题的映射表转换（有映射就覆盖）
      const mapping = this.normalizeOptionMapping(targetQuestion && targetQuestion.optionMapping)
      if (mapping) {
        values = values.map(v => {
          const key = String(v)
          return Object.prototype.hasOwnProperty.call(mapping, key) ? String(mapping[key]) : key
        })
      }

      // 2) 只保留在目标题 options 中真实存在的值，避免脏值
      const targetOptionValues = new Set(((targetQuestion && targetQuestion.options) || []).map(o => String(o.value)))
      if (targetOptionValues.size > 0) {
        values = values.filter(v => targetOptionValues.has(String(v)))
      }

      // 去重
      values = [...new Set(values)]
      if (values.length === 0) return ''

      // 3) 根据目标题型输出格式
      if (this.isMultipleChoice(targetQuestion.itemType)) {
        return JSON.stringify(values)
      }

      // 目标是单选：只取第一个
      return JSON.stringify([values[0]])
    },

    // 获取回圈关联候选键：itemKey / __vModel__ / serialNo（兼容历史配置）
    getLoopbackCandidateKeys(question) {
      const normalize = (v) => {
        if (v === null || v === undefined) return ''
        const s = String(v).trim()
        return (s === 'undefined' || s === 'null') ? '' : s
      }

      const keys = [
        normalize(question?.itemKey),
        normalize(question?.vModel),
        normalize(question?.serialNo)
      ].filter(Boolean)

      return [...new Set(keys)]
    },

    // 标准化 optionMapping
    // 支持格式：
    // 1) 对象：{ "A": "B", "1": "2" }
    // 2) 数组：[{sourceValue:'A', targetValue:'B'}] / [{from:'A', to:'B'}]
    normalizeOptionMapping(raw) {
      if (!raw) return null

      let data = raw
      if (typeof raw === 'string') {
        try {
          data = JSON.parse(raw)
        } catch {
          return null
        }
      }

      if (Array.isArray(data)) {
        const map = {}
        data.forEach(item => {
          if (!item) return
          const from = item.sourceValue ?? item.from ?? item.source ?? item.key
          const to = item.targetValue ?? item.to ?? item.target ?? item.value
          if (from !== undefined && from !== null && to !== undefined && to !== null) {
            map[String(from)] = String(to)
          }
        })
        return Object.keys(map).length ? map : null
      }

      if (typeof data === 'object') {
        const map = {}
        Object.keys(data).forEach(k => {
          const v = data[k]
          if (v !== undefined && v !== null && v !== '') {
            map[String(k)] = String(v)
          }
        })
        return Object.keys(map).length ? map : null
      }

      return null
    },
    
    // 根据审核答案自动更新评审结果
    updateReviewResultFromAnswer(question, reviewAnswer) {
      if (!reviewAnswer) {
        // 没有选择任何选项，不得分
        this.$set(this.reviewMap, question.itemKey, 'NO_SCORE')
        return
      }
      
      // 检查是否选中了“不涉及”选项
      if (this.isNotApplicableAnswer(question, reviewAnswer)) {
        this.$set(this.reviewMap, question.itemKey, 'NOT_APPLICABLE')
        return
      }
      
      // 情况1: 如果每个选项都设置了分值（perOption模式），使用混合计分逻辑
      if (this.hasAllOptionsWithScore(question)) {
        // 计算正分之和与负分之和
        let positiveSum = 0
        let negativeSum = 0
        try {
          let selectedValues = JSON.parse(reviewAnswer)
          if (!Array.isArray(selectedValues)) selectedValues = [selectedValues]
          
          selectedValues.forEach(val => {
            const opt = question.options.find(o => String(o.value) === String(val))
            if (opt && opt.score) {
              if (opt.score > 0) {
                positiveSum += opt.score
              } else {
                negativeSum += opt.score
              }
            }
          })
        } catch {
          // 解析失败，按不得分处理
        }
        
        // 使用混合计分逻辑：只选负分项时从满分扣减
        const fullScore = question.maxScore || 0
        let baseScore = 0
        if (positiveSum === 0 && negativeSum < 0) {
          baseScore = fullScore  // 只选负分项时，从满分扣减
        }
        
        // 公式：Final = min(Total, Base + Pos) + Neg
        let calculated = baseScore + positiveSum
        if (calculated > fullScore) calculated = fullScore
        let earnedScore = calculated + negativeSum
        if (earnedScore < 0) earnedScore = 0
        
        // 只有满分才是SCORE，不满分就是NO_SCORE
        this.$set(this.reviewMap, question.itemKey, earnedScore >= fullScore ? 'SCORE' : 'NO_SCORE')
        return
      }
      
      // 情况2: 有正确答案，比对审核答案与正确答案
      if (question.correctAnswer) {
        const isCorrect = this.checkAnswerCorrect(reviewAnswer, question.correctAnswer, question.scoringMethod)
        this.$set(this.reviewMap, question.itemKey, isCorrect ? 'SCORE' : 'NO_SCORE')
      } else {
        // 情况3: 没有正确答案的题目，不自动设置得分，需要审核人员手动判断
        // 如果当前已有评审结果则保持，否则默认不得分
        const currentResult = this.reviewMap[question.itemKey]
        if (!currentResult) {
          this.$set(this.reviewMap, question.itemKey, 'NO_SCORE')
        }
        // 否则保持原评审结果不变，让用户手动修改
      }
    },
    
    isSelectedOption(question, option) {
      const answer = this.answerMap[question.itemKey]
      if (!answer) return false
      try {
        const selected = JSON.parse(answer)
        if (Array.isArray(selected)) {
          return selected.includes(option.value)
        }
        return selected === option.value
      } catch {
        return String(answer) === String(option.value)
      }
    },
    
    getAnswerValue(itemKey) {
      return this.answerMap[itemKey] || ''
    },
    
    getNavItemClass(question) {
      const result = this.reviewMap[question.itemKey]
      if (!result) return 'unanswered'
      if (result === 'SCORE') return 'correct'
      if (result === 'NO_SCORE') return 'wrong'
      if (result === 'NOT_APPLICABLE') return 'not-applicable'
      return 'unanswered'
    },
    
    scrollToQuestion(index) {
      const ref = this.$refs['question-' + index]
      if (ref && ref[0]) {
        ref[0].scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    scrollToRefItem(phaseNo, itemKey) {
      const ref = this.$refs['ref-item-' + phaseNo + '-' + itemKey]
      if (ref && ref[0]) {
        ref[0].scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    filteredRefItems(group) {
      const keyword = (this.navSearchKeyword || '').trim().toLowerCase()
      if (!keyword) return group.items
      return group.items.filter(item => 
        this.stripHtml(item.label).toLowerCase().includes(keyword)
        || (item.serialNo && item.serialNo.toLowerCase().includes(keyword))
      )
    },
    
    formatDuration(seconds) {
      if (!seconds) return '00:00:00'
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = seconds % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
    
    stripHtml(str) {
      if (!str) return ''
      return str.replace(/<[^>]+>/g, '').trim()
    },
    
    truncateTitle(str, maxLen) {
      if (!str) return ''
      return str.length > maxLen ? str.substring(0, maxLen) + '...' : str
    },
    
    // 自动检查答案是否正确，返回 SCORE/NO_SCORE/NOT_APPLICABLE
    autoCheckAnswerResult(question) {
      const userAnswer = this.answerMap[question.itemKey]
      
      // 如果没有作答，返回不得分
      if (!userAnswer) {
        return 'NO_SCORE'
      }
      
      // 检查用户是否选了“不涉及”选项
      if (this.isNotApplicableAnswer(question, userAnswer)) {
        return 'NOT_APPLICABLE'
      }
      
      // 情况1：每个选项都设置了分值（perOption 模式），使用混合计分逻辑
      if (this.hasAllOptionsWithScore(question)) {
        // 计算正分之和与负分之和
        let positiveSum = 0
        let negativeSum = 0
        try {
          let selectedValues = []
          try {
            selectedValues = JSON.parse(userAnswer)
            if (!Array.isArray(selectedValues)) selectedValues = [selectedValues]
          } catch {
            selectedValues = [userAnswer]
          }
          
          selectedValues.forEach(val => {
            const opt = question.options.find(o => String(o.value) === String(val))
            if (opt && opt.score) {
              if (opt.score > 0) {
                positiveSum += opt.score
              } else {
                negativeSum += opt.score
              }
            }
          })
        } catch (e) {
        }
        
        // 使用混合计分逻辑：只选负分项时从满分扣减
        const fullScore = question.maxScore || 0
        let baseScore = 0
        if (positiveSum === 0 && negativeSum < 0) {
          baseScore = fullScore  // 只选负分项时，从满分扣减
        }
        
        // 公式：Final = min(Total, Base + Pos) + Neg
        let calculated = baseScore + positiveSum
        if (calculated > fullScore) calculated = fullScore
        let earnedScore = calculated + negativeSum
        if (earnedScore < 0) earnedScore = 0
        
        return earnedScore > 0 ? 'SCORE' : 'NO_SCORE'
      }
      
      // 情况2：有正确答案，根据正确答案判断
      if (question.correctAnswer) {
        const isCorrect = this.checkAnswerCorrect(userAnswer, question.correctAnswer, question.scoringMethod)
        return isCorrect ? 'SCORE' : 'NO_SCORE'
      }
      
      // 情况3：没有正确答案也没有选项分值，需要人工判断
      // 情况3：没有正确答案也没有选项分值，需要人工判断
      return ''  // 需要人工判断
    },
    
    // 比较用户答案和正确答案是否匹配
    // scoringMethod: 'single' | 'allContains' | 'perOption' | 'manual'
    //   - single（默认/多选精确匹配）：用户答案 === 正确答案（排序后全一致）
    //   - allContains（仅多选）：用户答案覆盖全部正确答案即可（允许多选错项）
    checkAnswerCorrect(userAnswer, correctAnswer, scoringMethod) {
      if (!userAnswer || !correctAnswer) {
        return false
      }

      try {
        // 尝试解析为数组（多选题）
        let userArr = []
        let correctArr = []

        try {
          userArr = JSON.parse(userAnswer)
          if (!Array.isArray(userArr)) userArr = [userArr]
        } catch {
          userArr = String(userAnswer).split(',').map(s => s.trim())
        }

        try {
          correctArr = JSON.parse(correctAnswer)
          if (!Array.isArray(correctArr)) correctArr = [correctArr]
        } catch {
          correctArr = String(correctAnswer).split(',').map(s => s.trim())
        }

        const userStrs = userArr.map(String).map(s => s.trim()).filter(Boolean)
        const correctStrs = correctArr.map(String).map(s => s.trim()).filter(Boolean)

        // 包含全部正确答案即得分（多选宽松模式）
        if (scoringMethod === 'allContains') {
          if (correctStrs.length === 0) return false
          const userSet = new Set(userStrs)
          return correctStrs.every(c => userSet.has(c))
        }

        // 默认：精确匹配（single / 未指定）
        const userSorted = [...userStrs].sort()
        const correctSorted = [...correctStrs].sort()
        if (userSorted.length !== correctSorted.length) return false
        return userSorted.every((val, idx) => val === correctSorted[idx])
      } catch (e) {
        // 简单字符串比较
        return String(userAnswer).trim() === String(correctAnswer).trim()
      }
    },

    // 判断答案是否选中了标记为“不涉及”的选项
    isNotApplicableAnswer(question, answerValue) {
      if (!answerValue || !question.options) return false
      // 获取所有标记为 notApplicable 的选项值
      const naOptionValues = question.options
        .filter(opt => opt.notApplicable)
        .map(opt => String(opt.value))
      if (naOptionValues.length === 0) return false
      // 解析答案中选中的选项
      let selectedValues = []
      try {
        const parsed = JSON.parse(answerValue)
        selectedValues = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)]
      } catch {
        selectedValues = [String(answerValue)]
      }
      // 只要选中了任何一个“不涉及”选项，就返回 true
      return selectedValues.some(v => naOptionValues.includes(v))
    },

    // 判断是否为评分类题目
    isRatingQuestion(itemType) {
      if (!itemType) return false
      const type = itemType.toUpperCase()
      return ['RATING', 'RATE', 'SCORE', 'NPS', 'SLIDER'].includes(type)
    },

    // 获取评分题最大分数
    getRatingMax(question) {
      if (question.maxScore) return question.maxScore
      // 尝试解析配置信息
      try {
        const config = JSON.parse(question.configJson || '{}')
        return config.max || config.maxScore || 5
      } catch (e) {
        return 5
      }
    },
    
    // 判断是否为签名题
    isSignatureQuestion(itemType) {
      if (!itemType) return false
      const type = itemType.toUpperCase()
      return ['SIGNATURE', 'SIGN', 'HANDWRITING', 'SIGN_PAD'].includes(type)
    },
    
    // 判断是否为手机号验证题
    isPhoneVerifyQuestion(itemType) {
      if (!itemType) return false
      return ['MOBILE_INPUT', 'PHONE', 'PHONE_VERIFY', 'SMS_VERIFY'].includes(itemType)
    },
    
    // 解析手机号验证答案 JSON
    parsePhoneAnswer(val) {
      if (!val) return { phone: '', code: '' }
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        return { phone: parsed.phone || parsed.mobile || '', code: parsed.code || parsed.verifyCode || '' }
      } catch {
        return { phone: String(val), code: '' }
      }
    },
    
    // 判断廉政协议 URL 是否为图片（非 PDF）
    isAgreementImage(url) {
      if (!url) return false
      const urlPath = url.split('?')[0].toLowerCase()
      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(urlPath)
    },
    
    // 格式化答案显示（处理可能的 JSON 字符串）
    formatAnswerDisplay(val) {
      if (!val) return ''
      if (typeof val !== 'string') return String(val)
      const trimmed = val.trim()
      // 尝试解析 JSON 对象/数组，格式化展示
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed)
          // 如果是文件数组 [{url, name}]，提取名称
          if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].url) {
            return parsed.map(f => f.name || '文件').join('、')
          }
          // 普通对象，格式化为 key: value
          if (typeof parsed === 'object' && !Array.isArray(parsed)) {
            return Object.entries(parsed).map(([k, v]) => `${k}：${v}`).join('，')
          }
        } catch { /* 非法 JSON，原样返回 */ }
      }
      return val
    },

    // ===== 42 号方案 D.1：作废 / 修改审核 / 恢复 / 审核历史 =====

    auditStatusText(status) {
      const s = Number(status) || 0
      if (s === -1) return '已驳回'
      if (s === 0) return '待一审'
      if (s === 1) return '一审完成'
      if (s === 2) return '二审完成'
      return '三审完成'
    },

    openVoidDialog(level) {
      this.voidDialogLevel = level
      this.voidDialogReason = ''
      this.voidDialogVisible = true
    },

    async submitVoidLevel() {
      const reason = (this.voidDialogReason || '').trim()
      if (reason.length < 10) {
        this.$message.warning('请填写作废原因，不少于 10 字')
        return
      }
      if (!this.currentExaminee || !this.currentExaminee.answerKey) {
        this.$message.error('答卷信息缺失')
        return
      }
      this.voidSubmitting = true
      try {
        const { voidReviewLevel } = await import('index/api/project/form')
        await voidReviewLevel({
          answerKey: this.currentExaminee.answerKey,
          reviewLevel: this.voidDialogLevel,
          reason
        })
        this.$message.success(`已作废 ${['', '一', '二', '三'][this.voidDialogLevel]} 审结果`)
        this.voidDialogVisible = false
        // 更新本地状态（audit_status 回退）
        this.currentExaminee.auditStatus = this.voidDialogLevel - 1
        const idx = this.examineeList.findIndex(e => e.answerKey === this.currentExaminee.answerKey)
        if (idx >= 0) this.examineeList[idx].auditStatus = this.voidDialogLevel - 1
        // 刷新详情以获取清除后的数据
        this.selectExaminee(this.currentExaminee)
      } catch (e) {
        const msg = e?.data?.message || e?.message || '作废失败'
        this.$message.error(msg)
      } finally {
        this.voidSubmitting = false
      }
    },

    openModifyDialog(level) {
      this.modifyDialogLevel = level
      this.modifyDialogReason = ''
      this.modifyDialogVisible = true
    },

    enterModifyMode() {
      const reason = (this.modifyDialogReason || '').trim()
      if (reason.length < 10) {
        this.$message.warning('请填写修改原因，不少于 10 字')
        return
      }
      this.modifyMode = { active: true, level: this.modifyDialogLevel, reason }
      this.modifyDialogVisible = false
      // 切到该级别并解锁编辑模式（走现有 reviewLevel 切换逻辑）
      if (this.reviewLevel !== this.modifyDialogLevel) {
        this.reviewLevel = this.modifyDialogLevel
        this.handleReviewLevelChange && this.handleReviewLevelChange()
      }
      this.$message.info('已进入修改模式，编辑完成后点"提交审核"即可保存为新版本')
    },

    async openHistoryDrawer() {
      if (!this.currentExaminee || !this.currentExaminee.answerKey) {
        this.$message.error('答卷信息缺失')
        return
      }
      this.historyDrawerVisible = true
      this.historyLoading = true
      try {
        const { getReviewHistory } = await import('index/api/project/form')
        const res = await getReviewHistory(this.currentExaminee.answerKey)
        this.reviewHistoryList = res?.data?.records || res?.data?.data?.records || []
      } catch (e) {
        console.error('加载审核历史失败', e)
        this.reviewHistoryList = []
      } finally {
        this.historyLoading = false
      }
    },

    openRestoreDialog(historyItem) {
      this.restoreDialogTarget = historyItem
      this.restoreDialogReason = ''
      this.restoreDialogVisible = true
    },

    async submitRestore() {
      const reason = (this.restoreDialogReason || '').trim()
      if (reason.length < 10) {
        this.$message.warning('请填写恢复原因，不少于 10 字')
        return
      }
      const target = this.restoreDialogTarget
      if (!target || !this.currentExaminee) return
      this.restoreSubmitting = true
      try {
        const { restoreReview } = await import('index/api/project/form')
        await restoreReview({
          answerKey: this.currentExaminee.answerKey,
          reviewLevel: target.reviewLevel,
          targetVersion: target.reviewVersion,
          reason
        })
        this.$message.success(`已恢复到 ${['', '一', '二', '三'][target.reviewLevel]} 审 V${target.reviewVersion}`)
        this.restoreDialogVisible = false
        this.historyDrawerVisible = false
        // 刷新当前答卷详情
        this.selectExaminee(this.currentExaminee)
      } catch (e) {
        const msg = e?.data?.message || e?.message || '恢复失败'
        this.$message.error(msg)
      } finally {
        this.restoreSubmitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/* 一票否决 Banner（ExamGrading 审核页顶部） */
.veto-banner-danger {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border: 1px solid #fca5a5;
  border-left: 4px solid #dc2626;
  border-radius: 8px;
  padding: 14px 16px;
  margin: 12px 16px 16px;
  color: #7f1d1d;

  .veto-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 700;
    color: #b91c1c;
    i { font-size: 18px; color: #dc2626; }
  }
  .veto-list {
    margin: 8px 0 6px;
    padding-left: 20px;
    font-size: 13px;
    line-height: 1.7;
    strong { color: #991b1b; }
  }
  .veto-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 6px;
  }
  .veto-tip {
    color: #92400e;
    font-size: 12px;
  }
}
.veto-banner-warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #fcd34d;
  border-left: 4px solid #d97706;
  border-radius: 8px;
  padding: 12px 16px;
  margin: 12px 16px 16px;
  color: #78350f;

  .veto-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #b45309;
    i { font-size: 18px; color: #d97706; }
  }
  .veto-override-reason {
    margin-top: 6px;
    font-size: 13px;
    color: #78350f;
  }
}

.exam-grading-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
  overflow: hidden;
  
  /* 自定义滚动条 - 匹配原型图细滚动条 */
  ::v-deep *::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  ::v-deep *::-webkit-scrollbar-track {
    background: transparent;
  }
  ::v-deep *::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.3);
    border-radius: 3px;
    &:hover {
      background: rgba(148, 163, 184, 0.5);
    }
  }
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.3);
    border-radius: 3px;
  }
}

.grading-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 24px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  position: relative;
  flex-shrink: 0;
  z-index: 10;
  
  .title {
    font-size: 16px;
    font-weight: 600;
    color: #5856D6;
    flex-shrink: 0;
  }
  
  .current-dealer-info {
    margin-left: 0;
    margin-right: auto;
    display: flex;
    align-items: center;
    gap: 12px;
    
    .dealer-code {
      background: #2563eb;
      color: #fff;
      padding: 4px 12px;
      border-radius: 4px;
      font-size: 14px;
      font-weight: 700;
    }
    
    .dealer-name {
      font-size: 16px;
      color: #1e293b;
      font-weight: 600;
    }
  }
  
  .review-level-tabs {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 12px;
    
    ::v-deep .el-radio-group {
      background: #f1f5f9;
      padding: 4px;
      border-radius: 8px;
      
      .el-radio-button__inner {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        color: #64748b !important;
        font-weight: 500;
        padding: 6px 24px;
        border-radius: 6px !important;
        font-size: 14px;
      }
      
      .el-radio-button.is-active .el-radio-button__inner,
      .el-radio-button__orig-radio:checked + .el-radio-button__inner {
        background: #fff !important;
        color: #2563eb !important;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
        font-weight: 600;
      }
    }
    
    .level-hint-wrapper {
      min-width: 120px;
      display: flex;
      align-items: center;
    }
    
    .level-hint {
      font-size: 12px;
      white-space: nowrap;
      
      i {
        margin-right: 4px;
      }
      
      &.readonly-hint {
        color: #909399;
        background: #f4f4f5;
        padding: 4px 8px;
        border-radius: 4px;
      }
      
      &.warning {
        color: #E6A23C;
      }
    }
  }
}

/* 42 号方案 §10.6.1：数据治理工具条 */
.review-governance-bar {
  margin-left: auto;
  display: flex;
  align-items: center;
  padding-right: 16px;
}

/* 42 号方案 §10.6.3：审核历史时间线 */
.history-timeline {
  padding: 0 24px 24px;
}
.history-timeline .timeline-wrap {
  position: relative;
  padding-left: 24px;
}
.history-timeline .timeline-wrap::before {
  content: '';
  position: absolute;
  left: 7px;
  top: 4px;
  bottom: 4px;
  width: 2px;
  background: #e2e8f0;
}
.history-timeline .timeline-item {
  position: relative;
  padding-bottom: 20px;
}
.history-timeline .timeline-dot {
  position: absolute;
  left: -24px;
  top: 4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #cbd5e1;
  border: 3px solid #fff;
  box-shadow: 0 0 0 1px #cbd5e1;
}
.history-timeline .dot-type-0 { background: #60a5fa; box-shadow: 0 0 0 1px #60a5fa; }
.history-timeline .dot-type-1 { background: #f59e0b; box-shadow: 0 0 0 1px #f59e0b; }
.history-timeline .dot-type-2 { background: #ef4444; box-shadow: 0 0 0 1px #ef4444; }
.history-timeline .dot-type-3 { background: #10b981; box-shadow: 0 0 0 1px #10b981; }
.history-timeline .timeline-body {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
}
.history-timeline .timeline-current .timeline-body {
  background: #ecfdf5;
  border-color: #a7f3d0;
}
.history-timeline .timeline-line1 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}
.history-timeline .tl-action-type {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #64748b;
  font-weight: 500;
}
.history-timeline .action-type-0 { background: #dbeafe; color: #1d4ed8; }
.history-timeline .action-type-1 { background: #fef3c7; color: #b45309; }
.history-timeline .action-type-2 { background: #fee2e2; color: #b91c1c; }
.history-timeline .action-type-3 { background: #dcfce7; color: #15803d; }
.history-timeline .tl-current-tag {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #10b981;
  color: #fff;
  font-weight: 600;
}
.history-timeline .timeline-line2 {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}
.history-timeline .timeline-line3 {
  font-size: 13px;
  color: #475569;
  margin-top: 4px;
  line-height: 1.5;
}
.history-timeline .timeline-line4 {
  margin-top: 8px;
}

.grading-footer {
  height: 64px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  background: #fff;
  border-top: 1px solid #e2e8f0;
  flex-shrink: 0;
  
  .readonly-text {
    color: #909399;
    font-size: 14px;
    
    i {
      margin-right: 4px;
    }
  }
  
  ::v-deep .el-button {
    border-radius: 8px;
    padding: 10px 28px;
    font-size: 14px;
    font-weight: 500;
    
    &--success {
      background: #22c55e;
      border-color: #22c55e;
      &:hover {
        background: #16a34a;
        border-color: #16a34a;
      }
    }
    
    &--default {
      border-color: #e2e8f0;
      color: #475569;
      &:hover {
        border-color: #cbd5e1;
        color: #334155;
      }
    }
  }
}

.grading-content {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.left-panel {
  width: 256px;
  background: #fff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  
  .panel-header {
    padding: 16px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .panel-title {
      font-weight: 700;
      color: #334155;
    }
    
    .progress {
      color: #94a3b8;
      font-size: 12px;
    }
  }
  
  .examinee-list {
    flex: 1;
    overflow-y: auto;
  }
  
  .examinee-item {
    padding: 14px 16px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all 0.15s;
    position: relative;
    
    &:hover {
      background: #f8fafc;
    }
    
    &.active {
      background: #eff6ff;
      border: none;
      border-right: 4px solid #3b82f6;
      
      .examinee-name {
        color: #1d4ed8;
        font-weight: 600;
      }
    }
    
    .status-badge {
      font-size: 11px;
      padding: 3px 10px;
      border-radius: 12px;
      font-weight: 500;
      
      &.status-pending { background: #dbeafe; color: #2563eb; }
      &.status-review1 { background: #e6f7ff; color: #1890ff; }
      &.status-review2 { background: #fff7e6; color: #fa8c16; }
      &.status-complete { background: #f6ffed; color: #52c41a; }
      &.status-rejected { background: #fff1f0; color: #f5222d; }
    }
    
    .draft-badge {
      font-size: 10px;
      padding: 2px 6px;
      border-radius: 8px;
      background: #fff7e6;
      color: #d48806;
      border: 1px solid #ffe58f;
      font-weight: 500;
    }
  }
  
  .left-panel-footer {
    padding: 16px;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    
    .el-input__inner {
      border-color: #e2e8f0 !important;
      background: #fff;
      font-size: 14px;
    }
    
    .footer-info {
      margin-top: 12px;
      
      p {
        font-size: 11px;
        color: #64748b;
        margin: 0;
        line-height: 1.8;
      }
    }
  }
}

.center-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f8fafc;
}

.center-panel {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
  
  .answer-list {
    max-width: 900px;
    margin: 0 auto;
  }
  
  .question-card {
    background: #fff;
    border-radius: 8px;
    padding: 16px 20px;
    margin-bottom: 16px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }
  
  .question-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    
    .question-title {
      font-weight: 600;
      color: #303133;
    }
    
    .question-score-info {
      margin-left: 10px;
      color: #909399;
      font-size: 13px;
    }
    
    .full-record-badge {
      margin-left: auto;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      color: #3b82f6;
      padding: 4px 14px;
      border: 1px solid #bfdbfe;
      border-radius: 6px;
      background: #eff6ff;
      font-weight: 500;
      
      .dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #3b82f6;
      }
    }
    
    .float-play-btn {
      color: #3b82f6 !important;
      font-size: 13px !important;
      font-weight: 500;
      margin-left: 8px;
      padding: 0 !important;
    }
  }
  
  .question-label {
    margin-bottom: 12px;
    color: #606266;
    line-height: 1.6;
  }
  
  .options-list {
    .loopback-hint {
      margin-bottom: 6px;
      padding: 6px 10px;
      font-size: 12px;
      color: #909399;
      background: #f5f7fa;
      border: 1px dashed #dcdfe6;
      border-radius: 4px;
    }

    .option-item {
      display: flex;
      align-items: center;
      padding: 8px 12px;
      margin-bottom: 4px;
      border-radius: 4px;
      position: relative;
      
      &.clickable {
        cursor: pointer;
        transition: all 0.2s;
        
        &:hover {
          background: #ecf5ff;
        }
      }
      
      &.readonly {
        cursor: default;
        opacity: 0.8;
      }
      
      &.is-review-selected {
        background: #ecf5ff;
        border: 2px solid #409EFF;
      }
      
      &.is-correct {
        background: #f0f9eb;
      }
      
      &.is-wrong {
        background: #fef0f0;
      }
      
      .option-marker {
        margin-right: 8px;
        
        .correct-icon {
          color: #67C23A;
        }
        
        .wrong-icon {
          color: #F56C6C;
        }
        
        .review-check {
          color: #409EFF;
          font-weight: bold;
        }
        
        .option-circle {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 1px solid #dcdfe6;
          border-radius: 50%;
        }
        
        .option-checkbox {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 1px solid #dcdfe6;
          border-radius: 2px;
        }
      }
      
      .original-tag {
        margin-left: auto;
        padding: 2px 6px;
        font-size: 10px;
        background: #fef0f0;
        color: #F56C6C;
        border-radius: 4px;
      }
      
      .review1-tag {
        margin-left: 4px;
        padding: 2px 6px;
        font-size: 10px;
        background: #e6f7ff;
        color: #1890ff;
        border-radius: 4px;
      }
      
      .review2-tag {
        margin-left: 4px;
        padding: 2px 6px;
        font-size: 10px;
        background: #fff7e6;
        color: #fa8c16;
        border-radius: 4px;
      }
    }
  }
  
  .subjective-answer {
    background: #f5f7fa;
    padding: 12px;
    border-radius: 4px;
    
    .answer-label {
      color: #909399;
      font-size: 12px;
      margin-bottom: 8px;
    }
    
    .answer-content {
      color: #303133;
      line-height: 1.6;
    }
  }
  
  // 录音题样式
  .audio-answer {
    .answer-label {
      color: #606266;
      font-size: 13px;
      font-weight: 500;
      margin-bottom: 12px;
    }
    
    .answer-content {
      margin-top: 8px;
    }
  }
  
  .review-section {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px dashed #e4e7ed;
    
    .review-options {
      display: flex;
      align-items: center;
      margin-bottom: 12px;
      
      .review-label {
        color: #606266;
        margin-right: 12px;
        font-weight: 500;
      }
    }
    
    .review-remark {
      margin-top: 8px;
    }
  }
  
  .empty-tip {
    text-align: center;
    padding: 100px 0;
    color: #909399;
  }
}

.right-panel {
  width: 280px;
  background: #fff;
  border-left: 1px solid #e2e8f0;
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  
  .score-summary {
    text-align: center;
    margin-bottom: 16px;
    
    .label {
      display: block;
      color: #94a3b8;
      font-size: 13px;
      margin-bottom: 4px;
    }
    
    .score {
      font-size: 36px;
      font-weight: 700;
      color: #1e293b;
      line-height: 1.2;
      
      &.original {
        color: #1e293b;
      }
      
      &.changed {
        color: #67C23A;
      }
    }
    
    &.review-score .score {
      color: #ef4444;
      font-size: 40px;
    }

    .rule-chip {
      display: inline-block;
      margin-top: 6px;
      padding: 2px 8px;
      background: #f0f7ff;
      color: #1476ff;
      border-radius: 10px;
      font-size: 11px;
      line-height: 1.4;
      cursor: help;
    }
  }
  
  .level-display {
    text-align: center;
    margin-bottom: 24px;
    
    .level-label {
      color: #94a3b8;
      font-size: 13px;
    }
    
    .level-value {
      display: inline-block;
      font-size: 14px;
      font-weight: 700;
      margin-left: 8px;
      padding: 4px 16px;
      border-radius: 20px;
      background: #fef2f2;
      color: #ef4444 !important;
    }
  }
  
  .simplified-title {
    text-align: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e2e8f0;
    
    .label {
      color: #606266;
      font-size: 14px;
      font-weight: 500;
    }
  }
  
  .nav-search {
    padding: 0 4px 12px;
    
    .el-input {
      .el-input__inner {
        border-radius: 8px;
        border-color: #e2e8f0;
        font-size: 12px;
        &:focus {
          border-color: #3b82f6;
        }
      }
    }
  }
  
  .question-nav {
    flex: 1;
    overflow-y: auto;
    margin-bottom: 20px;
    padding-top: 12px;
    border-top: 1px solid #e2e8f0;
    
    &.nav-list-mode {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    
    .nav-phase-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      font-weight: 600;
      color: #64748b;
      padding: 4px 8px;
      margin-top: 4px;
      cursor: pointer;
      user-select: none;
      
      &:hover { color: #475569; }
      
      .nav-collapse-arrow {
        font-size: 12px;
        transition: transform 0.2s;
        color: #94a3b8;
        
        &.expanded {
          transform: rotate(90deg);
        }
      }
      
      .nav-phase-badge {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: #2563eb;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }
      
      .nav-phase-count {
        margin-left: auto;
        font-size: 11px;
        color: #94a3b8;
        font-weight: 400;
      }
    }
    
    .nav-ref-item {
      background: #f8fafc;
      border-color: #e2e8f0;
      border-style: dashed;
      
      &:hover {
        background: #eff6ff;
        border-color: #93c5fd;
      }
      
      .nav-index { color: #94a3b8; }
      .nav-title { color: #94a3b8; }
    }
    
    .nav-ref-tag {
      flex-shrink: 0;
      font-size: 10px;
      padding: 1px 6px;
      border-radius: 4px;
      background: #eff6ff;
      color: #3b82f6;
      margin-left: auto;
    }
    
    .nav-section-divider {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 8px 0;
      font-size: 11px;
      color: #94a3b8;
      
      &::before,
      &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: #e2e8f0;
      }
    }
    
    .nav-list-item {
      display: flex;
      align-items: center;
      padding: 12px 14px;
      border-radius: 10px;
      border: 1px solid #e2e8f0;
      font-size: 13px;
      cursor: pointer;
      transition: all 0.15s;
      
      &:hover {
        border-color: #cbd5e1;
        background: #f8fafc;
      }
      
      .nav-index {
        flex-shrink: 0;
        min-width: 32px;
        color: #64748b;
        font-weight: 600;
      }
      
      .nav-title {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: #475569;
      }
      
      .nav-status {
        flex-shrink: 0;
        margin-left: 8px;
      }
      
      .nav-pass {
        display: inline-block;
        font-size: 11px;
        padding: 3px 12px;
        border-radius: 6px;
        background: #22c55e;
        color: #fff;
        font-weight: 600;
      }
      
      .nav-fail {
        display: inline-block;
        font-size: 11px;
        padding: 3px 12px;
        border-radius: 6px;
        background: #ef4444;
        color: #fff;
        font-weight: 600;
      }
      
      &.unanswered {
        background: #fff;
        border-color: #e2e8f0;
        .nav-title { color: #94a3b8; }
        &::after {
          content: '未打分';
          font-size: 11px;
          color: #94a3b8;
          margin-left: auto;
          flex-shrink: 0;
        }
      }
      
      &.correct {
        background: #f0fdf4;
        border-color: #bbf7d0;
        border-left: 4px solid #22c55e;
        .nav-index { color: #16a34a; }
        .nav-title { color: #15803d; }
      }
      
      &.wrong {
        background: #fef2f2;
        border-color: #fecaca;
        border-left: 4px solid #ef4444;
        .nav-index { color: #dc2626; }
        .nav-title { color: #b91c1c; }
      }
      
      &.not-applicable {
        background: #f8fafc;
        border-color: #e2e8f0;
        border-left: 4px solid #94a3b8;
        .nav-index { color: #94a3b8; }
      }
    }
  }
  
  .nav-buttons {
    margin-top: auto;
    display: flex;
    gap: 12px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
    
    ::v-deep .el-button {
      flex: 1;
      border-radius: 8px;
      height: 40px;
      font-size: 14px;
      border-color: #e2e8f0;
      color: #475569;
      font-weight: 500;
    }
  }
}

// Question 组件适配样式
.question-display-wrapper {
  position: relative;
  
  // 隐藏 Question 组件内的标题（审核页面有自己的标题）
  ::v-deep .question-item {
    .question-header {
      display: none;
    }
    
    .question-desc,
    .question-tip {
      display: none;
    }
  }
}

// 原答案标识样式
.original-answer-markers {
  margin-top: 12px;
  padding: 10px 12px;
  background: #fef0f0;
  border-radius: 4px;
  border-left: 3px solid #F56C6C;
  
  .original-answer-marker {
    display: block;
    color: #F56C6C;
    font-size: 13px;
    line-height: 1.6;
    
    &:not(:last-child) {
      margin-bottom: 4px;
    }
  }
}

// 文件上传展示样式
.file-upload-answer {
  .answer-label {
    color: #303133;
    font-weight: 500;
    margin-bottom: 8px;
  }
  
  .file-gallery {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .file-thumb {
    border-radius: 4px;
    overflow: hidden;
    
    &.image-thumb {
      width: 100px;
      height: 100px;
      cursor: pointer;
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    
    &.video-thumb {
      max-width: 320px;
    }
    
    &.audio-thumb {
      display: flex;
      flex-direction: column;
      gap: 4px;
      
      .audio-name {
        font-size: 12px;
        color: #909399;
      }
    }
  }
  
  .file-download-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 8px 12px;
    background: #f5f7fa;
    border-radius: 4px;
    color: #409eff;
    text-decoration: none;
    
    &:hover {
      background: #ecf5ff;
    }
    
    i {
      font-size: 16px;
    }
  }
  
  .no-file {
    color: #909399;
    font-style: italic;
  }
  
  /* 评分显示样式 */
  .rating-display {
    display: flex;
    align-items: center;
    
    ::v-deep .el-rate {
      display: inline-flex;
      align-items: center;
    }
    
    ::v-deep .el-rate__icon {
      font-size: 18px;
      margin-right: 2px;
    }
  }
}

// "其他"选项文本样式
.other-text-value {
  color: #e6a23c;
  font-weight: 500;
  margin-left: 4px;
}

// "其他"选项输入框样式
.other-input-wrapper {
  margin-left: 28px;  // 与选项文字对齐（option-marker宽度）
  margin-top: 8px;
  margin-bottom: 8px;
  
  .other-input {
    max-width: 300px;
  }
}

/* ===== 跨阶段参考资料区域 ===== */
.cross-phase-reference {
  margin-bottom: 32px;
}

.ref-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 16px;
  i { font-size: 18px; color: #94a3b8; }
}

/* 原型图风格：白色圆角卡片 + 阴影 */
.ref-phase-group {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  &:last-child { margin-bottom: 0; }
}

/* badge 和标题在同一行 */
.ref-phase-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.ref-phase-badge {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #2563eb;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 0;
}

/* 不同阶段用不同颜色（原型图统一蓝色） */
.ref-phase-group:nth-child(2) .ref-phase-badge { background: #2563eb; }
.ref-phase-group:nth-child(3) .ref-phase-badge { background: #2563eb; }
.ref-phase-group:nth-child(4) .ref-phase-badge { background: #2563eb; }

.ref-phase-name {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.ref-phase-time {
  font-size: 12px;
  color: #94a3b8;
  margin-left: auto;
}

/* 42 号方案 §9.3：跨阶段参考审核状态徽章 */
.ref-phase-audit-tag {
  font-size: 12px;
  line-height: 1;
  padding: 4px 8px;
  border-radius: 10px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.ref-phase-audit-tag.audit-tag-done    { background: #dcfce7; color: #15803d; }
.ref-phase-audit-tag.audit-tag-level   { background: #fef3c7; color: #b45309; }
.ref-phase-audit-tag.audit-tag-pending { background: #f1f5f9; color: #64748b; }
.ref-phase-audit-tag.audit-tag-rejected{ background: #fee2e2; color: #b91c1c; }

/* 未审核 / 已驳回 的参考阶段整体加浅色底，视觉上降权，提示审核员数据可信度较低 */
.ref-phase-group.ref-phase-untrusted {
  background: #fafaf9;
  border-color: #e5e7eb;
  position: relative;
}
.ref-phase-group.ref-phase-untrusted::before {
  content: '此阶段数据未经审核 / 已驳回，仅供参考';
  display: block;
  font-size: 12px;
  color: #92400e;
  background: #fef3c7;
  padding: 6px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
}

.ref-question-card {
  padding: 0;
  margin-bottom: 24px;
  &:last-child { margin-bottom: 0; }
}

.ref-question-title {
  font-size: 14px;
  font-weight: 500;
  color: #2563eb;
  margin-bottom: 8px;
  line-height: 1.5;
}

.ref-serial {
  color: #2563eb;
  font-weight: 600;
  margin-right: 2px;
}

.ref-answer-area {
  font-size: 14px;
  color: #303133;
}

.ref-option {
  padding: 8px 12px;
  margin: 4px 0;
  border-radius: 8px;
  font-size: 14px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 8px;
  
  .ref-option-circle {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 1px solid #cbd5e1;
    flex-shrink: 0;
  }
  
  .ref-check-icon {
    width: 16px;
    height: 16px;
    color: #2563eb;
    flex-shrink: 0;
  }
  
  &.selected {
    color: #2563eb;
    background: rgba(59,130,246,0.04);
    font-weight: 500;
    border: 1px solid rgba(59,130,246,0.15);
    border-radius: 8px;
    padding: 8px 12px;
  }
}

.ref-signature-container {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  width: 250px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.2s;
  
  &:hover {
    border-color: #cbd5e1;
  }
  
  img {
    max-width: 100%;
    max-height: 100px;
    object-fit: contain;
    opacity: 0.8;
  }
}

.ref-text-answer {
  display: block;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  word-break: break-all;
  line-height: 1.6;
}

.ref-signature-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  width: fit-content;
  max-width: 256px;
  img {
    max-height: 128px;
    object-fit: contain;
    cursor: pointer;
    opacity: 0.8;
    mix-blend-mode: multiply;
  }
}

.ref-no-answer {
  color: #94a3b8;
  font-size: 13px;
  font-style: italic;
}

.ref-file-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.ref-file-thumb {
  width: 80px;
  height: 80px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
  cursor: pointer;
  position: relative;
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.12); }
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.ref-thumb-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  i { color: #fff; font-size: 20px; }
  .ref-file-thumb:hover & { opacity: 1; }
}

.ref-video-thumb, .ref-audio-thumb {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: #f5f7fa;
  i { font-size: 24px; color: #409eff; }
  span { font-size: 10px; color: #909399; text-align: center; overflow: hidden; text-overflow: ellipsis; max-width: 70px; white-space: nowrap; }
  &:hover { background: #ecf5ff; }
}

.ref-file-link {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
  color: #409eff;
  text-decoration: none;
  &:hover { background: #ecf5ff; }
}

.cross-phase-loading {
  text-align: center;
  padding: 16px;
  color: #909399;
  font-size: 13px;
}

/* 当前审核标记分隔线 */
.current-audit-marker {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 16px 0;
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #409eff;
    opacity: 0.3;
  }
}

.audit-marker-badge {
  background: #409eff;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
  white-space: nowrap;
}

.audit-marker-text {
  font-size: 12px;
  color: #409eff;
  font-weight: 500;
  white-space: nowrap;
}

/* 廉政协议题 */
.agreement-answer-section {
  .answer-label {
    font-size: 13px;
    color: #909399;
    margin-bottom: 8px;
  }
  .agreement-pdf-pages {
    width: 100%;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    overflow: hidden;
    padding: 4px;
    background: #f5f5f5;
    margin-bottom: 10px;
    .pdf-loading, .pdf-error {
      padding: 24px; text-align: center; color: #909399; font-size: 14px;
      i { margin-right: 6px; }
    }
    .pdf-error { color: #e6a23c; }
  }
  .agreement-check-status {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding: 8px 0;
    white-space: nowrap;
    i { font-size: 16px; }
    span { font-size: 14px; color: #606266; }
  }
}

/* 明检分类题型 */
.mingjian-answer-section {
  margin-top: 8px;
}
.mingjian-photos {
  margin-bottom: 12px;
}
.mingjian-files {
  margin-bottom: 12px;
}
.mingjian-files .file-row {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; border: 1px solid #e4e7ed; border-radius: 8px; background: #fff;
}
.mingjian-files .file-row-icon { font-size: 22px; color: #409EFF; flex-shrink: 0; }
.mingjian-files .file-row-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.mingjian-files .file-row-name { font-size: 13px; color: #303133; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mingjian-files .file-row-meta { font-size: 12px; color: #909399; }
.mingjian-files .file-row-action { color: #606266; font-size: 16px; cursor: pointer; padding: 4px; text-decoration: none; }
.mingjian-files .file-row-action:hover { color: #409EFF; }
.mingjian-files-hint { color: #909399; font-size: 12px; font-weight: normal; margin-left: 6px; }
.mingjian-files-inline-empty {
  display: inline-flex; align-items: center;
  padding: 0 12px;
  color: #909399; font-size: 12px;
  align-self: center;
}

/* 证据网格 + 缩略图卡片 */
.evidence-grid {
  display: flex; flex-wrap: wrap; gap: 10px;
}
.evidence-tile {
  position: relative;
  width: 84px; height: 84px;
  border: 1px solid #e4e7ed; border-radius: 8px;
  background: #fff;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  cursor: pointer; user-select: none;
  transition: border-color .15s, box-shadow .15s;
  padding: 6px 4px 4px;
  box-sizing: border-box;
}
.evidence-tile:hover { border-color: #409EFF; box-shadow: 0 2px 6px rgba(64,158,255,.12); }
.evidence-tile.selected { border-color: #67c23a; background: #f0f9eb; }
.evidence-tile.appended { border-color: #e6a23c; background: #fdf6ec; }
.evidence-tile.appended:hover { border-color: #d48806; }
.evidence-badge {
  position: absolute; top: -1px; left: -1px;
  background: #e6a23c; color: #fff;
  font-size: 10px; line-height: 1; padding: 2px 5px;
  border-radius: 8px 0 8px 0;
  white-space: nowrap;
  max-width: calc(100% - 2px);
  overflow: hidden;
  text-overflow: ellipsis;
}
.evidence-tile.add-tile {
  border: 1px dashed #c0c4cc; background: #fafbfc; color: #909399;
}
.evidence-tile.add-tile i { font-size: 22px; margin-bottom: 4px; }
.evidence-tile.add-tile:hover { color: #409EFF; border-color: #409EFF; background: #f5faff; }

.evidence-thumb {
  width: 60px; height: 50px;
  object-fit: cover; border-radius: 4px;
}
/* 签名 / 协议等"细长或文档型"图片用 contain 避免关键内容被裁掉 */
.evidence-thumb.fit-contain {
  object-fit: contain;
  background: #f8fafc;
}
/* 用 <a> 实现的 tile（PDF 新 tab 打开）去掉默认下划线与颜色 */
a.evidence-tile-link {
  text-decoration: none;
  color: inherit;
}
.evidence-file-icon {
  font-size: 32px; color: #409EFF; margin: 4px 0;
}
.evidence-name {
  width: 100%;
  margin-top: 2px;
  font-size: 11px; color: #606266;
  text-align: center;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  padding: 0 2px;
}

.evidence-check {
  position: absolute; top: 2px; left: 2px;
  font-size: 16px; color: #c0c4cc; background: #fff; border-radius: 50%;
  padding: 1px;
}
.evidence-check.checked { color: #67c23a; }
.evidence-zoom {
  position: absolute; top: 2px; right: 2px;
  font-size: 14px; color: #606266; background: rgba(255,255,255,.85); border-radius: 50%;
  padding: 2px 3px; cursor: pointer;
  text-decoration: none;
}
.evidence-zoom:hover { color: #409EFF; }
.evidence-remove {
  position: absolute; bottom: 18px; right: 2px;
  font-size: 14px; color: #f56c6c; background: rgba(255,255,255,.85); border-radius: 50%;
  padding: 2px 3px; cursor: pointer;
}
.evidence-remove:hover { color: #c0392b; }
.mingjian-section-label {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  i { color: #409EFF; }
}
.mingjian-result {
  margin-top: 8px;
}
</style>
